/*
 * bc-segment-builder
 *
 * The Segment Builder configurator: one custom webbing segment — webbing
 * entry (color-flattened), length (fixed variant or custom), and sewing
 * items (End Loops at launch; the item model generalizes to future mid-line
 * types via location.mode) — priced as the ONE webbing cart line (sewing is
 * free by design; Phase B contract), with every sewing choice riding as a
 * line-item property.
 *
 * A build is up to THREE such segments, one per ROLE: the MAINLINE (always
 * present), an optional BACKUP rigged under it, and an optional EXTENDER
 * that makes up the backup's length (so it only exists alongside a
 * backup). Every role offers the same options, so state.lines[role] holds
 * one identically-shaped lineState each and every per-line accessor takes
 * its role explicitly — there is no implicit "current line". Only the
 * VIEW's own state (which end is zoomed, which panel is open) sits on the
 * state root, since exactly one editor is ever open. Part ids are
 * '<role>:<part>'; a BARE id means the mainline, which is what keeps
 * pre-role permalinks, the section's static data-bc-part="webbing" button
 * and the scene's 'end1'/'end2' view names working unchanged.
 *
 * The diagram (<bc-segment-scene> in assets/bc-segment-diagram.js) is the
 * primary navigation; editors split between the OVERLAY on the diagram and
 * ONE inline panel below it ([data-bc-picker] — deliberately not a modal,
 * so the diagram stays visible while editing):
 *   - LENGTH is TAP-TO-EDIT on the diagram. Each line's length dimension
 *     draws its VALUE as a chip; tapping it opens THE editor pill — one
 *     for the whole scene — floating at that dimension in the overlay
 *     layer ([data-bc-overlay], _renderOverlay): a select over the fixed
 *     lengths plus a custom input, a ft/m conversion readout in BOTH modes
 *     (FT_PER_M, the PDP rule), a Done button, and on the backup its
 *     recommendation / shortfall note under the controls. While it is open
 *     that row SHOWS no value — the pill REPLACES the number rather than
 *     restating it. Done, Escape, a tap outside, or tapping another line's
 *     value closes it; the build sheet's Length row is the keyboard/AT way
 *     in. The scene publishes a named anchor per row, ED_CLEAR off the
 *     line, and _positionOverlay re-pins the open pill off it after every
 *     draw (the span it is centred on moves as you type).
 *     OPENING AND CLOSING COST NO REDRAW (2026-08-02, Jerry on Staging:
 *     "the custom length loads very slowly with this new setup"). Hiding
 *     the row's value was a model change, and every model change rebuilt
 *     the whole svg; nothing the drawing computes depends on which pill is
 *     open, so it is a class toggle on the drawn scene now
 *     (_setSceneLengthOpen → the scene's setLengthOpen), and the length
 *     select commits down the same rAF-coalesced path a keystroke does.
 *
 *     FIVE ROUNDS to get here, because a box at a fixed CSS size on a
 *     canvas that scales is genuinely awkward:
 *       v1  the box sat IN a measured gap cut out of the dimension line,
 *           whose width was fed back from the box's own measurement. On a
 *           tight band the box was wider than its span and covered its
 *           own arrows.
 *       v2  a dimension row per band piece so two boxes could not collide
 *           — still ON the row. Jerry: "the length box covers the
 *           dimension arrow totally. Maybe its better to move the length
 *           inputs off the dimension bars so we can see both of them
 *           easily, then have the length inputs below?"
 *       v3  editors off the canvas entirely, into a strip below the sticky
 *           stage; rows display-only with the value as inline text.
 *       v4  Jerry, 2026-08-02: "I still want the length editors on the
 *           diagram, just off the dimensions the arrows" — so the boxes
 *           came back, one permanently visible per line, each on a rung
 *           the canvas had to reserve for it (+42% canvas height on a
 *           three-line build, and the drawing scaled down to pay for it).
 *       v5  (current) Jerry's pick from an interactive mockup of three
 *           options: TAP-TO-EDIT. Only one box can be open, so no canvas
 *           is reserved for any of them, the band's two pieces share one
 *           dimension row again, and the drawing is back to v3's compact
 *           drafting at v3's heights (222 / 300 / 300).
 *     The dimension row as a whole is still not clickable (Jerry,
 *     2026-08-01, on a frame that wrapped a control already inside it);
 *     the chip is a value-sized target that opens a control which is not
 *     on the page until it does. See the scene's _drawPieceHotspots.
 *   - ORIENTATION is NOT a line-level control (Jerry, 2026-07-31). A loop
 *     can be folded either way, so which face shows on the OUTSIDE is a
 *     per-SEWN-LOOP choice, made in the end editor; the LINE's drawn
 *     colors and each loop's fold direction are DERIVED from those
 *     choices (_lineFaces). Offered only for webbings whose two faces
 *     differ AND are named (bottomFace + faceChoice in the config; naming
 *     the faces on the webbing_pattern metaobject is the opt-in — two
 *     technically distinct faces aren't reason enough to ask).
 *   - ONE "Change <line>" button per present line sits beside the stage,
 *     each opening the webbing picker MODAL for its own role: tiles with
 *     the collection-card quick-spec line (entry.specsHtml,
 *     server-rendered by bc-card-specs so both always agree; the same line
 *     renders persistently under EACH line's Webbing summary row via
 *     _renderWebbingSpecs).
 *     Labels and visibility are synced in place by _syncStageActions — a
 *     solo build shows the single "Change webbing" it always did, and the
 *     "Mainline" wording only appears once there is a second line to tell
 *     it apart from.
 *   - clicking an end loop ZOOMS the scene into that end (state.endView)
 *     and swaps the panel to that END's editor — a top-to-bottom decision
 *     flow (see _endPanelHtml): the loop presets as one-tap starting
 *     points, the Loop style segmented control (Sleeved / Naked / No
 *     loop), the opening size, and the sleeve length when sleeved, in the
 *     order those choices depend on each other. The mm inputs carry an
 *     inches secondary readout (MM_PER_IN) + default/range hints. What
 *     merely DESCRIBES the loop sits behind the "Stitching details"
 *     disclosure at the foot: the pattern select where there's a choice,
 *     its needle/bobbin threads and stitch count, and the sewn loop
 *     strength (pattern mbsKn/meanKn from the per-product sewing_spec
 *     join — labelled as the pattern-on-this-webbing rating). The zoomed
 *     scene's dimensions are DRAGGABLE (Jerry, 2026-07-31, replacing the
 *     panel's rail sliders): the scene emits bc:segment:dim-drag with raw
 *     millimetres and this element clamps + writes state (_onDimDrag), so
 *     the number gets dialed in on the drawing while the panel keeps the
 *     exact-entry inputs and the only written statement of the bounds.
 *     "Full segment" or Escape zooms back out.
 *   - the build sheet's Backup / Extender section headers carry the
 *     Remove for those lines (a two-step inline confirm, never a browser
 *     dialog), and "+ Add backup line" appears both beside the stage and
 *     at the foot of the sheet. Either one adds the line AND opens its
 *     webbing picker in one gesture; cancelling the picker keeps the
 *     line (Remove is the only way back out).
 * The build sheet (left column) mirrors the hotspots: its rows re-open the
 * matching editor for keyboard/AT users. Its LENGTH row mirrors the value
 * chip on that line's dimension — same activation, same toggle — and is
 * the full-size target for an action whose on-diagram one is the size of a
 * number. The two End rows state the
 * PROTECTION only ("Sleeved" / "Naked" / the no-loop label) and keep their
 * dimensions behind a chevron disclosure beside the row (state.endDetails,
 * never the URL) — the sheet is a summary, not a spec dump.
 *
 * Structure mirrors assets/bc-primitive-kit-builder.js (config from a
 * <script type="application/json" data-bc-segment-config> child, central
 * state, URL permalink, batch add via window.bcCartAdd). The scene is
 * render()-driven with a plain model — this element never reaches into its
 * internals.
 *
 * Cart contract (ONE cart line per present role, all posted in a single
 * /cart/add.js — see _lines/_addToCart; qty is the SYSTEM count, so it
 * scales every line):
 *   custom mode : { id: entry.custom.id, quantity: ceil(length × qty) }
 *                 + "Piece Length"/"Qty" properties (same names + ceil rule
 *                 as assets/bc-custom-length.js — the cart-side editors in
 *                 snippets/cart-products.liquid depend on both)
 *   fixed mode  : { id: entry.variants[value].id, quantity: qty } and NO
 *                 Piece Length/Qty (the cart would misprice a per-piece
 *                 variant carrying that property)
 *   both        : "Sewn Loop - End 1/2" (the PROTECTION value) + per-end
 *                 Opening/Fold/Sleeve Length properties + a Build permalink.
 *                 Fold is DERIVED (opening + pattern length + 5,
 *                 _foldLiveMm) — stated because the shop measures the
 *                 fold; it never enters the permalink.
 *                 Deliberately NO pattern property — every segment uses
 *                 its webbing's default pattern, and the permalink still
 *                 carries the pattern token, so the line stays fully
 *                 recoverable. Webbings whose two faces are NAMED
 *                 (bottomFace + faceChoice in the config) also get
 *                 "End N Orientation: <face name>" per SEWN end — the
 *                 name of the face that loop folds outward ("Purple",
 *                 "Dots Up"), which is something the sewing shop can check
 *                 against the webbing in front of them. Written for every
 *                 flippable webbing whose loop folds the NON-default
 *                 (bottom) face out, never for single-face ones, for
 *                 two-faced webbings left unnamed, nor for a no-loop end.
 *                 So an ABSENT row means no face choice was offered or the
 *                 default face is out — not missing data; don't treat it
 *                 as an error downstream. Also in the permalink, as the
 *                 e-token's trailing .ft/.fb segment.
 *                 Every dimension row prints ONLY when it DEVIATES from
 *                 the BC default (Jerry, 2026-08-05): an absent row always
 *                 means the catalog value, so a fully default end posts
 *                 its "Sewn Loop - End N" treatment alone and a customized
 *                 end states exactly what was changed — the catalog isn't
 *                 a build spec, and printing it made every ordinary
 *                 segment read as custom. Rows are judged per FIELD and
 *                 ends INDEPENDENTLY; Fold rides Opening (pure
 *                 derivation). Read a treatment-only end as "BC default
 *                 <treatment>"; the Build permalink is UNCHANGED and still
 *                 carries full per-end state, so nothing about
 *                 recoverability moves. See _properties.
 *                 REPLACES the line-level "Top Face: Standard | Reversed"
 *                 (2026-07-31), which described a whole piece and stopped
 *                 being true once each end could fold its own way; legacy
 *                 cart lines carrying it still seed both ends on edit.
 *                 NOTE: "End N Orientation" is new vocabulary — mirror it
 *                 in bc-dashboard's BomModifier when wiring segment
 *                 orders.
 *   every line  : "Segment: <4-char code>" — the build's IDENTITY, shared
 *                 by all of its lines, VISIBLE on purpose (Jerry,
 *                 2026-08-01): with two custom segments in one cart the
 *                 role labels alone can't say which mainline goes with
 *                 which backup, and the same question is asked of the
 *                 staff "New order" email and of bc-dashboard's BOM —
 *                 neither of which can see a '_'-prefixed property. The
 *                 cart groups a run of lines sharing it under one header
 *                 (snippets/cart-products.liquid). Written on SOLO builds
 *                 too, so every segment in the cart is labelled the same
 *                 way; that ends the earlier "a solo mainline posts
 *                 byte-identically to what this page always posted"
 *                 property (deliberately — the label is worth more than
 *                 the invariant), so anything downstream that enumerates
 *                 properties sees one new row on every segment line.
 *                 Plus the hidden "_build_id" — the machine handle the
 *                 cart's Edit link and _editTargets address a build by.
 *                 The two are independent values (the code is for people,
 *                 the id for code) and both are minted per ADD, but only
 *                 the CODE is inherited by an edit's replacement lines: the
 *                 id is always fresh, so replacement lines can never come
 *                 out byte-identical to the lines they replace and merge
 *                 into a leftover (see _reuseIdentity).
 *   multi-line  : each line ALSO carries "Segment Piece: Mainline | Backup
 *                 | Extender" (which piece of the build it is), written
 *                 only when the build has more than one line — a solo
 *                 build has no piece to name.
 *                 The "Build" permalink is the SAME on every line (it
 *                 describes the whole build, not the piece), which is also
 *                 what lets any one of them reopen the full builder.
 *                 NOTE: "Segment" / "Segment Piece" / "_build_id" are new
 *                 vocabulary — mirror them in bc-dashboard's BomModifier too.
 *
 * Re-adding the SAME build folds into the existing cart line (_mergeAdd):
 * pieces bump, quantity recomputes, properties/Build rewrite — Shopify's
 * native identical-property merge would instead sum raw quantity (METERS
 * on a custom line) while the Qty property stayed stale. SOLO MAINLINE
 * ONLY: a multi-line build always adds fresh lines, because folding it
 * would have to match all three lines at once and then reconcile three
 * quantities against one system count — complexity for a case (re-adding
 * a whole identical three-piece build) that is worth a second set of
 * lines in the cart instead.
 *
 * STOCK is checked BEFORE any cart mutation (_stockError). On an
 * inventory-tracked, deny-overselling variant Shopify accepts an oversized
 * /cart/add and silently CLAMPS the line to what is on hand while the "Piece
 * Length" property still states the full request — cart quantity units on a
 * custom line are METERS, so 75 m ordered against 40 m in stock becomes a
 * 40 m line labelled "75 m". The build is refused WHOLE rather than shrunk
 * to fit. The ceilings ride the config (maxQty per variant, from
 * snippets/bc-kit-variant-json.liquid) and are therefore as old as the page,
 * so a mid-visit sell-out still falls through to Shopify's clamp.
 *
 * ADD FEEDBACK is Horizon's PDP choreography, not a local invention (Jerry,
 * 2026-08-02: "the add to cart doesn't do the same animation that product
 * pages do on the segment builder. Can we mimic that behavior?"): the button
 * carries upstream's own add-to-cart markup/classes, so flipping data-added
 * plays the same checkmark burst, and a <fly-to-cart> tile flies to the
 * header cart icon. Fired on SUCCESS rather than on click (upstream's
 * timing), because this page POSTs its own build and Shopify can reject it.
 * See _playAdded; the cart bubble and its announcement come free through
 * assets/bc-events-compat.js.
 *
 * Cart EDIT mode: the cart's per-line "Edit segment" link (bc-mod in
 * snippets/cart-products.liquid) opens the line's Build permalink plus
 * &edit=<buildId> — or &edit=<line key> for a line with no build id, which
 * since 2026-08-01 means only lines written before the id was put on solo
 * builds too (every fresh build carries one now, whatever its size). The
 * permalink restores the exact build, then _syncFromCartLine overrides
 * qty/length PER ROLE from the LIVE lines (fresh navigations only —
 * reload/back-forward keep the URL's in-progress values), so cart-side
 * edits always carry over. The edit key relabels Add to cart "Save
 * Segment" and switches _addToCart to _saveEdit(), which REPLACES every
 * line the key addresses: remove first (one batched /cart/update.js
 * zeroing each key, so a multi-line build can't be left half-removed),
 * then re-add — remove-first because an unchanged config re-added while
 * the old line exists would MERGE into it (same variant + properties) and
 * the removal would then wipe both. The old lines are captured up front
 * and restored if the re-add fails; if they're already gone (other tab,
 * stale back-button edit) the save demotes to a plain add, and if the
 * REMOVAL fails nothing is added at all (the cart stays as the customer
 * left it) and the error surfaces. A cart that cannot be READ is a third
 * case and aborts the save outright — an unreadable cart is not an empty
 * one, and demoting on it would duplicate the build (see _editTargets).
 * Success navigates back to the cart.
 * The edit key stays out of _buildParams so permalinks/Build properties
 * never carry it, but _writeUrl re-appends it so a mid-edit refresh keeps
 * edit mode.
 *
 * LOOP PRESETS: each end editor OPENS with the chip row — the CATALOG
 * DEFAULTS first, one per loop treatment the webbing offers ("Default
 * Sleeved" / "Default Naked": _defaultSewingItem's pattern, its default
 * opening and the type's default sleeve, in that treatment), then the
 * build's OWN other sewn ends ("Backup · End 2" — _buildCopyChips; with
 * up to six ends across three lines, copying a dialed-in loop onto the
 * next end is the commonest use of this row, and it needs no account and
 * no storage), then the customer's saved loop designs. The webbing's OWN
 * default treatment leads the row, and a default whose geometry can't
 * take that treatment (sleeved under the opening floor) renders DISABLED
 * rather than quietly applying the other one. The row carries ONE CHIP
 * PER UNIQUE DESIGN (Jerry, 2026-08-01) — the same loop reachable as a
 * catalog default, as an end already sitting on it and as a saved copy
 * is still one design, so the earliest source keeps the chip (defaults,
 * then the build's other ends, then saved) and later duplicates drop. A
 * DISABLED default claims no signature: it stands for the design it
 * CAN'T apply, not for the fallback its loop degraded to.
 * Opening the editor on a LOOP-LESS end applies that leading default on
 * the way in, so "add a sewn loop" lands in a populated editor instead
 * of an empty one (see _setEndView); the red "No loop" segment of the
 * Loop style control is still the way back to bare. Saving a design
 * (and managing the one the end matches) sits at the FOOT of the same
 * panel, opposite the chips that load one.
 * Saved loops have TWO stores behind one list (Jerry, 2026-07-31):
 * a logged-in customer's live on the PROFILE (metafield
 * custom.saved_loops, rendered into the config by bc-segment-config.liquid
 * and mutated through the balance-shipping-app app proxy —
 * config.settings.loopsUrl, default /apps/bc/loops); a guest's live in
 * localStorage under the identical record shape. SAVING NO LONGER
 * REQUIRES LOGIN — the login link is a hint about cross-device
 * persistence, not a wall, and a device with no storage at all (private
 * mode) is the one case that falls back to it. _loopsWrite is the seam:
 * logged in it is exactly the call this always made, so the account path
 * is unchanged. On the first load after a login, _mergeLocalLoops replays
 * each device loop through the proxy's own upsert-by-name and then clears
 * the device, silently and idempotently. Loops are end- and
 * webbing-agnostic: applying one uses the same offered-treatment /
 * offered-pattern / bounds policy as the e1/e2 URL tokens (pattern kept +
 * a note when the webbing doesn't offer it).
 * Sharing is a URL, not a server object: ?loop=<treatment>.<pattern>.
 * <opening>[.<sleeve>]&loopname=<name> applies the design to BOTH ends
 * on load and shows a banner offering "Save to my loops" (device or
 * profile, following the same rule).
 * Like the edit key, loop/loopname stay out of _buildParams (permalinks
 * and cart Build properties never carry them) but _writeUrl re-appends
 * them until the design is saved or dismissed, so a mid-visit refresh
 * keeps the banner.
 */
(() => {
  if (customElements.get('bc-segment-builder')) return;

  // Matches bc-primitive-kit-builder: a touch above 3.28084 so ft rounds up.
  const FT_PER_M = 3.3;
  const MM_PER_IN = 25.4;

  // Sleeve gating (Jerry, 2026-07-30): an opening under 50 mm cannot take a
  // sleeve at all; from 50 mm up the sleeve caps at (opening − 10) × 2 —
  // e.g. a 60 mm opening allows at most a 100 mm sleeve. Applied everywhere
  // sleeve state enters: the editor pills/inputs, share-URL parsing, and
  // the webbing-switch carry-over (see _enforceSleeveRule).
  const SLEEVE_MIN_OPENING_MM = 50;
  const sleeveCapForOpening = (openingMm) => (Number(openingMm) - 10) * 2;

  const END_SLOTS = [1, 2];
  // 'face' is NOT here any more — orientation moved onto the sewn loops
  // (see _lineFaces), so there is no whole-line face to activate. Inbound
  // 'face' part ids are still PARSED and redirected to the webbing picker
  // (_activatePart) so a stale id can't become a dead control: nothing
  // MINTS one today (the scene stopped drawing mid-line hotspots
  // altogether on 2026-08-01), but cached markup and old permalinks can
  // still carry one.
  const PARTS = ['webbing', 'length', 'end1', 'end2'];

  // The build's lines, in draw / permalink / build-sheet order. 'main' is
  // always present; 'extender' is only ever set alongside 'backup'.
  const ROLES = ['main', 'backup', 'extender'];
  // (OVERLAY_GAP_PX went with _layOutOverlay in round 5: it was the
  // breathing room between two overlay editors that would otherwise touch,
  // and there is only ever ONE open now — nothing to de-collide against.)
  // URL param prefix per role. The mainline's params stay UNPREFIXED, so
  // every permalink written before the backup existed (and every Build
  // property already sitting in a cart or an order) parses unchanged.
  const ROLE_PREFIX = { main: '', backup: 'b', extender: 'x' };
  // What each line is CALLED on screen — build-sheet section headers, panel
  // titles, add/remove affordances. The cart's own role property is the
  // same vocabulary (bc-dashboard BOM), so the two can't drift apart.
  const ROLE_LABELS = { main: 'Mainline', backup: 'Backup', extender: 'Extender' };

  // Alphabet for the customer-facing segment code (the visible "Segment"
  // property): base 32 minus the characters that get misread off a screen
  // or a printed build sheet — 0/O and 1/I/L. Four characters is ~920k
  // codes, which is not uniqueness in any global sense but is far more
  // than one cart or one order ever holds, and it stays short enough to
  // read back over the phone. Only ever compared for EQUALITY, so a
  // collision between two carts costs nothing; a collision WITHIN one cart
  // would merge two segments under one header (display only — the edit
  // flow addresses builds by "_build_id", which carries a timestamp).
  const SEGMENT_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

  // bc-segment-builder.css's phone breakpoint, mirrored here because a media query can't
  // be read back out of the stylesheet. It gates behaviour that only makes
  // sense where the end panel is STACKED and taller than the viewport (see
  // _setEndView / _scrollPanelIntoView), so the two must agree — if the CSS
  // breakpoint ever moves, move this with it. The gap is the breathing room
  // left between the pinned diagram and the field we scroll to.
  const NARROW_MAX_PX = 749;
  const PANEL_SCROLL_GAP = 12;

  // Chip value prefix for the catalog-default presets ("__default:sleeved").
  // Namespaced away from saved-loop ids, which are server-generated.
  const DEFAULT_PRESET = '__default:';
  // …and for the copy-from-another-end chips ("__copy:backup:1"). A build
  // now has up to SIX sewn ends, so copying one end's design onto another
  // is the commonest reason to want a preset at all (Jerry, 2026-07-31).
  const COPY_PRESET = '__copy:';

  // Guest saved loops (localStorage). Same records the customer metafield
  // holds, so a login can push them straight through the app proxy — see
  // _mergeLocalLoops. Bumping this key orphans every guest's designs.
  const LOCAL_LOOPS_KEY = 'bc-saved-loops';
  // Matches the proxy's own cap (app/routes/proxy.loops.tsx), so a device
  // list can never be one the server would refuse.
  const MAX_SAVED_LOOPS = 50;

  // How long the drawing waits after the last keystroke before it re-draws
  // (Jerry, 2026-08-02: "it still feels slow on the custom length").
  //
  // A length keystroke changes GEOMETRY — the band's span, the backup
  // split, the shortfall frame — and geometry is the expensive half of a
  // frame here: svg.replaceChildren() plus every piece's paths, gradients,
  // loops and hotspots, and then the settle handler's _positionOverlay,
  // whose getBoundingClientRect forces a synchronous layout of this whole
  // page (overflow:hidden html/body, a 100dvh .page-wrapper, two sticky
  // columns — the most expensive single operation on this page, and
  // Firefox's cost for it is several times Chrome's). Per FRAME, which is
  // what a rAF-coalesced redraw means while someone types, that stacks.
  //
  // What does NOT wait is the FEEDBACK: the pill's own ft/m readout and
  // the build sheet's Length row are plain text writes in recompute(), so
  // they still move character by character. The DRAWING waits for the
  // interaction to END — no scene draw fires while the customer is typing
  // at all. This was a 150ms trailing settle first, and that was still too
  // slow (Jerry, 2026-08-02, Mac Firefox: "its still slow, now while
  // inside the editor" — every typing pause bought a full svg rebuild plus
  // the settle handler's forced layout; then, his call: "maybe the
  // redrawing of the diagram should only happen when done entering the
  // length"). So a deferred edit just marks the scene DIRTY, and the draw
  // happens exactly once, at whichever interaction end arrives first: the
  // field committing (blur/Enter/change → a next-frame draw), the pill
  // closing, add-to-cart, or any full render (see _flushScene and
  // _renderScene's cancel-at-head). Nothing can leave a drawing that
  // disagrees with the build — it can only lag while the caret is still in
  // the field that is changing it.

  // Added-to-cart feedback timings, all lifted from the PDP so the two
  // buttons behave identically (see _playAdded):
  //   HOLD    — AddToCartComponent.animateAddToCart's 800ms before it
  //             drops data-added and the label fades back in;
  //   ANNOUNCE— product-form.js's SUCCESS_ / ERROR_MESSAGE_DISPLAY_DURATION,
  //             how long each message sits in the live region before it is
  //             cleared (clearing is what lets an identical later message
  //             be announced again).
  const ADDED_HOLD_MS = 800;
  const ADDED_ANNOUNCE_MS = 5000;
  const ERROR_ANNOUNCE_MS = 10000;

  // Per-instance counter, so the build sheet's aria-controls ids stay
  // unique if the section is ever placed twice on one page.
  let instances = 0;

  class BcSegmentBuilder extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._uid = ++instances;

      this.currency = this.dataset.currency || 'USD';
      this.locale = this.dataset.locale || 'en';
      this.lengthProperty = this.dataset.lengthProperty || 'Piece Length';
      this.piecesProperty = this.dataset.piecesProperty || 'Qty';
      this.cartUrl = this.dataset.cartUrl || '/cart';

      this.config = this._readConfig();
      if (!this.config) return;
      this.settings = this.config.settings || {};
      this.webbings = this.config.webbings || {};
      this.webbingKeys = Object.keys(this.webbings);
      if (!this.webbingKeys.length) return;
      this.customer = this._readCustomer();
      // Where the saved designs come from: the profile when there is one,
      // the device otherwise. One list either way, so every chip / manage /
      // share path below is unaware of which store it is looking at.
      this.savedLoops = this.customer.loggedIn ? this.customer.savedLoops : this._readLocalLoops();
      this.loopsUrl = this.settings.loopsUrl || '/apps/bc/loops';

      this.money = window.bcMoney.formatter(this.locale, this.currency);
      this.scene = this.querySelector('bc-segment-scene');
      this.stage = this.querySelector('.bc-segment__stage');
      this.overlayHost = this.querySelector('[data-bc-overlay]');
      this.pickerHost = this.querySelector('[data-bc-picker]');
      this.webbingModal = this.querySelector('[data-bc-webbing-modal]');
      // The dialog can close itself (Escape, the backdrop, form method
      // dialog), so its own close event is what keeps state.panel true —
      // never the other way around. A close fired while state.panel is
      // already null came FROM _renderWebbingModal and is ignored, which
      // is what stops the two from chasing each other.
      if (this.webbingModal) {
        this.webbingModal.addEventListener('close', () => {
          if (this.state && this.state.panel) this._closeWebbingPanel();
        });
      }

      // Per-role product handle last written into that line's build-sheet
      // quick-spec line, so recompute()'s per-keystroke pass rewrites only
      // the line whose webbing actually changed (see _renderWebbingSpecs).
      this._specsHandles = {};
      // Which line's length pill is open, or null. TRANSIENT — not in
      // `state`, never in the URL, never in the cart (see the open/close
      // block). A page always loads with it closed: a permalink is a build,
      // not a cursor position.
      this._lengthOpen = null;

      this.state = this._defaultState(this.webbingKeys[0]);
      this._applyUrl();
      this._syncQtyInput();
      this._applyEditMode();
      this._syncFromCartLine();
      this._wireEvents();
      this._renderOverlay();
      this._renderPicker();
      this._renderLoopBanner();
      this._afterChange({ writeUrl: false });
      this._observeStage();
      this._observeScroll();
      // Loops saved before this visit's login belong on the profile now.
      // Deliberately last and un-awaited: it is a background migration
      // that nobody is waiting on, and it fails silently (see the method).
      this._mergeLocalLoops();
    }

    disconnectedCallback() {
      if (this._sceneRaf) { cancelAnimationFrame(this._sceneRaf); this._sceneRaf = null; }
      // A dirty scene on an element leaving the document is simply dropped
      // — there is nothing left to draw it onto.
      this._sceneDirty = false;
      if (this._posRaf) { cancelAnimationFrame(this._posRaf); this._posRaf = null; }
      if (this._switchRaf) { cancelAnimationFrame(this._switchRaf); this._switchRaf = null; }
      if (this._urlTimer) { clearTimeout(this._urlTimer); this._urlTimer = null; }
      if (this._stageRO) { this._stageRO.disconnect(); this._stageRO = null; }
      if (this._stageResize) { window.removeEventListener('resize', this._stageResize); this._stageResize = null; }
      if (this._scrollFn) {
        window.removeEventListener('scroll', this._scrollFn);
        window.removeEventListener('resize', this._scrollFn);
        this._scrollFn = null;
      }
      if (this._docKeydown) {
        document.removeEventListener('keydown', this._docKeydown);
        this._docKeydown = null;
      }
      if (this._docPointerDown) {
        // Capture phase, so it must be removed with the same flag.
        document.removeEventListener('pointerdown', this._docPointerDown, true);
        this._docPointerDown = null;
      }
      if (this._docPointerUp) {
        document.removeEventListener('pointerup', this._docPointerUp, true);
        document.removeEventListener('pointercancel', this._docPointerUp, true);
        this._docPointerUp = null;
      }
      // The blur close's next-tick check. Its own guards would make it a
      // no-op on a detached element, but a timer outliving the component is
      // a leak whatever it does.
      if (this._ovBlurTimer) { clearTimeout(this._ovBlurTimer); this._ovBlurTimer = null; }
      if (this._addedTimer) { clearTimeout(this._addedTimer); this._addedTimer = null; }
      if (this._announceTimer) { clearTimeout(this._announceTimer); this._announceTimer = null; }
    }

    // ---- config ----------------------------------------------------------

    _readConfig() {
      try {
        const el = this.querySelector('script[data-bc-segment-config]');
        return JSON.parse(el?.textContent || 'null');
      } catch (e) {
        console.error('[bc-segment] bad config JSON', e);
        return null;
      }
    }

    // ---- per-line accessors ----------------------------------------------
    //
    // Every one of these takes the ROLE whose line it reads, plus an
    // optional webbing `key` override for the paths that build a line
    // BEFORE it is installed on the state (_defaultLine, _changeWebbing's
    // carry-over). With a key given the role only names the line the answer
    // is for; without one it resolves the line's current webbing.

    _lineState(role) {
      return (this.state && this.state.lines && this.state.lines[role]) || null;
    }

    _lineWebbing(role) {
      const line = this._lineState(role);
      return line ? line.webbing : '';
    }

    _entry(role, key) {
      return this.webbings[key || this._lineWebbing(role)] || null;
    }

    // Which way round the LINE is drawn, and which way each loop folds —
    // both DERIVED from the per-loop orientation choices (Jerry,
    // 2026-07-31). Orientation moved onto the sewn loops because that is
    // where the decision physically lives: a loop can be folded either
    // way, so each end independently picks which face ends up on the
    // OUTSIDE of its eye. The line's drawn colors are then a consequence,
    // not a separate control.
    //
    // The physical rule: a loop whose outside face matches the line's
    // upper face has its tail on the BOTTOM (the fold goes down), and vice
    // versa. So —
    //   MAINLINE      both ends agree → that face is the line's top and
    //                 both tails hang down. They disagree → End 1 wins the
    //                 line (its tail is always down on a mainline) and End
    //                 2 folds the other way, tail up.
    //   BACKUP/EXT.   rigged upside down, so the mirror image: the agreed
    //                 outside face is the line's BOTTOM and tails ride on
    //                 top; on disagreement End 1 still wins, tail up.
    // Which is one formula once `lineTop` absorbs the mirroring —
    // tailUp = (this end's outside face) !== (the line's top face).
    //
    // Ends with no loop have no say (an unsewn end can't state a face) and
    // no tail; they take the role's natural default.
    //
    // Two DIFFERENT questions about a webbing's second face, and they must
    // not be conflated (they were, briefly, and it cost the jelly-pro
    // class its backup underside):
    //   _flippable   is there a CHOICE to offer? Needs NAMED faces, since
    //                an unnamed pair can't be put in front of a customer.
    //   _mirrorable  is there another face to DRAW? Needs only that one
    //                exists. A two-faced webbing whose faces nobody named
    //                still shows its underside when it hangs upside down —
    //                that is physics, not a preference.
    // So a mirrored line (backup / extender) with no choice on offer is
    // still drawn from below when it HAS an underside; only a genuinely
    // single-faced webbing stays as-is.
    _lineFaces(role) {
      const entry = this._entry(role);
      const flippable = this._flippable(entry);
      const mirrorable = this._mirrorable(entry);
      const mirrored = role !== 'main';
      const outOf = (end) => {
        const item = this._endItem(role, end);
        if (!item || item.treatment === 'none') return null;
        return item.faceOut === 'bottom' ? 'bottom' : 'top';
      };
      const o1 = outOf(1);
      const o2 = outOf(2);
      // End 1 wins the line; a loop-less End 1 hands the say to End 2.
      const winner = o1 || o2 || 'top';
      const flipped = winner === 'top' ? 'bottom' : 'top';
      const lineTop = flippable
        ? (mirrored ? flipped : winner)
        : ((mirrored && mirrorable) ? 'bottom' : 'top');
      const tailUp = (o) => ((o == null || !flippable) ? mirrored : o !== lineTop);
      // Whether a ZOOM into this end must draw the OTHER face than the
      // line does. The full view shows the line as rigged; a zoom is a
      // detail of one loop, and what that loop presents is its chosen
      // OUTSIDE face — which is the opposite of the line's top whenever
      // the two disagree. Notably true for a flippable BACKUP at its
      // defaults: the line hangs upside down, so its top is the face the
      // loop is NOT showing. Only ever true where a choice exists —
      // without one there is nothing for the zoom to contradict.
      const zoomFlipped = (o) => !!(flippable && o != null && o !== lineTop);
      return {
        lineTop,
        mirrorable,
        tailUp: { 1: tailUp(o1), 2: tailUp(o2) },
        zoomFlipped: { 1: zoomFlipped(o1), 2: zoomFlipped(o2) },
      };
    }

    // Does this entry have a second face to draw at all? (See _lineFaces
    // for why this is not _flippable.) The face _flipEntry consumes falls
    // back to the top one for a two-TONE entry that describes no second
    // face — the same fallback the old bottom-view strip used, and what
    // keeps _flipEntry from ever building a face out of undefined.
    _mirrorable(entry) {
      return !!(entry && (entry.bottomFace || (entry.scene && entry.scene.twoTone)));
    }

    _mirrorFace(entry) {
      return entry ? (entry.bottomFace || entry.topFace) : null;
    }

    // Entry as the scene should see it: with the line's derived top face
    // brought to the top, which swaps the side view's two-tone halves and
    // mirrors the face across the flat width (physically flipping the
    // webbing swaps left and right).
    // (No `key` override, unlike the other per-line accessors: the flip is
    // derived from the line's OWN loops, so asking for it against some
    // other webbing has no meaning.)
    _effectiveEntry(role) {
      const entry = this._entry(role);
      if (!entry) return null;
      return this._lineFaces(role).lineTop === 'bottom'
        ? this._flipEntry(entry, this._mirrorFace(entry))
        : entry;
    }

    // One face brought to the top: edges reversed and marks MIRRORED
    // across the flat width (physically flipping the webbing swaps left
    // and right), with the side view's two-tone halves swapped to match.
    _flipEntry(entry, face) {
      const scene = entry.scene || {};
      const flatW = (Number(entry.widthMm) || 0) - (Number(entry.thicknessMm) || 0);
      const f = face || {};
      return {
        ...entry,
        topFace: {
          base: f.base,
          edges: [(f.edges || [])[1], (f.edges || [])[0]],
          marks: (f.marks || []).map((m) => (flatW > 0
            ? { ...m, x: flatW - (Number(m.x) || 0) - (Number(m.w) || 0.5) }
            : m)),
        },
        scene: scene.twoTone
          ? { ...scene, color: scene.colorLower, colorLower: scene.color }
          : scene,
      };
    }

    // A webbing offers the face choice only when its cross-section has
    // NAMED faces (config `faceChoice`, see bc-segment-webbing-json) — two
    // technically distinct faces aren't reason enough to ask the customer
    // to pick. Gates the end editor's Orientation step, the derived line
    // flip, the End N detail row, the e-token's face suffix and the
    // "End N Orientation" cart property, together.
    _flippable(entry) {
      return !!(entry && entry.bottomFace && entry.faceChoice);
    }

    // Distinct names for the two faces, or null — identical names would
    // label both flip pills the same, worse than Standard/Reversed. Named
    // per face on the webbing_pattern metaobject (top_face_name /
    // bottom_face_name), else derived from the palette; see
    // bc-segment-webbing-json.
    // The same rule for a LOOP's choice, which is about which face ends up
    // on the OUTSIDE of the eye rather than which way the line lies:
    // "Purple out" / "Green out", and a name that already states its own
    // orientation ("Dots Up") stands verbatim — appending "out" to it
    // would say two things at once, and its pair is already distinct.
    _faceOutLabel(name) {
      const n = String(name == null ? '' : name).trim();
      return /\s(up|down)$/i.test(n) ? n : `${n} out`;
    }

    _faceNames(entry) {
      const top = entry && entry.topFace && entry.topFace.name;
      const bot = entry && entry.bottomFace && entry.bottomFace.name;
      return top && bot && top !== bot ? { top, bot } : null;
    }

    // The NAME of the face a loop folds outward — what the cart property
    // carries (Jerry, 2026-07-31: the order should say "Purple", not
    // "Reversed", because a name is something the sewing shop can check
    // against the webbing in front of them). Same resolution the editor
    // labels use, minus the "out"/"Up" suffixing: authored face names
    // where they exist, the palette-derived ones otherwise
    // (bc-segment-webbing-json resolves both into topFace/bottomFace.name).
    // Standard/Reversed survives only as the guard for a malformed config
    // that claims a face choice without two distinct names.
    _faceOutName(entry, faceOut) {
      const bottom = faceOut === 'bottom';
      const names = this._faceNames(entry);
      if (!names) return bottom ? 'Reversed' : 'Standard';
      return bottom ? names.bot : names.top;
    }

    // The inverse, for reading a cart line back (see _syncFromCartLine):
    // match a stored orientation value against THIS webbing's two faces,
    // case-insensitively. Null when it matches neither — the webbing may
    // have been changed, or the metaobject's face renamed, since the line
    // was written, and inventing a choice from an unrecognised string
    // would be worse than leaving the default alone.
    _faceOutFromName(entry, value) {
      const v = String(value == null ? '' : value).trim().toLowerCase();
      if (!v) return null;
      // Legacy lines carry the line-level Standard/Reversed vocabulary.
      if (v === 'standard') return 'top';
      if (v === 'reversed') return 'bottom';
      const names = this._faceNames(entry);
      if (!names) return null;
      if (String(names.top).trim().toLowerCase() === v) return 'top';
      if (String(names.bot).trim().toLowerCase() === v) return 'bottom';
      return null;
    }

    _sewingData(role, key) {
      const entry = this._entry(role, key);
      const byHandle = this.config.sewing || {};
      return (entry && byHandle[entry.productHandle]) || { endTreatments: [], patterns: [] };
    }

    // The launch sewing type. locationMode 'end' items are the only ones the
    // UI renders today; future mid-line types (locationMode 'midline') will
    // add their own editor + location control on this same item model.
    _endLoopType() {
      const types = this.config.sewingTypes || [];
      return types.find((t) => t.key === 'end_loop')
        || types.find((t) => t.locationMode === 'end')
        || { key: 'end_loop', label: 'End Loop', patternKind: 'loop', locationMode: 'end', supportsSleeve: true, sleeveDefaultMm: 200, sleeveMinMm: null, sleeveMaxMm: null };
    }

    _patternsFor(role, typeKey, key) {
      const type = typeKey === 'end_loop' ? this._endLoopType() : null;
      const kind = (type && type.patternKind) || 'loop';
      return (this._sewingData(role, key).patterns || []).filter((p) => p.kind === kind);
    }

    _fixedValues(role, key) {
      const entry = this._entry(role, key);
      return entry ? Object.keys(entry.variants || {}).filter((k) => k !== '') : [];
    }

    // Default treatment kind: the configured default label when the webbing
    // offers it, else 'sleeved', else the first offered kind.
    _defaultTreatment(role, key) {
      const ends = this._sewingData(role, key).endTreatments || [];
      const wanted = String(this.settings.endDefault || 'Sleeved').toLowerCase();
      const byLabel = ends.find((t) => String(t.label).toLowerCase() === wanted);
      if (byLabel) return byLabel.kind;
      const sleeved = ends.find((t) => t.kind === 'sleeved');
      if (sleeved) return 'sleeved';
      return ends.length ? ends[0].kind : 'sleeved';
    }

    // ---- state -----------------------------------------------------------

    // A fresh build: the mainline alone. QTY and the VIEW's state live on
    // the root — qty is how many of the WHOLE system are being ordered
    // (Jerry, 2026-07-31), so it scales every line rather than belonging to
    // one, and one editor is open at a time, so which end is zoomed and
    // which panel is showing are properties of the page, not of a line.
    _defaultState(key) {
      return {
        lines: {
          main: this._defaultLine('main', key),
          backup: null,
          extender: null,   // never non-null without a backup (see _addExtender)
        },
        qty: 1,             // systems, not pieces of any one line
        endView: null,      // {role, end} while zoomed into an end, else null
        panel: null,        // inline panel below the diagram: {role, part} | null
      };
    }

    // One line's own build spec — the whole of what a role contributes to
    // the cart, and the unit _buildParams writes one prefixed param set for.
    // No qty: that's the system's (see _defaultState).
    _defaultLine(role, key) {
      const fixed = this._fixedValues(role, key);
      const entry = this._entry(role, key);
      const hasCustom = !!(entry && entry.custom);
      return {
        webbing: key,
        lengthMode: fixed.length ? 'fixed' : 'custom',
        fixedValue: fixed.length ? fixed[0] : '',
        length: hasCustom ? 1 : 0,
        sewing: END_SLOTS.map((end) => this._defaultSewingItem(role, key, end)),
        // Which build-sheet End rows have their dimensions disclosed.
        // Deliberately state, NOT a URL param: the sheet re-renders on
        // every change (so the DOM can't hold it) but it's a reading
        // preference, not part of the build — _buildParams must stay the
        // spec of the segment itself.
        endDetails: { end1: false, end2: false },
      };
    }

    _defaultSewingItem(role, key, end) {
      const type = this._endLoopType();
      const patterns = this._patternsFor(role, 'end_loop', key);
      const pattern = patterns.length ? patterns[0] : null;
      const item = {
        id: `end${end}`,
        typeKey: 'end_loop',
        location: { mode: 'end', end },
        treatment: this._defaultTreatment(role, key),
        pattern: pattern ? pattern.handle : null,
        openingMm: pattern && pattern.openingMm != null ? pattern.openingMm : null,
        sleeveLengthMm: Number(type.sleeveDefaultMm) || 200,
        // Which face ends up on the OUTSIDE of this loop's eye. Only
        // meaningful on a webbing whose faces are named (_flippable); the
        // line's drawn orientation derives from these (see _lineFaces).
        faceOut: 'top',
      };
      // Defaults obey the sleeve rule too (a pattern whose default opening
      // sits under the floor must not start sleeved).
      this._enforceSleeveRule(item, role, key);
      return item;
    }

    _endItem(role, end) {
      const line = this._lineState(role);
      return ((line && line.sewing) || []).find(
        (it) => it.location && it.location.mode === 'end' && it.location.end === end
      ) || null;
    }

    _pattern(item, role, key) {
      if (!item || !item.pattern) return null;
      return (this._sewingData(role, key).patterns || []).find((p) => p.handle === item.pattern) || null;
    }

    // Effective opening for the sleeve rule: the edited value, else the
    // selected pattern's default. Null when the pattern has no numeric
    // opening (rule can't gate on unknown geometry — sleeve stays allowed
    // under the static type bounds).
    _effectiveOpeningMm(item, role, key) {
      if (item && item.openingMm != null) return item.openingMm;
      const p = this._pattern(item, role, key);
      return p && p.openingMm != null ? p.openingMm : null;
    }

    _sleeveAllowed(item, role, key) {
      const o = this._effectiveOpeningMm(item, role, key);
      return o == null || o >= SLEEVE_MIN_OPENING_MM;
    }

    // Sleeve bounds: the sewing type's static bounds intersected with the
    // opening-driven cap.
    _sleeveBounds(item, role, key) {
      const type = this._endLoopType();
      const min = type.sleeveMinMm != null ? type.sleeveMinMm : 50;
      let max = type.sleeveMaxMm != null ? type.sleeveMaxMm : 2000;
      const o = this._effectiveOpeningMm(item, role, key);
      if (o != null) max = Math.min(max, sleeveCapForOpening(o));
      return { min, max: Math.max(min, max) };
    }

    // Sleeve MEMORY (Jerry, 2026-07-31): when the RULE strips a sleeve the
    // end remembers the length it had, so winding the opening back over the
    // floor puts the sleeve back rather than making the customer re-pick it
    // (see _applyOpeningChange). Runtime only — deliberately NOT in the
    // e-tokens, the Build permalink or the cart properties: it is a
    // gesture-level nicety describing a moment, not part of the segment's
    // spec, and a permalink that silently re-sleeved on load would be a
    // different build from the one that was shared. The '_' prefix says so.
    //
    // Cleared by ANY explicit treatment choice — the segmented control, No
    // loop, a preset/chip, the diagram's Add sleeve — because a deliberately
    // naked end must never re-sleeve itself behind the customer's back.
    _clearSleeveMemory(item) {
      if (item) item._sleeveMemoryMm = null;
    }

    // Normalize an item into the sleeve rule: a sleeved treatment below the
    // opening floor falls back to the webbing's non-sleeved loop treatment;
    // a kept sleeve clamps into bounds. Returns true when anything changed
    // (callers re-render on structural change).
    _enforceSleeveRule(item, role, key) {
      if (!item || item.treatment !== 'sleeved') return false;
      if (!this._sleeveAllowed(item, role, key)) {
        const ends = this._sewingData(role, key).endTreatments || [];
        const fallback = ends.find((t) => t.kind === 'naked')
          || ends.find((t) => t.kind !== 'sleeved' && t.kind !== 'none');
        // Written HERE rather than in the callers: this is the one place
        // the rule (as opposed to a person) takes a sleeve away, and every
        // path that can shrink an opening runs through it.
        const had = Number(item.sleeveLengthMm);
        item._sleeveMemoryMm = Number.isFinite(had) && had > 0 ? had : null;
        item.treatment = fallback ? fallback.kind : 'naked';
        return true;
      }
      const { min, max } = this._sleeveBounds(item, role, key);
      const v = Number(item.sleeveLengthMm);
      const base = Number.isFinite(v) && v > 0 ? v : (Number(this._endLoopType().sleeveDefaultMm) || 200);
      const clamped = Math.min(max, Math.max(min, base));
      if (clamped !== item.sleeveLengthMm) {
        item.sleeveLengthMm = clamped;
        return true;
      }
      return false;
    }

    // Live fold-to-end length (Jerry's model, 2026-07-30): the eye's flat
    // length is the OPENING SIZE (the builder's changeable entry), the
    // two-layer sewn zone is a 5 mm throat inset + the pattern length,
    // and the fold is their sum:
    //   fold = opening + pattern_length + 5
    // (The catalog defaults run this the other way — the pattern's
    // authoritative fold pre-derives the stored opening_size.)
    // Fallback without a pattern length: the authored webbing_fold_mm,
    // tracking the full opening delta (fold = opening + area, so a 10 mm
    // bigger opening folds 10 mm more webbing).
    _foldLiveMm(item, role, key) {
      const p = this._pattern(item, role, key);
      if (!p) return null;
      const opening = item.openingMm != null ? item.openingMm : p.openingMm;
      if (p.patternLengthMm != null && opening != null) {
        return Math.round(opening + p.patternLengthMm + 5);
      }
      if (p.foldMm == null) return null;
      if (opening != null && p.openingMm != null) {
        return Math.round(p.foldMm + (opening - p.openingMm));
      }
      return p.foldMm;
    }

    // ---- recommended lengths ----------------------------------------------

    // A line's length in METRES, however it was chosen: the custom input
    // (in its entry's own unit) or the fixed variant's option value ("2 m",
    // "50 ft", "100 cm" — the same parse _lengthConvText makes, kept here
    // because the recommendation must work in one unit). Null when there is
    // no orderable length to measure yet.
    _lineLengthM(role) {
      const line = this._lineState(role);
      if (!line) return null;
      if (line.lengthMode === 'custom') {
        const entry = this._entry(role);
        const unit = (entry && entry.custom && entry.custom.unit) || 'm';
        const v = Number(line.length) || 0;
        if (!(v > 0)) return null;
        return String(unit).toLowerCase() === 'ft' ? v / FT_PER_M : v;
      }
      const s = String(line.fixedValue || '');
      const num = parseFloat(s.replace(',', '.'));
      if (!(num > 0)) return null;
      if (/ft|feet|foot|′|'/i.test(s)) return num / FT_PER_M;
      if (/cm/i.test(s)) return num / 100;
      return num;
    }

    _mainLengthM() {
      return this._lineLengthM('main');
    }

    // "The backup must be N% longer than the mainline" — the MAINLINE
    // webbing's webbing.highline_backup_length_m, emitted per entry by
    // bc-segment-webbing-json only when it parses as a positive number.
    // The backup's own webbing says nothing about this: the requirement is
    // a property of what's being backed up.
    _backupPct() {
      const entry = this._entry('main');
      const pct = entry ? Number(entry.backupPct) : NaN;
      return Number.isFinite(pct) && pct > 0 ? pct : null;
    }

    // Rounded UP to the next 0.5 m — a recommendation that rounds down is a
    // backup that's too short. toFixed(6) first because 10 × 1.1 is
    // 11.000000000000002 in binary floating point, which a bare ceil would
    // push a whole half-metre.
    _ceilHalfM(m) {
      return Math.ceil(Number((Number(m) * 2).toFixed(6))) / 2;
    }

    _recommendedBackupM() {
      const pct = this._backupPct();
      const main = this._mainLengthM();
      if (pct == null || !(main > 0)) return null;
      return this._ceilHalfM(main * (1 + pct / 100));
    }

    // What an extender still has to make up: the recommendation minus the
    // backup PIECE's own length. Null while either is unknown; never
    // negative (a backup already at or past the recommendation needs no
    // extender, it doesn't need a negative one).
    _recommendedExtenderM() {
      const rec = this._recommendedBackupM();
      const backup = this._lineLengthM('backup');
      if (rec == null || backup == null) return null;
      return Math.max(0, this._ceilHalfM(rec - backup));
    }

    // How far the backup + extender fall SHORT of the recommendation.
    // Advisory: > 0 drives a warning, nothing blocks. Null when there is no
    // backup line or no recommendation to measure against.
    _backupShortfallM() {
      const rec = this._recommendedBackupM();
      if (rec == null || !this._lineState('backup')) return null;
      const backup = this._lineLengthM('backup');
      if (backup == null) return null;
      const total = backup + (this._lineLengthM('extender') || 0);
      return Number((rec - total).toFixed(3));
    }

    // The scene/summary-facing bundle. Null only when there is nothing to
    // say at all — a mainline whose webbing carries no percentage and no
    // backup line to measure.
    _recommendation() {
      const backupM = this._recommendedBackupM();
      if (backupM == null && !this._lineState('backup')) return null;
      return {
        backupM,
        extenderM: this._recommendedExtenderM(),
        shortfallM: this._backupShortfallM(),
      };
    }

    // One line's length exactly as its own editor states it — the fixed
    // variant's option value, or the custom number in the entry's unit.
    // Empty string when there is no length yet; callers supply their own
    // placeholder (the build sheet's em dash, the scene's blank label).
    _lengthTextFor(role) {
      const line = this._lineState(role);
      if (!line) return '';
      if (line.lengthMode !== 'custom') return line.fixedValue || '';
      const entry = this._entry(role);
      const unit = (entry && entry.custom && entry.custom.unit) || 'm';
      return line.length > 0 ? `${line.length} ${unit}` : '';
    }

    // A recommendation (always computed in METRES) stated in the unit the
    // line it's aimed at is actually sold in, re-rounded up there — the
    // same conversion _prefillLength writes into the field, so the number
    // the note recommends is the number the field gets.
    _recText(role, meters) {
      if (!(meters >= 0)) return '';
      const entry = this._entry(role);
      const unit = String((entry && entry.custom && entry.custom.unit) || 'm').toLowerCase();
      const v = this._ceilHalfM(unit === 'ft' ? meters * FT_PER_M : meters);
      return `${this._fmtNum(v)} ${unit}`;
    }

    // Trailing ".0" off a half-metre value ("11" not "11.0", "11.5" stays).
    _fmtNum(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '';
      return String(Math.round(n * 100) / 100);
    }

    // The build sheet's note under the Backup rows: what length the
    // mainline's webbing asks the backup to be, and — when the backup +
    // extender fall short — how far short they are. Null when the mainline
    // webbing carries no percentage (assumption 2 in the plan: no
    // recommendation is not a reason to withhold the backup itself).
    _recommendationNote() {
      const rec = this._recommendedBackupM();
      if (rec == null) return null;
      const pct = this._backupPct();
      const mainText = this._lengthTextFor('main');
      let text = `Recommended: ${this._recText('backup', rec)}`;
      if (pct != null && mainText) text += ` — ${this._fmtNum(pct)}% longer than your ${mainText} mainline`;
      const short = this._backupShortfallM();
      if (short > 0) text += `. ${this._recText('backup', short)} short right now.`;
      return { text, warn: short > 0 };
    }

    // ---- line add / remove -------------------------------------------------

    // A new line starts on the FIRST collection webbing rather than the
    // mainline's: a backup is a separate choice (usually a different, often
    // cheaper webbing), and copying the mainline would read as a decision
    // already made.
    _addBackup() {
      if (this._lineState('backup')) return this._lineState('backup');
      const line = this._defaultLine('backup', this.webbingKeys[0]);
      this._prefillLength('backup', line, this._recommendedBackupM());
      this.state.lines.backup = line;
      // No overlay work: adding a line adds a DIMENSION ROW with its own
      // value chip (the scene's job, via _afterChange's redraw), not an
      // editor. The overlay holds at most the one pill that is open, and
      // adding a line does not open one.
      this._afterChange();
      return line;
    }

    // The extender extends the BACKUP — removing the backup takes it with
    // it, and any open editor pointing at either has nothing left to edit.
    _removeBackup() {
      if (!this._lineState('backup')) return;
      this.state.lines.backup = null;
      this.state.lines.extender = null;
      this._removeConfirm = null;
      // _closeViewsFor drops the length pill along with the zoom and the
      // panel when it points at a line that has just gone.
      this._closeViewsFor(['backup', 'extender']);
      this._afterChange();
    }

    // Defaults to the backup's own webbing: an extender is a continuation
    // of the backup line, so matching it is the answer that needs no
    // explaining (Jerry's assumption 5 in the plan).
    _addExtender() {
      const backup = this._lineState('backup');
      if (!backup || this._lineState('extender')) return this._lineState('extender');
      const line = this._defaultLine('extender', backup.webbing);
      this._prefillLength('extender', line, this._recommendedExtenderM());
      this.state.lines.extender = line;
      this._afterChange();
      return line;
    }

    _removeExtender() {
      if (!this._lineState('extender')) return;
      this.state.lines.extender = null;
      this._removeConfirm = null;
      this._closeViewsFor(['extender']);
      this._afterChange();
    }

    // "Remove" on a build-sheet section header is a TWO-STEP action: the
    // first click swaps the header for a red confirm, the second commits.
    // Deliberately not window.confirm (a browser dialog is out of the
    // question here) and deliberately not a one-click red button either —
    // No Loop can be one click because it edits ONE end that is on screen,
    // where this discards a whole configured line. The confirm is transient
    // UI state: any other change, an Escape, or the removal itself clears
    // it, and it never reaches the URL.
    // `host` is the control cluster the ask came FROM — the stage row's
    // per-line wrapper, or nothing for the sheet header. Both render a
    // confirm for the same role off the one `_removeConfirm`, so focus has
    // to be told which of the two to land in: sending it to the other
    // scrolls the customer away from the control they just pressed, and on
    // a phone the sheet is below the fold entirely. Falls back to a
    // document-order search (the sheet, which precedes the stage) when the
    // cluster was re-rendered out from under us by _renderSummary.
    _askRemoveLine(role, host) {
      if (!this._lineState(role)) return;
      this._removeConfirm = role;
      this._renderSummary();
      this._syncStageActions();
      const scope = (host && host.isConnected) ? host : this;
      const btn = scope.querySelector(`[data-bc-remove-confirm="${role}"]`);
      if (btn) btn.focus();
    }

    _cancelRemoveLine(host) {
      if (!this._removeConfirm) return;
      const role = this._removeConfirm;
      this._removeConfirm = null;
      this._renderSummary();
      this._syncStageActions();
      const scope = (host && host.isConnected) ? host : this;
      const btn = scope.querySelector(`[data-bc-remove-line="${role}"]`);
      if (btn) btn.focus();
    }

    // The question BOTH two-step controls ask. The sheet header shows it as
    // text where the stage row's compact confirm carries it as an accessible
    // name — one source, so the "takes the extender with it" warning cannot
    // go missing from whichever of the two the customer used.
    _removeAsk(role) {
      const takes = role === 'backup' && this._lineState('extender') ? ' and its extender' : '';
      return `Remove ${this._roleLabel(role).toLowerCase()}${takes}?`;
    }

    // Pre-fill a fresh line's CUSTOM length from a recommendation in metres.
    // Only when the entry sells by length at all — a fixed-variant-only
    // webbing has no field to pre-fill, and forcing custom mode there would
    // resolve to no cart line. The value is converted into the entry's own
    // unit and re-rounded up there, so a ft-sold backup never lands short.
    _prefillLength(role, line, meters) {
      const entry = this._entry(role, line.webbing);
      if (!entry || !entry.custom || !(meters > 0)) return;
      const unit = String(entry.custom.unit || 'm').toLowerCase();
      line.lengthMode = 'custom';
      line.length = this._ceilHalfM(unit === 'ft' ? meters * FT_PER_M : meters);
    }

    // Drop EVERY editor pointing at a line that has gone away — the zoom,
    // the webbing panel, and the length pill — and rebuild what held them;
    // the caller's _afterChange only redraws the scene and the build sheet.
    // The pill is in here rather than left to the callers (which is where
    // the overlay's rebuild used to live, back when it had a group per
    // line) because it is now exactly the same kind of dangling reference
    // the other two are: one role, named, with nothing behind it.
    _closeViewsFor(roles) {
      let closed = false;
      if (this.state.endView && roles.includes(this.state.endView.role)) {
        this.state.endView = null;
        closed = true;
      }
      if (this.state.panel && roles.includes(this.state.panel.role)) {
        this.state.panel = null;
        closed = true;
      }
      if (this._lengthOpen && roles.includes(this._lengthOpen)) {
        this._lengthOpen = null;
        this._renderOverlay();
      }
      if (!closed) return;
      this._renderPicker();
    }

    // ---- part activation (hotspots / summary rows) ------------------------

    // Part ids are role-scoped ('backup:end1'); a BARE id is the
    // mainline's, which is how the section's static data-bc-part="webbing"
    // button and any id minted before roles existed still resolve. An
    // unrecognised prefix reads as a bare part rather than dropping the
    // activation — a stale id must not become a dead control.
    _parsePart(id) {
      const s = String(id == null ? '' : id);
      const i = s.indexOf(':');
      if (i === -1) return { role: 'main', part: s };
      const role = s.slice(0, i);
      return ROLES.includes(role) ? { role, part: s.slice(i + 1) } : { role: 'main', part: s };
    }

    // The inverse. The mainline keeps BARE ids so the DOM the section ships
    // and everything already matching on them stay correct.
    _partId(role, part) {
      return role === 'main' ? part : `${role}:${part}`;
    }

    _panelIs(role, part) {
      const p = this.state.panel;
      return !!p && p.role === role && p.part === part;
    }

    _activatePart(id, opts = {}) {
      const parsed = this._parsePart(id);
      const role = parsed.role;
      let part = parsed.part;
      // A 'face' activation is a STALE id only — a summary row in cached
      // markup, or a link minted before orientation moved onto the sewn
      // loops. No live control emits one (the scene draws no mid-line
      // hotspot on any role since 2026-08-01). The line has no face
      // control to open, so it lands on the webbing picker rather than
      // dropping the click. Ahead of the PARTS guard, which no longer
      // lists it.
      if (part === 'face') part = 'webbing';
      if (!PARTS.includes(part)) return;
      if (!this._lineState(role)) return;
      // Going ANYWHERE abandons a pending remove-confirm — which is what
      // _askRemoveLine's "any other change clears it" has always claimed
      // and nothing actually did. Ahead of the end-view branch so it
      // covers every activation, not just the full-view ones — and it
      // re-renders the sheet itself rather than trusting the paths below
      // to: opening an END only recomputes when it auto-filled a loop-less
      // end, so the cleared confirm could otherwise stay on screen.
      if (this._removeConfirm) {
        this._removeConfirm = null;
        this._renderSummary();
        this._syncStageActions();
      }
      if (part === 'end1' || part === 'end2') {
        this._setEndView(role, part === 'end1' ? 1 : 2, opts);
        return;
      }
      // LENGTH is its own editor and its own toggle, and it lives entirely
      // on the diagram — no `state.panel`, no picker, no scene view change.
      // Ahead of the panel handling below so none of that runs for it.
      //
      // Reached from BOTH routes to the same action, which is why the
      // toggle lives here and not in either of them: the value CHIP on the
      // dimension (pointer + keyboard, via the scene's part-click) and the
      // build sheet's Length row (the full-size target, and the AT route).
      // Re-activating the row whose pill is open CLOSES it — the same
      // toggle-on-repeat the webbing picker has, and what makes a second
      // tap on a value put the number back.
      if (part === 'length') {
        // ONE editor at a time, and leaving an END VIEW is the half that
        // matters: a zoomed scene draws no length dimensions at all, so a
        // pill opened from the build sheet's Length row while zoomed in
        // would have no anchor to pin to and would hide itself. The
        // zoom-out's tween is cancelled by the scene draw _openLengthEditor
        // does a moment later — a jump rather than a fly-out, which is the
        // honest reading of "take me to a different editor". (Since
        // 2026-08-02 that open is a class toggle rather than a draw, so
        // the scene hands the tweening case BACK for a real render rather
        // than letting the fly-out play out — see its setLengthOpen.)
        //
        // (The webbing picker is a modal in the TOP LAYER, so nothing
        // behind it is clickable and this branch cannot be reached with one
        // open. Clearing it is defensive, and deliberately without
        // _closeWebbingPanel's focus handoff, which would fight the pill's
        // own.)
        //
        // Both closes are CONDITIONAL, because the everyday case is that
        // neither is open and this is a bare tap on a value chip: _zoomOut
        // returns untouched with no end view, and _renderPicker is a
        // hidden-flag write plus an innerHTML wipe on a host that is
        // already empty. Small, but it is style invalidation on a heavy
        // page inside the gesture that has to feel instant, for nothing.
        // (_zoomOut renders the picker itself when it does close one, so
        // the guard here covers only the panel clear above it.)
        const hadPanel = !!this.state.panel;
        if (hadPanel) { this.state.panel = null; this._panelOpener = null; }
        this._zoomOut();
        if (hadPanel) this._renderPicker();
        if (this._lengthOpen === role) this._closeLengthEditor({ refocus: true });
        else this._openLengthEditor(role);
        return;
      }
      // webbing lives in the full view.
      const wasEnd = this.state.endView != null;
      this.state.endView = null;
      if (part === 'webbing') {
        // Activating the SAME part again closes its panel (unless we just
        // came back from an end view, where it should simply open).
        this.state.panel = (this._panelIs(role, part) && !wasEnd) ? null : { role, part };
      } else {
        this.state.panel = null;
      }
      // ...and the other side of the same rule: opening the picker closes
      // any open length pill. Before the renders, so the scene draw below
      // already has the value back on its dimension.
      this._lengthOpen = null;
      this._renderOverlay();
      // Remembered BEFORE the render that opens the modal, since the
      // renders below can replace the opener (a summary row) — see
      // _closeWebbingPanel, which hands focus back to it.
      this._panelOpener = opts.opener || null;
      this._renderPicker();
      this._renderScene();
    }

    // Opening the editor on a LOOP-LESS end lands you in a real loop
    // (Jerry, 2026-07-31: "when adding a loop when there isn't one, it's
    // not clear what you are supposed to click… I guess when you add a
    // loop, it should automatically be the BC Default?"). A 'none' end
    // used to open a panel with no opening, no sleeve, no pattern and a
    // single un-checked Naked pill — an editor with nothing in it, which
    // is what made the next click a guess. Adding the loop IS the point
    // of the click (the diagram hotspot literally reads "add a sewn
    // loop"), so the webbing's default design is applied on the way in.
    //
    // THE TRIGGER IS THIS FUNCTION, not the render: _setEndView is the
    // one explicit "open End N" path (diagram hotspot, ghost "+", and
    // the build-sheet row all reach it through _activatePart), while
    // _renderPicker runs on every field commit. Auto-filling from the
    // render would fight the No Loop action forever. No Loop itself
    // _zoomOut()s, so the panel is CLOSED when the loop goes away and
    // only a fresh open can re-fill it.
    _setEndView(role, end, opts = {}) {
      const item = this._endItem(role, end);
      const filled = !!(item && item.treatment === 'none');
      if (filled) this._restoreDefaults(role, end);
      this.state.panel = null;
      // The length pill goes with every other full-view editor. A zoomed
      // scene draws no length dimensions, so it would pin to nothing and
      // hide itself anyway — but closing it OUTRIGHT is what keeps the
      // tween honest: the pill is gone before the camera moves, rather
      // than blinking out mid-flight, and it is not waiting to reappear
      // over a drawing that has changed underneath it. Rendered here (not
      // left to the renders below) so the scene draw already has the value
      // back on the mainline's dimension.
      this._lengthOpen = null;
      this._renderOverlay();
      this.state.endView = { role, end };
      // Arm the settle re-align (see the bc:segment:scene-rendered handler,
      // which spends it). Set HERE for the same reason the auto-fill above
      // is: opening the editor is the one moment moving the page to the
      // panel is the point, and _setEndView is the only path that opens
      // one — while the scene re-renders on every field commit.
      this._alignOnSettle = true;
      // The auto-fill is a real build change, so the build sheet, the
      // price and the URL follow it — recompute() before the renders,
      // _writeUrl() after them. Same ordering rule _setEndNoLoop
      // documents: the scene render must be LAST, or the second one
      // lands mid zoom-tween and the settle event never arrives.
      if (filled) this.recompute();
      this._renderPicker();
      this._renderScene();
      if (filled) this._writeUrl();
      if (this.pickerHost) {
        // Opening an end lands you IN the opening input on EVERY viewport
        // (Jerry, 2026-07-31: clicking a loop should "highlight the
        // opening input" too — desktop pointer clicks used to focus
        // nothing at all, so the panel appeared with no caret in it and
        // the very next thing to do was a second click). It is the
        // panel's FIRST field and the value everything below it hangs off
        // (opening gates the sleeve, see _endPanelHtml), so it is the one
        // place an "edit this end" gesture can put the caret without
        // guessing.
        //
        // What stays viewport-specific is the SCROLL, not the focus. The
        // stacked phone panel is taller than the viewport, so
        // _scrollPanelIntoView's block:'nearest' aligned its BOTTOM edge
        // and left the opening input scrolled off above the fold (Jerry,
        // 2026-07-31: tapping a loop "scrolls too far — it should go to
        // the opening size and make that input active"); handing an
        // alignment target in replaces that with an explicit "sit just
        // under the pinned diagram" scroll. Desktop passes null and keeps
        // the plain panel-into-view behaviour — the panel fits there, so
        // aligning the page to a field inside it would scroll for no
        // reason.
        //
        // The target is the PANEL's top, not the opening input's: since
        // the editor became a top-to-bottom flow the opening is the third
        // row, so aligning to it would hide the two decisions above it
        // (the presets and the loop style) behind the pinned diagram —
        // the same "scrolled too far" complaint one row further down.
        // Aligning the panel's top shows the whole flow from its start
        // and still lands well short of the old bottom-edge alignment,
        // while the focus below still puts the caret in the opening.
        //
        // focus() then runs SYNCHRONOUSLY inside the activating gesture:
        // iOS only opens the keyboard for a focus that is still part of
        // the user's tap, so nothing async may come between. select()
        // makes the first keystroke replace the value rather than append
        // to it (same reason the custom-length field selects itself).
        //
        // A panel with no opening input (an authored-text pattern, or the
        // 'none' safety-net branch) has nothing to land on — so it
        // neither scrolls to it nor pops a keyboard for it, and keyboard
        // activation keeps its old fallback chain.
        const opening = this.pickerHost.querySelector('[data-bc-opening]');
        this._scrollPanelIntoView(this._isNarrow() ? this.pickerHost : null);
        if (opening) {
          // preventScroll: on the phone the alignment above already
          // placed it and the browser's own focus scroll-into-view would
          // only fight that (iOS still lifts the field clear of the
          // keyboard if it must); on desktop it keeps the panel-into-view
          // scroll from being nudged a second time.
          opening.focus({ preventScroll: true });
          if (typeof opening.select === 'function') opening.select();
        } else if (opts.via === 'key') {
          // Reached only when there is NO opening input — the branch
          // above owns that case on every viewport now, so the chain's
          // first selector is redundant by construction and kept only so
          // the priority order still reads whole. In practice this lands
          // on the checked treatment pill (the radios are visually
          // hidden; the label carries the focus ring), then on whatever
          // field exists — which since the Stitching block was split out
          // is usually the sewing-pattern select. It moved columns and
          // then boxes, not wrappers: it still sits inside
          // [data-bc-sewing-item], so that last-resort selector still
          // reaches it.
          const f = this.pickerHost.querySelector('[data-bc-opening]')
            || this.pickerHost.querySelector('[data-bc-treatment]:checked')
            || this.pickerHost.querySelector('[data-bc-sewing-item] input, [data-bc-sewing-item] select');
          if (f) f.focus();
        }
      }
    }

    _zoomOut() {
      if (this.state.endView == null) return;
      this.state.endView = null;
      this._renderPicker();
      this._renderScene();
      this._ensureStageVisible();
    }

    // The stage's sticky pin only holds while its column is tall enough to
    // pin against. Closing a panel removes it from below the diagram, which
    // shortens that column — and the browser clamps the scroll position to
    // the new maximum, so we can be left scrolled PAST where the stage can
    // stay pinned. It then rides up under Horizon's sticky header and loses
    // its top band: the caption and the mainline's whole length dimension,
    // which is where its value chip and any pill opened from it live. It
    // was reported as "the length editor disappeared", and it is the
    // second of the two ways that happens — bc-segment-builder.css's
    // --bc-seg-sticky-top covers the case where the pin DOES hold, this
    // covers the case where it can't.
    //
    // It is also why the build sheet's Length row needs no scroll nudge of
    // its own (see _activatePart): the pill opens inside the stage, so
    // "make the stage visible" is the same thing as "make the editor
    // visible", and this is the function that does it.
    //
    // Scroll only when the stage actually got clipped, so closing a panel
    // with the diagram already in view never jumps.
    //
    // The delta is computed and applied by hand rather than with
    // scrollIntoView() for two measured reasons: (1) on a position:sticky
    // target scrollIntoView resolves against the PINNED box and produces
    // no movement at all, and (2) Horizon sets scroll-behavior:smooth on
    // both <html> and .page-wrapper, so anything at the default
    // behavior:'auto' animates — the diagram would glide for ~400ms right
    // under the pointer that just clicked. behavior:'instant' is therefore
    // required, not a preference.
    //
    // One pass converges: scrolling up by N moves the stage's containing
    // block down by N, which is exactly the room the sticky pin needs to
    // retake its offset.
    _ensureStageVisible() {
      // Both sticky columns get pushed up by the same clamp, so scroll by
      // whichever lost more and the diagram AND the build sheet come back
      // whole. Each one's target is read from its OWN computed `top` —
      // that is the offset its CSS rule actually asked for, so this can't
      // drift from --bc-seg-sticky-top. It also self-disables: anything
      // not currently sticky (the summary goes static under 989px, and
      // the whole sticky mode is a section setting) has no pin to restore
      // and is skipped, leaving deficit 0.
      let deficit = 0;
      [this.stage, this.querySelector('.bc-segment__summary')].forEach((el) => {
        if (!el || getComputedStyle(el).position !== 'sticky') return;
        const want = parseFloat(getComputedStyle(el).top) || 0;
        deficit = Math.max(deficit, want - el.getBoundingClientRect().top);
      });
      // 1px of slack: sticky pins land on subpixel values.
      if (deficit <= 1) return;
      const sp = this._scrollParent();
      const from = sp === window ? window.scrollY : sp.scrollTop;
      sp.scrollTo({ top: Math.max(0, from - deficit), behavior: 'instant' });
    }

    // Horizon v4 scrolls .page-wrapper, not the document — so the scroll
    // container has to be found rather than assumed. Falls back to the
    // window for a layout that does scroll the document.
    _scrollParent() {
      let n = this.parentElement;
      while (n && n.nodeType === 1) {
        const oy = getComputedStyle(n).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n;
        n = n.parentElement;
      }
      return window;
    }

    // Phone-width test, matching bc-segment-builder.css's 749px stack breakpoint (see
    // NARROW_MAX_PX). Absent matchMedia = treat as desktop, i.e. leave the
    // old behaviour alone.
    _isNarrow() {
      return typeof window.matchMedia === 'function'
        && window.matchMedia(`(max-width: ${NARROW_MAX_PX}px)`).matches;
    }

    // Bring the just-opened panel into view WITHOUT a settle window: the
    // scroll is instant (a smooth scroll kept the panel gliding under the
    // pointer for ~400ms, so first clicks on its controls landed on moving
    // content and died), and the sticky stage's compact state is applied
    // in the same frame with its max-height transition suppressed (the
    // 250ms shrink animation the scroll itself triggers was the other
    // moving target). Double instant scroll = final layout in one frame.
    //
    // `alignTo` (passed only on phones, only from _setEndView) swaps the
    // second pass for an explicit "put THIS element just under the pinned
    // stage" scroll: block:'nearest' on a panel taller than the viewport
    // aligns its BOTTOM, which is precisely the "scrolls too far" bug.
    // Same hand-computed delta + behavior:'instant' as _ensureStageVisible,
    // for the same two reasons (scrollIntoView resolves against a PINNED
    // sticky box; Horizon's scroll-behavior:smooth animates the default).
    _scrollPanelIntoView(alignTo) {
      if (!this.pickerHost) return;
      const svg = this.scene && this.scene.querySelector('svg');
      if (svg) svg.style.transition = 'none';
      this.pickerHost.scrollIntoView({ block: 'nearest' });
      this.classList.toggle('bc-segment--compact', this.getBoundingClientRect().top < -40);
      if (alignTo) {
        // Two passes, because the two states feed each other: the align
        // scroll can push this component's top past the compact threshold,
        // and compacting shrinks the stage — which is the very floor the
        // target is aligned against. Pass two re-decides both; on an
        // already-settled layout its delta is 0 and it is a no-op.
        this._alignUnderStage(alignTo);
        this.classList.toggle('bc-segment--compact', this.getBoundingClientRect().top < -40);
        this._alignUnderStage(alignTo);
      } else {
        this.pickerHost.scrollIntoView({ block: 'nearest' });
      }
      // The transition is restored a frame later, not now: the two
      // scrolls above run against a stage whose max-height must not be
      // animating under them (see the head of this function).
      requestAnimationFrame(() => {
        if (svg) svg.style.transition = '';
      });
    }

    // Scroll `el` to sit just below whatever is pinned above it. Delta
    // computed and applied by hand for the reasons _ensureStageVisible
    // documents at length — behavior:'instant' is required, not a taste.
    _alignUnderStage(el) {
      const delta = el.getBoundingClientRect().top - (this._stageFloor() + PANEL_SCROLL_GAP);
      // 1px of slack: sticky pins land on subpixel values.
      if (Math.abs(delta) <= 1) return;
      const sp = this._scrollParent();
      const from = sp === window ? window.scrollY : sp.scrollTop;
      // Overscroll is clamped by the scroller itself; a panel too close to
      // the page foot simply lands as high as the document allows.
      sp.scrollTo({ top: Math.max(0, from + delta), behavior: 'instant' });
    }

    // The viewport y the panel has to clear: the bottom edge of the pinned
    // stage, or — when the diagram isn't sticky at all (a section setting)
    // — Horizon's own sticky header, read from the same
    // --bc-seg-sticky-top the CSS pins against so the two can't drift.
    //
    // Deliberately computed as the stage's pin offset + its HEIGHT rather
    // than read off its current rect: the stage sits above the panel, so
    // it WILL be pinned once this scroll lands, while its live rect still
    // describes where it was standing before. Height is safe to read here
    // only because _scrollPanelIntoView has already killed the svg's
    // max-height transition — otherwise this measures a mid-shrink stage.
    _stageFloor() {
      const header = parseFloat(getComputedStyle(this).getPropertyValue('--bc-seg-sticky-top')) || 0;
      if (!this.stage) return header;
      const cs = getComputedStyle(this.stage);
      if (cs.position !== 'sticky') return header;
      return Math.max(header, (parseFloat(cs.top) || 0) + this.stage.getBoundingClientRect().height);
    }

    // 'full' | '<role>:end1|end2'. The mainline's stays BARE ('end1'), the
    // view name the scene has always drawn from.
    _sceneView() {
      const v = this.state.endView;
      return v ? this._partId(v.role, `end${v.end}`) : 'full';
    }

    // ---- overlay (THE floating on-diagram length editor) -------------------
    //
    // TAP-TO-EDIT (round 5, 2026-08-02 — Jerry's pick from an interactive
    // mockup of three length-entry options). Each length dimension draws
    // its VALUE as a chip; activating one opens THE editor pill here — one
    // for the whole scene — at that dimension, and the row it belongs to
    // stops drawing its value for as long as it is up. The pill contains
    // what it always has (the fixed-length select + Custom, the custom
    // number input and its unit, the ft/m readout) plus a Done button and,
    // on the backup, the recommendation / shortfall note under the
    // controls.
    //
    // Lineage, since four rounds are dead and the scars are all over this
    // file:
    //   v1  box in a measured gap in the dimension line, the gap sized from
    //       the box's own measured width; on a tight band it covered its
    //       own arrows.
    //   v2  a dimension row per band piece (the scene's B_DIM_STEP) so two
    //       boxes could not collide — still ON the row, still covering it.
    //   v3  editors off the canvas entirely, into a [data-bc-lengths] strip
    //       below the sticky stage; rows display-only.
    //   v4  boxes back on the canvas, one per line, permanently visible,
    //       each floating clear of its own row on a rung the canvas
    //       reserved for it.
    //   v5  one box, on demand, at the value you tapped.
    //
    // WHAT v5 BUYS. v4's three boxes had to be laid out against each other
    // and against the drawing at every viewport, which cost a reserved rung
    // per line (+8 / +50 / +125 canvas units) and a de-collision pass in
    // px on top. One transient box needs none of that: no rungs, no
    // stacking, no arbitration, the band's two pieces share a dimension row
    // again, and the canvas is back to v3's heights — which, under
    // bc-segment-builder.css's max-height cap, means a bigger drawing on every build.
    //
    // WHAT IT COSTS. The pill floats OVER the drawing while it is open (the
    // mockup centred it on the line outright; this clears the row by the
    // scene's ED_CLEAR and lets the foot clamp push it back when there is
    // no room below). And the length is no longer visible-and-editable at
    // once for all three lines — you see all three VALUES, and edit one at
    // a time. That is the trade Jerry chose.
    //
    // The rebuild-vs-sync discipline below is v3's, kept through both
    // moves: it is what stops a rebuild landing on the caret. It matters
    // less now (there is only a pill to rebuild when there IS one) but it
    // matters for exactly as long as one is open, which is exactly when
    // someone is typing in it.

    // Rebuilt whenever the pill OPENS, CLOSES or changes STRUCTURE (the
    // length-mode switch that adds/removes the number input; a webbing
    // switch that changes the fixed values). Never on a value change and
    // never while typing: a rebuild replaces the very input the caret is
    // in. Values and the backup's note are written in place instead
    // (_syncOverlay), the same split _renderSummary makes.
    _renderOverlay() {
      const host = this.overlayHost;
      if (!host) return;
      const role = this._lengthOpen;
      // CLOSED is the common state and it renders NOTHING — no hidden
      // groups parked off-screen, no per-line DOM waiting to be shown. A
      // closed pill is not a positioning problem, which is the whole
      // simplification round 5 is made of.
      if (!role || !this._lineState(role)) {
        // A role naming a line that has gone is CLEARED, not just skipped:
        // left set it would keep telling the scene to blank a dimension
        // that no longer exists, and every close path would think there
        // was still something to close. _closeViewsFor catches removals
        // directly — this is the net for any other way a line can vanish
        // (a permalink load, a cart-edit sync).
        this._lengthOpen = null;
        host.innerHTML = '';
        this._overlaySigCur = null;
        this._overlayDirty = false;
        return;
      }
      // Anchored on that line's own length dimension — the anchor name IS
      // the line's length part id ('length' for the mainline,
      // 'backup:length', 'extender:length'), which is also how every
      // control inside it routes back to its line (_overlayRoleFromEvent)
      // and what the scene matches `model.lengthOpen` against.
      host.innerHTML =
        `<div class="bc-seg__ov" data-bc-anchor="${this._attr(this._partId(role, 'length'))}">` +
        this._lengthEditorHtml(role) + this._lengthNoteHtml(role) +
        `</div>`;
      this._overlaySigCur = this._overlaySig();
      // A rebuild satisfies any banked drift (see _syncOverlay) — left
      // set, the next sync would rebuild once more for nothing.
      this._overlayDirty = false;
      this._syncOverlay();
      // A rebuild replaces the pill, so the fresh one has no position at
      // all until this runs. Deliberately AFTER _syncOverlay — the note it
      // writes changes the group's height, which is what the near-edge to
      // centre conversion is computed from.
      this._positionOverlay();
    }

    // The open pill's group, if there is one.
    _overlayGroup(role) {
      const host = this.overlayHost;
      if (!host) return null;
      return host.querySelector(`[data-bc-anchor="${this._partId(role, 'length')}"]`);
    }

    // Which line an overlay control edits: the anchor name of the group it
    // sits in. Falls back to the mainline, which is what a control outside
    // any group (impossible today, but the fallback keeps a stale DOM from
    // becoming a dead control) would have edited before roles existed.
    _overlayRoleFromEvent(e) {
      const group = e.target.closest && e.target.closest('[data-bc-anchor]');
      if (!group) return 'main';
      return this._parsePart(group.getAttribute('data-bc-anchor')).role;
    }

    // The STRUCTURE of the open pill: which line it edits and which length
    // CONTROLS that line renders. VALUES are deliberately absent — the
    // typed length and the selected fixed value move on every keystroke,
    // and a signature that tracked them would rebuild the field in use.
    // `lengthMode` IS here, because custom mode adds the number input
    // beside the select and dropping back to fixed takes it away.
    // Null while closed, which makes "the pill opened or closed" a
    // signature change like any other.
    _overlaySig() {
      const role = this._lengthOpen;
      const line = role && this._lineState(role);
      if (!line) return null;
      const entry = this._entry(role);
      return JSON.stringify([
        role,
        this._fixedValues(role),
        !!(entry && entry.custom),
        entry && entry.custom ? entry.custom.unit : '',
        line.lengthMode,
      ]);
    }

    // Everything that moves WITHIN that structure, written in place on
    // every recompute() — so a per-keystroke length edit never touches the
    // DOM the caret is in. Two things move: the ft/m readout (every
    // keystroke), and the backup's recommendation line, which follows the
    // MAINLINE's length and so can change while the BACKUP's pill is the
    // one open. The ROLE is not one of them — the scene writes it on the
    // dimension row the pill floats beside, which is the one place it can
    // be said once for both.
    //
    // The custom-length INPUT's own value is never written here, only by a
    // rebuild: _onInput has already sanitized what is in the field, and
    // echoing state back into it mid-entry would eat a trailing "." (state
    // holds 10, the field holds "10.").
    //
    // It also rebuilds on structural DRIFT, the same safety net
    // _renderSummary's row-count re-check is: every path that changes the
    // structure calls _renderOverlay itself, and this catches a DOM that
    // got out of step some other way. Guarded on focus, so the net can
    // never be the thing that destroys a field being typed in.
    _syncOverlay() {
      const host = this.overlayHost;
      if (!host) return;
      if (this._overlaySigCur !== this._overlaySig() || this._overlayDirty) {
        // Never under the caret — but REMEMBERED, so the pass after focus
        // leaves the overlay still heals the drift rather than waiting for
        // some unrelated change to re-enter recompute().
        if (host.contains(document.activeElement)) {
          this._overlayDirty = true;
        } else {
          this._overlayDirty = false;
          this._renderOverlay();
          return;
        }
      }
      const group = this._lengthOpen && this._overlayGroup(this._lengthOpen);
      if (!group) return;
      const conv = group.querySelector('[data-bc-length-conv]');
      if (conv) {
        const text = this._lengthConvText(this._lengthOpen);
        if (conv.textContent !== text) conv.textContent = text;
      }
      this._syncOverlayNotes();
    }

    // One line's length editor: a select over the fixed lengths (+ Custom),
    // in custom mode the numeric input beside it, the ft/m readout, and
    // Done. The same controls through all five rounds — where the pill
    // SITS, and now whether it is there at all, has been redecided four
    // times; what it contains has not.
    _lengthEditorHtml(role) {
      const line = this._lineState(role);
      const entry = this._entry(role);
      if (!line || !entry) return '';
      const fixed = this._fixedValues(role);
      const hasCustom = !!entry.custom;
      if (!fixed.length && !hasCustom) {
        // Defensive: a webbing in the collection with neither fixed
        // variants nor a custom-cut option. Still a PILL, and still with a
        // Done — an opened editor that cannot be closed by any visible
        // control is a trap, and this is the one branch where the customer
        // has the least idea what happened.
        return '<div class="bc-seg__ov-pill">' +
          '<span class="bc-seg__ov-note">Not orderable — see the product page</span>' +
          '<button type="button" class="bc-seg__ov-done" data-bc-length-done>Done</button>' +
          '</div>';
      }
      const parts = [];
      // No "Length" caption in the pill. It carried one in v1/v2, when the
      // box sat IN the dimension and had to say what it was; a pill opened
      // BY a length value, sitting on a row already labelled "MAINLINE ·
      // MANTRA MK4" with an arrowed span under it, is not an unlabelled
      // box. Leaving it out also keeps the pill narrow, which is what lets
      // it land on a phone at all. The select's aria-label still names it.
      if (fixed.length) {
        const items = fixed.slice();
        if (hasCustom) items.push('__custom__');
        const current = line.lengthMode === 'custom' ? '__custom__' : line.fixedValue;
        parts.push(
          `<select data-bc-length-select aria-label="Segment length">${items.map((v) => {
            const label = v === '__custom__' ? 'Custom' : v;
            const sel = String(v) === String(current) ? ' selected' : '';
            const variant = v !== '__custom__' ? entry.variants[v] : null;
            const soldOut = variant && variant.available === false ? ' — sold out' : '';
            return `<option value="${this._attr(v)}"${sel}>${this._esc(label + soldOut)}</option>`;
          }).join('')}</select>`
        );
      }
      if (line.lengthMode === 'custom' && hasCustom) {
        parts.push(this._customLengthFieldHtml(role));
      }
      // ft/m readout in BOTH length modes — fixed lengths convert too,
      // parsed off the variant's option value.
      parts.push(`<span class="bc-seg__ov-conv" data-bc-length-conv>${this._esc(this._lengthConvText(role))}</span>`);
      // DONE. Escape and a tap outside both close the pill, but neither is
      // discoverable and neither is reachable with a thumb — this is the
      // visible way out, and on a phone it is the only obvious one. It also
      // gives keyboard users a focusable terminus inside the pill: tabbing
      // past it leaves the editor, which is the same thing.
      parts.push(`<button type="button" class="bc-seg__ov-done" data-bc-length-done>Done</button>`);
      return `<div class="bc-seg__ov-pill">${parts.join('')}</div>`;
    }

    // The typed-length field, as its own fragment. Extracted because TWO
    // paths put it on screen — the pill rebuild above, and the in-place
    // mode switch (_swapOverlayCustomInput) — and a second copy would be
    // free to drift on the attributes that decide what a touch keyboard
    // offers (`inputmode="decimal"`), which is the kind of difference
    // nobody notices until a customer cannot type a decimal point.
    //
    // `enterkeyhint="done"` (Jerry, 2026-08-02: "can we make it so pressing
    // enter in the custom length input confirms the length? Same for the
    // checkmark on iOS?") is a LABEL and nothing else — it renames the
    // return key on the keyboards that HAVE one (Android's numeric pad,
    // iPadOS) to Done, so the key says what pressing it now does. The
    // BEHAVIOUR is two separate paths, because the two keyboards give two
    // different signals: a return key sends Enter, which the keydown
    // branch in _wireEvents commits and closes on; iOS's decimal pad has no
    // return key at all and its checkmark only BLURS the field, which the
    // overlay's focusout close picks up (_onOverlayFocusOut). Both end in
    // the same commit — the same _commitBoundedInput a change event runs.
    _customLengthFieldHtml(role) {
      const line = this._lineState(role);
      const entry = this._entry(role);
      if (!line || !entry || !entry.custom) return '';
      const unit = entry.custom.unit || 'm';
      return `<span class="bc-seg__ov-len">` +
        `<input type="text" inputmode="decimal" enterkeyhint="done" data-bc-custom-length ` +
        `aria-label="${this._attr(`Length in ${unit}`)}" value="${this._attr(line.length || '')}">` +
        `<span class="bc-seg__ov-unit">${this._esc(unit)}</span>` +
        `</span>`;
    }

    // The custom↔fixed switch as a MUTATION of the pill already on screen,
    // instead of _renderOverlay's innerHTML rebuild. Returns the control
    // that should now hold focus, or null when the pill is not in a shape
    // this can patch — the caller falls back to a rebuild, so the two paths
    // can never disagree about what the pill contains.
    //
    // WHY (Jerry, 2026-08-02, Mac Firefox: "its mainly when I first select
    // custom length, it takes about 1 second to be able to edit
    // anything"). The rebuild cost three things, none of which it needed
    // to:
    //
    //   1. A BOX WITH NO HISTORY. Destroying and re-creating the pill means
    //      the input handed focus a line later has never been laid out
    //      anywhere. That is what makes the browser's focus
    //      scroll-into-view guess — and guess badly, since the box is
    //      absolutely positioned inside a position:sticky stage. With
    //      Horizon's scroll-behavior:smooth on the scroller that guess is a
    //      ~400ms animation with the field moving under the caret. The
    //      focus() call carries preventScroll now (see _onChange), and
    //      keeping the box alive removes the reason it was ever asked.
    //
    //   2. A <select> DESTROYED INSIDE ITS OWN change EVENT. On macOS a
    //      select is a native popup widget mid-teardown at that moment;
    //      tearing down its frame from inside the dispatch it is still
    //      unwinding is not a case any engine has reason to be fast at, and
    //      Firefox/macOS is the pairing where it shows.
    //
    //   3. A forced whole-page layout before the caret ever landed — the
    //      fresh pill has no position, so _renderOverlay must measure and
    //      pin it (three getBoundingClientRects) between the customer's
    //      pick and the field being usable. On a document that is
    //      overflow:hidden html/body + a 100dvh .page-wrapper + two sticky
    //      columns, that is this page's most expensive single operation;
    //      the in-file estimate is ~10ms in Chrome and Firefox's layout on
    //      this DOM is several times that.
    //
    // Mutating pays none of them: the select stays alive, ONE node is
    // inserted beside it, the pill keeps its position (so the reposition
    // can move to a frame, after the focus), and the field the caret lands
    // in is a real laid-out box rather than a microsecond-old one.
    _swapOverlayCustomInput(role, wantCustom) {
      const group = this._overlayGroup(role);
      const pill = group && group.querySelector('.bc-seg__ov-pill');
      if (!pill) return null;
      const line = this._lineState(role);
      const entry = this._entry(role);
      if (!line || !entry) return null;
      const select = pill.querySelector('[data-bc-length-select]');
      const existing = pill.querySelector('.bc-seg__ov-len');
      if (wantCustom) {
        // No select to insert after, or a webbing with no custom-cut option
        // at all: not a pill this understands. Hand it back.
        if (!select || !entry.custom) return null;
        if (!existing) {
          const holder = document.createElement('div');
          holder.innerHTML = this._customLengthFieldHtml(role);
          const field = holder.firstElementChild;
          if (!field) return null;
          select.after(field);
        }
      } else if (existing) {
        existing.remove();
      }
      // The pill now MATCHES what a rebuild would have produced, so the
      // structural signature has to say so — left stale, the next
      // _syncOverlay would see drift and rebuild the field we just handed
      // the caret to (or, with the caret in it, bank the drift and rebuild
      // the moment focus leaves).
      this._overlaySigCur = this._overlaySig();
      this._overlayDirty = false;
      return wantCustom ? pill.querySelector('[data-bc-custom-length]') : select;
    }

    // The backup's recommendation line, riding its own pill directly under
    // the input the number is about. ALWAYS rendered while that pill is
    // open (empty when there is nothing to recommend — .bc-seg__ov-rec:empty
    // is display:none, so an empty one costs no layout): the recommendation
    // depends on the MAINLINE's length, which can change while the backup's
    // pill is up, and _syncOverlayNotes can only fill a container that
    // exists. Emitting it conditionally meant a recommendation that became
    // computable later never appeared at all — and _overlaySig deliberately
    // has no recommendation term, so no rebuild was coming to fix it.
    //
    // It is only on screen while the pill is, which is the one real loss in
    // moving from v4's always-open boxes. The advisory is still discoverable
    // without it, on two channels that are always up: the scene reddens the
    // backup's dimension frame whenever the build is short (see its
    // `shortfall`), and the build sheet's Backup note states the
    // recommendation and the shortfall in words, with its own "+ Add
    // extender" beside it (_recommendationNote / _summaryBlocks).
    _lengthNoteHtml(role) {
      if (role !== 'backup') return '';
      return `<div class="bc-seg__ov-rec" data-bc-ov-rec data-sig="${this._attr(this._lengthNoteSig())}">` +
        `${this._lengthNoteInnerHtml()}</div>`;
    }

    // What the line says, flattened: the recommended value, whether the
    // extender offer is on, and — since the slot took over the shortfall
    // warning — WHICH of its two states it is in. Without that last term a
    // build that already has an extender never re-rendered the line when it
    // crossed into or out of shortfall: `_wantsExtender()` is false either
    // way once an extender exists, so the signature never moved and
    // _syncOverlayNotes short-circuited on it.
    _lengthNoteSig() {
      return [
        this._recommendedBackupM(),
        this._wantsExtender(),
        this._backupShortfallM() > 0,
      ].join('|');
    }

    _wantsExtender() {
      return this._backupShortfallM() > 0 && !this._lineState('extender');
    }

    // ONE slot, TWO states (Jerry, 2026-08-01: "maybe the recommended text
    // below the backup length can turn into that error?"). While the backup
    // + extender fall short of the recommendation the slot IS the error;
    // otherwise it states the recommendation. They are the same sentence
    // about the same number, so stacking them — which is what happened when
    // the scene drew its own "Shorter than the recommended N m" onto this
    // strip of canvas — said it twice and overlapped itself doing it.
    //
    // The error names the SHORTFALL, not the recommendation: the
    // recommended figure is what the line says in its other state, and by
    // the time you are short what you need to know is how much by. It reads
    // in the same shape as the build sheet's note ("N m short right now"),
    // which is the other place that number is written.
    //
    // This is the ONE place the words go red on the diagram. The scene
    // reddens the same dimension's frame, which is a different channel
    // (geometry, not words) — and the earlier "never in the warning red"
    // rule was written when the words were only ever the recommendation,
    // which is not an error and still renders plain.
    _lengthNoteInnerHtml() {
      const rec = this._recommendedBackupM();
      if (rec == null) return '';
      const short = this._backupShortfallM();
      const text = short > 0
        ? `${this._recText('backup', short)} short of the recommended ${this._recText('backup', rec)}`
        : `Recommended ${this._recText('backup', rec)}`;
      return (
        `<span class="bc-seg__ov-rec-text${short > 0 ? ' bc-seg__ov-rec-text--warn' : ''}">` +
        `${this._esc(text)}</span>` +
        (this._wantsExtender()
          ? `<button type="button" class="bc-seg__ov-rec-add" data-bc-add-line="extender">` +
            `+ Add extender</button>`
          : '')
      );
    }

    // The recommendation moves with the MAINLINE's length, so it can change
    // while the BACKUP's pill is open — updated in place, keyed by its own
    // content so a per-keystroke recompute() writes nothing once it has
    // settled. (Rebuilding would be wrong even though the caret is not in
    // this line: the "+ Add extender" button inside it is a live focus
    // target.)
    _syncOverlayNotes() {
      const el = this.overlayHost && this.overlayHost.querySelector('[data-bc-ov-rec]');
      if (!el) return;
      const sig = this._lengthNoteSig();
      if (el.getAttribute('data-sig') === sig) return;
      el.setAttribute('data-sig', sig);
      el.innerHTML = this._lengthNoteInnerHtml();
      // The note appearing, going away, or wrapping to a second line
      // changes the pill group's HEIGHT, and the group is positioned by its
      // near edge — so where its far edge lands, and whether the foot clamp
      // has to lift it, both move with it.
      //
      // Scheduled, not immediate: this sits inside recompute(), which runs
      // per keystroke, and _positionOverlay's three getBoundingClientRects
      // are a forced synchronous layout of the whole page. Doing it from a
      // frame keeps recompute() free of layout reads entirely — which is
      // what lets it stay on the instant clock while the drawing settles.
      // (It only runs when the WORDS changed, so it was already rare; rare
      // and free beats rare and 10ms.)
      this._scheduleOverlayPosition();
    }

    // Pin the open pill at its anchor. READ phase first (stage rect, anchor
    // point, group size), THEN the style writes — interleaved, every read
    // after a write re-ran synchronous layout of this heavy page (~10ms
    // each).
    //
    // This is the whole of v4's _positionOverlay + _layOutOverlay, which
    // together did a vertical de-collision sort, a stack lift off the
    // drawing's foot and a per-box clamp across up to three groups. With
    // one box there is nothing to de-collide and nothing to sort: what
    // survives is the near-edge → centre conversion (the thing that makes
    // the gap exactly ED_CLEAR whatever the pill's height turns out to be),
    // the clamps, and the one-shot remeasure.
    //
    // NOTE what is NOT here, and must not come back: v1 and v2 fed the
    // measured width back to the scene as the gap to cut in that line's
    // dimension, which re-rendered the scene, which fired scene-rendered,
    // which landed back in this function. The gap a row cuts is sized from
    // its own drawn value now. Measurement flows one way: the scene says
    // where, this says how big, nobody answers back.
    _positionOverlay() {
      // A scheduled pass is SPENT by any direct one that beats it to the
      // frame — chiefly the scene's own settle handler, which repositions
      // straight after the draw so the pill lands with the drawing rather
      // than a frame behind it. Without this, a recompute that queued a
      // pass and a draw that took one would pay the forced layout twice.
      if (this._posRaf) { cancelAnimationFrame(this._posRaf); this._posRaf = null; }
      const host = this.overlayHost;
      if (!host) return;
      const group = this._lengthOpen && this._overlayGroup(this._lengthOpen);
      if (!group) return;
      // The un-hide comes FIRST, before any measurement, so the whole read
      // phase below is contiguous. It used to sit between the anchor read
      // and the stage read, which on the pass that actually changed it
      // (the pill coming back after an end view) dirtied style in the
      // middle of the reads and bought a SECOND forced synchronous layout
      // of this heavy page — ~10ms, in the gesture. Idempotent when the
      // value doesn't move, which is every other pass.
      group.style.display = '';
      // Nothing to measure against yet — most often a _renderOverlay that
      // runs before the first scene draw (and can run before
      // <bc-segment-scene> has even upgraded). The pill has no position at
      // that point, so left unhidden it would paint in the stage's top-left
      // corner until the first anchor arrived.
      const p = (this.stage && this.scene && typeof this.scene.anchorPoint === 'function')
        ? this.scene.anchorPoint(group.getAttribute('data-bc-anchor'))
        : null;
      // The OTHER way this goes null: an end view draws no length
      // dimensions, so it publishes no length anchors, and mid zoom-tween
      // the anchors don't match the on-screen viewBox. Both are belt and
      // braces — the pill is closed on the way into an end view (see
      // _setEndView) — and both fail the same safe way, by hiding it.
      if (!p) { group.style.display = 'none'; return; }
      const stageRect = this.stage.getBoundingClientRect();
      // The DRAWING's foot in stage coordinates. The overlay layer spans
      // the whole sticky stage, whose last flow child is the per-line
      // button row — so a pill allowed to drift to the stage's own bottom
      // would sit ON those buttons and (pointer-events:auto) eat their
      // clicks. The scene element is the svg's box, so this is where the
      // canvas ends.
      const drawFloor = this.scene.getBoundingClientRect().bottom - stageRect.top;
      const w = group.offsetWidth;
      const h = group.offsetHeight;
      // A width of 0 means the group was display:none when it was measured
      // — it is being shown for the first time this pass. Ask for ONE more
      // pass, and only one: a builder inside a hidden container measures 0
      // forever, and a self-rescheduling loop there would never stop.
      if (w) this._remeasureTries = 0;
      else if (!(this._remeasureTries > 0)) {
        this._remeasureTries = 1;
        this._needsRemeasure = true;
      }
      // The box is translate(-50%,-50%), so these are CENTRES — but the
      // ANCHOR is not a centre: it is the point the pill's NEAR EDGE has to
      // touch, ED_CLEAR clear of its dimension line on the side that row
      // floats to. Converting here (centre = anchor ± h/2) is what makes
      // the gap exactly ED_CLEAR whatever the pill's height turns out to be
      // — a phone's taller pill and a desktop's shorter one hang off the
      // same rule, and the backup's note wrapping to a second line moves
      // its far edge, not its near one.
      const side = (p.data && p.data.side === -1) ? -1 : 1;
      let x = p.x - stageRect.left;
      let y = (p.y - stageRect.top) + side * (h / 2);
      // The overlay layer does NOT clip, so a pill centred on a narrow band
      // near the edge would hang off the stage and widen the PAGE (a phone
      // would gain a horizontal scroll). Clamped inside the drawing on both
      // axes — the stage's width, and the SVG's own foot rather than the
      // stage's, which reaches past it to the button row.
      //
      // The VERTICAL clamp is load-bearing in the ordinary case now, not
      // just at the edges: the band's dimension row sits DIM_FOOT (34
      // units) off the foot of the canvas and the pill wants ED_CLEAR + its
      // own ~30px below it, so on a backup or extender the clamp routinely
      // lifts it back over the row it is editing. That is the accepted cost
      // of the round — the drawing it covers is the drawing it is about.
      // A pill bigger than the stage on either axis is CENTRED: there is no
      // position that satisfies both edges, and centred is the least wrong.
      const stageW = stageRect.width;
      const stageH = drawFloor > 0 ? drawFloor : stageRect.height;
      x = w >= stageW ? stageW / 2 : Math.min(Math.max(x, w / 2), stageW - w / 2);
      y = h >= stageH ? stageH / 2 : Math.min(Math.max(y, h / 2), stageH - h / 2);
      group.style.left = `${Math.round(x)}px`;
      group.style.top = `${Math.round(y)}px`;
      if (this._needsRemeasure) {
        this._needsRemeasure = false;
        requestAnimationFrame(() => this._positionOverlay());
      }
    }

    // ---- opening and closing the pill --------------------------------------
    //
    // TRANSIENT UI, and deliberately not in `state`: which editor is open
    // is not part of the build, never reaches the URL and never reaches the
    // cart — the same rule `_removeConfirm` follows, and for the same
    // reason. A shared permalink opens with every pill closed and the whole
    // drawing visible, which is what someone opening a link wants to see.
    _openLengthEditor(role, opts = {}) {
      if (!this._lineState(role)) return;
      this._lengthOpen = role;
      // Scene FIRST: this row has to stop showing its value before the
      // pill lands on top of it. NOT a redraw — the anchor
      // _positionOverlay is about to read was published by the last one
      // and is not affected by the pill being open (see
      // _setSceneLengthOpen), so there is nothing for a fresh draw to
      // publish that the scene has not already got.
      this._setSceneLengthOpen();
      this._renderOverlay();
      if (opts.focus === false) return;
      // Land in the field that IS the value in this mode — the number input
      // in custom mode, otherwise the select. Same rule the build sheet's
      // Length row has always used to reach the editor.
      const group = this._overlayGroup(role);
      const f = !group ? null : (group.querySelector('[data-bc-custom-length]')
        || group.querySelector('[data-bc-length-select]'));
      // Don't focus a pill that _positionOverlay just hid for want of an
      // anchor. Read off the inline style it writes rather than
      // offsetParent: that getter flushes layout, and this runs one line
      // after the style writes that dirtied it — a second forced
      // synchronous layout of the whole page for a question the style
      // already answers.
      //
      // preventScroll for the reason the mode switch spells out at length
      // (see _onChange's length-select branch): the pill is an absolutely
      // positioned box on a sticky stage, the browser's own focus
      // scroll-into-view resolves that badly, and Horizon's
      // scroll-behavior:smooth turns the mistake into a ~400ms animation.
      // The pill is drawn over the stage the customer just tapped, so there
      // is never anything to scroll to.
      if (f && group.style.display !== 'none') {
        f.focus({ preventScroll: true });
        // A custom-mode open lands with the CURRENT length selected
        // (Jerry, 2026-08-02: "can we have the length number highlight in
        // custom length when we click the length?") — tapping the value is
        // "I want a different number", so typing should replace it, not
        // append to it. Same rule the mode switch has always applied to
        // its prefill; the select needs (and has) no equivalent.
        if (f.matches('[data-bc-custom-length]') && typeof f.select === 'function') f.select();
      }
    }

    // `refocus` sends focus back to the value chip that opened the pill —
    // it is where the customer was. It can only be reached AFTER the scene
    // has been told the pill is closed: while it is open the chip is
    // display:none (and a real draw in between destroys and rebuilds it
    // outright), and neither a hidden nor a detached element can take
    // focus. Falls back to the build sheet's Length row (a stable element)
    // when the scene isn't drawing that chip: an end zoom draws no length
    // rows at all. Skipped entirely when focus was never inside the pill,
    // so a close triggered by a click elsewhere on the page doesn't yank
    // it.
    //
    // `fromInside` is that same answer, ASSERTED by a caller that blurred
    // the field itself on the way in — the Enter commit, which has to blur
    // before it closes (see the keydown branch) and so arrives here with
    // the DOM no longer able to say where focus was a line ago. Nothing
    // else may pass it: it is a statement about the caller's own gesture,
    // not a preference.
    _closeLengthEditor(opts = {}) {
      const role = this._lengthOpen;
      if (!role) return;
      const had = opts.refocus
        && (opts.fromInside
          || (this.overlayHost
            && this.overlayHost.contains(document.activeElement)));
      this._lengthOpen = null;
      // The next pill is a fresh first paint — without this, one 0-width
      // measurement (builder inside a hidden container) would spend the
      // single remeasure retry for every open that follows.
      this._remeasureTries = 0;
      this._renderOverlay();
      // Putting the value back is the same class toggle opening it was
      // (_setSceneLengthOpen) — the chip has been redrawn with the live
      // length on every pass while it was hidden, so revealing it is
      // already correct. The ONE exception is a keystroke whose redraw is
      // still owed (the dirty flag, or the light path's rAF): that chip
      // is behind the field, so flush the draw rather than reveal a stale
      // number. Costs a render exactly when one was already coming — and
      // this is the flush point that matters most, since closing the pill
      // is how most typing ends.
      if (!this._flushScene()) this._setSceneLengthOpen();
      if (!had) return;
      const part = this._partId(role, 'length');
      const chip = this.querySelector(`[data-bc-hotspot="${part}"] .bc-segment__hot`);
      const back = chip || this._summaryRowEl(part);
      // preventScroll for the CHIP only. It lives on the pinned stage the
      // pill was floating over, so it is on screen by construction and the
      // browser's focus scroll-into-view can only glide somewhere worse
      // (Horizon's scroll-behavior:smooth — see the mode-switch branch).
      // The build-sheet ROW fallback is a different case: on a phone the
      // sheet is below the diagram and genuinely may be out of view, so
      // that one keeps the browser's scroll.
      if (back && typeof back.focus === 'function') {
        if (back === chip) back.focus({ preventScroll: true });
        else back.focus();
      }
    }

    _lengthConv(unit, value) {
      const v = Number(value) || 0;
      if (!v) return '';
      const u = String(unit || '').toLowerCase();
      // Feet render whole (the 3.3 multiple matches the webbing PDPs and
      // already rounds up vs true ft/m — decimals would oversell
      // precision), no ≈ sign (Jerry, 2026-07-30 — same for _mmConv).
      if (u === 'm') return `${Math.round(v * FT_PER_M)} ft`;
      if (u === 'ft') return `${(v / FT_PER_M).toFixed(2)} m`;
      return '';
    }

    // Conversion for the CURRENT length: custom mode reads the live input,
    // fixed mode parses the variant's option value ("2 m", "50 ft", …).
    _lengthConvText(role) {
      const line = this._lineState(role);
      if (!line) return '';
      if (line.lengthMode === 'custom') {
        const entry = this._entry(role);
        const unit = (entry && entry.custom && entry.custom.unit) || 'm';
        return this._lengthConv(unit, line.length);
      }
      const s = String(line.fixedValue || '');
      const num = parseFloat(s.replace(',', '.'));
      if (!(num > 0)) return '';
      if (/ft|feet|foot|′|'/i.test(s)) return this._lengthConv('ft', num);
      if (/cm/i.test(s)) return this._lengthConv('m', num / 100);
      return this._lengthConv('m', num);
    }

    // mm → inches secondary readout for the end-panel dimension inputs.
    // The VALUE alone is what the paired in-box shows (it carries its own
    // unit suffix, like the mm box beside it); the suffixed string is what
    // the build sheet's disclosed rows read as "(5.5 in)".
    _mmConvValue(mm) {
      const v = Number(mm) || 0;
      return v > 0 ? (v / MM_PER_IN).toFixed(1) : '';
    }

    _mmConv(mm) {
      const s = this._mmConvValue(mm);
      return s ? `${s} in` : '';
    }

    // Both editors, synced to state — ONE entry point, so every existing
    // "re-render the editors" call site keeps working whichever of them is
    // open. They live in different places: the END-LOOP editor is the
    // inline panel below the diagram (deliberately not a modal — the
    // diagram stays visible while an end is being dialed in), the WEBBING
    // picker is a modal dialog (Jerry, 2026-07-31: inline, it was pushed
    // off-screen as soon as a backup made the diagram taller).
    // Orientation has no panel at all — it edits in place on the dimension
    // line.
    // Rebuilt on STRUCTURAL changes only (open/close, webbing change,
    // treatment/pattern change) — never while typing in one of its
    // dimension inputs, which would destroy the field mid-keystroke.
    _renderPicker(focusSel) {
      const host = this.pickerHost;
      if (host) {
        const endOpen = this.state.endView != null;
        host.hidden = !endOpen;
        host.innerHTML = endOpen
          ? this._endPanelHtml(this.state.endView.role, this.state.endView.end)
          : '';
        if (endOpen && focusSel) {
          const f = host.querySelector(focusSel);
          if (f) f.focus();
        }
      }
      this._renderWebbingModal(focusSel);
    }

    // The webbing modal, driven entirely by state.panel: non-null opens
    // it (filled for that role), null closes it. showModal() is what
    // brings the focus trap, Escape and the top layer; a programmatic
    // close() here fires the dialog's close event, which the listener in
    // connectedCallback ignores precisely because state is already clear.
    // The dialog is inert on browsers without showModal (nothing opens —
    // the whole builder is JS-dependent anyway, and the product pages
    // remain the way to order the same webbings).
    _renderWebbingModal(focusSel) {
      const dlg = this.webbingModal;
      if (!dlg) return;
      const panel = this.state.panel;
      if (!panel) {
        if (dlg.open) dlg.close();
        return;
      }
      dlg.innerHTML = this._webbingPanelHtml(panel.role);
      if (!dlg.open && typeof dlg.showModal === 'function') {
        try { dlg.showModal(); } catch (_e) { /* already open in another frame */ }
      }
      // Focus lands on the CURRENT selection rather than the close button:
      // a modal has to place focus inside itself, and the tile you already
      // have is the one to arrow away from. (The radios are visually
      // hidden; .bc-seg__tile:has(input:focus-visible) carries the ring.)
      const f = (focusSel && dlg.querySelector(focusSel))
        || dlg.querySelector('[data-bc-webbing]:checked')
        || dlg.querySelector('[data-bc-webbing]');
      if (f) f.focus();
    }

    // The one way out of the webbing modal, whatever asked for it —
    // Escape, the backdrop, the ×, Save, or picking a webbing. State
    // first (it is the source of truth), then the render that actually
    // closes the dialog, then focus back to whatever opened it.
    _closeWebbingPanel() {
      if (!this.state.panel) return;
      const part = this._partId(this.state.panel.role, this.state.panel.part);
      this.state.panel = null;
      this._renderPicker();
      this._renderScene();
      // Back to the control that opened it — a stage "Change" button or a
      // summary row (the diagram has no route to the picker since
      // 2026-08-01). A row re-rendered since (the sheet rebuilds on
      // structural changes) is replaced by its live equivalent, and any
      // opener that went away falls back to the same row.
      const opener = this._panelOpener;
      this._panelOpener = null;
      const back = (opener && opener.isConnected) ? opener : this._summaryRowEl(part);
      if (back) back.focus();
    }

    // Webbing picker content: tiles with the collection-card quick-spec
    // line. The "Orientation" choice is NOT here — which side faces up is
    // a separate decision from which webbing, and since 2026-07-31 it is
    // not even a line-level one: each sewn loop chooses its own outward
    // face, in that end's editor (see _lineFaces).
    _webbingPanelHtml(role) {
      const current = this._lineWebbing(role);
      const tiles = this.webbingKeys.map((key) => {
        const w = this.webbings[key];
        const checked = key === current;
        const swatch = w.scene && w.scene.color
          ? `<span class="bc-seg__tile-swatch" style="background:${this._attr(w.scene.color)}"></span>`
          : '';
        const img = w.image ? `<img class="bc-seg__tile-img" src="${this._attr(w.image)}" alt="" loading="lazy">` : '';
        // specsHtml is server-rendered by bc-card-specs (trusted Liquid
        // output) — injected as-is so the selector matches the cards.
        const specs = w.specsHtml ? `<div class="bc-seg__tile-specs">${w.specsHtml}</div>` : '';
        return (
          `<label class="bc-seg__tile${checked ? ' is-active' : ''}">` +
          `<input type="radio" name="bc-seg-webbing" value="${this._attr(key)}" data-bc-webbing${checked ? ' checked' : ''}>` +
          img +
          `<span class="bc-seg__tile-label">${swatch}${this._esc(w.label || key)}</span>` +
          specs +
          `</label>`
        );
      }).join('');

      // The title names the LINE being changed on a multi-line build — with
      // three webbings in play, "Choose webbing" alone doesn't say which
      // one you're about to replace.
      const which = role === 'main' ? '' : ` ${this._roleLabel(role).toLowerCase()}`;
      // The inner card is what the dialog's own box is NOT: a click that
      // lands on the dialog itself (its margin, i.e. the backdrop area) is
      // read as "outside" and closes — see _onClick.
      return (
        `<div class="bc-seg__modal-inner">` +
        `<div class="bc-seg__picker-head">` +
        `<span class="bc-seg__picker-title">Choose${this._esc(which)} webbing</span>` +
        `<button type="button" class="bc-seg__picker-close" data-bc-picker-close aria-label="Close webbing picker">&times;</button>` +
        `</div>` +
        // The role is written ON the tile list, so a tile's change event
        // resolves which line it edits from its own DOM rather than from
        // state.panel — the modal may already be closing by then (a click
        // on the label reaches the delegate BEFORE the radio's change).
        `<div class="bc-seg__tiles" role="radiogroup" aria-label="Webbing" ` +
        `data-bc-webbing-role="${this._attr(role)}">${tiles}</div>` +
        this._panelFootHtml() +
        `</div>`
      );
    }

    // The line's name for headings and affordances. Only ever spelled out
    // when there is more than one line to tell apart — see _summaryBlocks.
    _roleLabel(role) {
      return ROLE_LABELS[role] || role;
    }

    // Webbing-switch fast path for the open picker: the tile LIST is
    // entry-independent, so only the active highlight changes — the tiles
    // (and their <img>s) stay in place. The previous full innerHTML
    // rebuild re-created all six tile images (decode + repaint) and
    // re-focused across the rebuild (an extra forced layout) on every
    // switch — the bulk of the visible freeze when changing webbings.
    // Falls back to a full rebuild if the panel isn't in the expected
    // shape.
    _updatePickerForWebbing(role) {
      const host = this.webbingModal;
      if (!host || this.state.endView != null) return;
      if (!this._panelIs(role, 'webbing')) return;
      const current = this._lineWebbing(role);
      const radios = host.querySelectorAll('[data-bc-webbing]');
      if (!radios.length) { this._renderPicker('[data-bc-webbing]:checked'); return; }
      radios.forEach((r) => {
        const on = r.value === current;
        if (r.checked !== on) r.checked = on;
        const tile = r.closest('.bc-seg__tile');
        if (tile) tile.classList.toggle('is-active', on);
      });
    }

    // ---- end-loop editor (the panel while zoomed into an end) -------------

    // A TOP-TO-BOTTOM DECISION FLOW (Jerry, 2026-07-31: the panel had a
    // "ton of info" and its "inputs [were] not super clear"). It used to
    // set the geometry inputs, the protection pills, the presets and a
    // whole Stitching card side by side in two boxes, so nothing said
    // which question came first, and the mm fields — 64px wide, sharing
    // their row with two hints — read as values rather than as inputs.
    // Now every decision is one captioned row, in the order the decisions
    // actually depend on each other:
    //
    //   Start from    the presets, FIRST: a one-tap starting point is the
    //                 fastest way through the whole panel, and everything
    //                 under it is "or adjust it yourself". Catalog
    //                 defaults lead (one per loop treatment the webbing
    //                 offers), then the customer's saved designs.
    //   Loop style    Sleeved / Naked / No loop as ONE segmented control.
    //                 'none' is IN the control now, where it was a red
    //                 footer button: the first thing this end decides is
    //                 whether it is sewn at all, and answering every
    //                 version of that in one control is most of what makes
    //                 the panel readable. It keeps the footer action's
    //                 semantics exactly — same data-bc-no-loop hook, so
    //                 the same _setEndNoLoop path — and keeps the red, so
    //                 the destructive answer still looks destructive.
    //   Opening size  the geometry the loop is judged by, and the value
    //                 that gates the row under it: below
    //                 SLEEVE_MIN_OPENING_MM no sleeve is allowed at all,
    //                 and above it the opening sets the sleeve's cap
    //                 (_sleeveBounds). Also where a phone tap lands,
    //                 focused and selected (see _setEndView).
    //   Sleeve length only exists once Sleeved is chosen, so it sits
    //                 directly under the control that summons it.
    //
    // Everything that DESCRIBES the loop rather than deciding it — the
    // pattern's threads, its stitch count, the sewn loop strength — moves
    // into the collapsed "Stitching details" disclosure at the foot, whose
    // summary line still names the pattern, so nothing needed at a glance
    // is actually hidden. The pattern SELECT lives there too, on the same
    // reasoning that dropped the End N Pattern cart property (Jerry,
    // 2026-07-31: "only default patterns in use") — and it usually doesn't
    // exist at all, since most webbings offer exactly one loop pattern.
    //
    // The zoomed scene renders the same numbers as static dimension
    // labels, so the diagram and this panel always agree.
    //
    // Every control stays inside [data-bc-sewing-item] — _sewingTargetFromEvent
    // and the field delegation resolve the end from that wrapper.
    // 'none' skips the opening / sleeve rows and the Stitching block with
    // the rest of the loop spec. Since 2026-07-31 that is a SAFETY NET
    // rather than a screen anyone reaches by choice: _setEndView auto-fills
    // a loop-less end with the webbing's default on the way in, and No loop
    // closes the panel rather than re-rendering it. Kept, and kept honest.
    // The whole flow is role-agnostic — one editor serves the mainline, the
    // backup and the extender, with only the title naming which line.
    _endPanelHtml(role, end) {
      const item = this._endItem(role, end);
      if (!item) return '';
      const type = this._endLoopType();
      const patterns = this._patternsFor(role, 'end_loop');
      const pattern = this._pattern(item, role);
      const isNone = item.treatment === 'none';

      const steps = [
        this._presetStepHtml(role, end, item),
        this._loopStyleStepHtml(role, end, item, isNone),
      ];
      if (!isNone) {
        // Orientation sits directly under Loop style: both are about the
        // FOLD — what the loop is made of, before the two size questions
        // under them. Absent entirely on a webbing with no face choice.
        steps.push(this._orientationStepHtml(role, end, item));
        if (pattern && pattern.openingMm != null) {
          const minB = pattern.openingMinMm != null ? pattern.openingMinMm : 50;
          const maxB = pattern.openingMaxMm != null ? pattern.openingMaxMm : 500;
          steps.push(this._dimStepHtml({
            key: 'opening',
            label: 'Opening size',
            attr: 'data-bc-opening',
            convAttr: 'data-bc-opening-conv',
            value: item.openingMm != null ? item.openingMm : '',
            min: minB,
            max: maxB,
            defaultMm: pattern.openingMm,
          }));
        }
        if (item.treatment === 'sleeved') {
          // Bounds follow the opening (see _sleeveBounds) — the panel
          // re-renders on every opening commit, so the shown range and
          // data-min/max always reflect the current opening.
          const { min, max } = this._sleeveBounds(item, role);
          steps.push(this._dimStepHtml({
            key: 'sleeve',
            label: 'Sleeve length',
            attr: 'data-bc-sleeve-length',
            convAttr: 'data-bc-sleeve-conv',
            value: item.sleeveLengthMm,
            min,
            max,
            // Shown default clamps into the live bounds so the hint never
            // advertises a value the cap forbids.
            defaultMm: Math.min(max, Math.max(min, type.sleeveDefaultMm != null ? type.sleeveDefaultMm : 200)),
            // Unreachable while the row only renders for a sleeved end
            // (which the sleeve rule won't allow under the floor) — kept
            // so an unusable row can never present an editable field.
            disabled: !this._sleeveAllowed(item, role),
          }));
        }
      }

      const title = `${this._roleTitle(role)}End ${end} loop`;
      return (
        `<div class="bc-seg__panel-fields" data-bc-sewing-item="${end}">` +
        `<div class="bc-seg__picker-head">` +
        `<span class="bc-seg__picker-title">${this._esc(title)}</span>` +
        `<button type="button" class="bc-seg__picker-close" data-bc-picker-close ` +
        `aria-label="${this._attr(`Close ${title} editor`)}">&times;</button>` +
        `</div>` +
        `<div class="bc-seg__flow">${steps.join('')}</div>` +
        (isNone ? '' : this._stitchingHtml(pattern, patterns, item)) +
        this._loopToolsHtml(item) +
        this._panelFootHtml() +
        `</div>`
      );
    }

    // Panel/summary prefix naming the line an editor belongs to. Empty for
    // the mainline: on a solo build there is nothing to disambiguate, and
    // "Mainline · End 1 loop" would be noise on every visit.
    _roleTitle(role) {
      return role === 'main' ? '' : `${this._roleLabel(role)} · `;
    }

    // Step 1 — the presets, as one-tap starting points at the TOP (they
    // pre-fill every row below them, so at the foot they read as an
    // afterthought). Catalog defaults, then the build's OWN other ends,
    // then saved designs — nearest-to-hand first: the defaults are the
    // recommendation, the other ends are what this build has already
    // decided, and a saved design is the one that comes from outside it.
    // The SAVE/manage affordances stay at the foot with _loopToolsHtml,
    // where "save what I just dialed in" belongs.
    // One chip per DESIGN (Jerry, 2026-08-01): the row offers loop
    // designs, and the same design reachable three ways — a catalog
    // default, an end that already sits on it, a saved copy of it — is
    // still ONE design; extra chips that all do the same thing only pad
    // the row. Earliest source keeps the chip (defaults, then the
    // build's other ends, then saved designs). A DISABLED default claims
    // no signature: it stands for the design it CAN'T apply, not for the
    // naked fallback its loop degraded to.
    _presetStepHtml(role, end, item) {
      const seen = new Set();
      const defaults = this._defaultChips(role, end, item);
      defaults.forEach((c) => { if (!c.disabled) seen.add(c.sig); });
      const chips = defaults
        .concat(this._buildCopyChips(role, end, item, seen))
        .map((c) => this._presetChipHtml(c.value, c.label, c.active, c.disabled));
      const saved = [];
      this.savedLoops.forEach((loop) => {
        const sig = this._loopSig(loop);
        if (seen.has(sig)) return;
        seen.add(sig);
        saved.push(this._presetChipHtml(loop.id, loop.name, this._itemMatchesLoop(item, loop)));
      });
      // Guests' designs live on the device, not the profile, and the row
      // has to say so — otherwise clearing site data reads as data loss.
      // Logged-in customers see the list exactly as they always have.
      if (saved.length && !this.customer.loggedIn) {
        chips.push(`<span class="bc-seg__chip-note">Saved on this device</span>`);
      }
      chips.push(...saved);
      if (!chips.length) return '';
      return (
        `<div class="bc-seg__step bc-seg__step--chips">` +
        `<span class="bc-seg__step-label">Start from</span>` +
        `<div class="bc-seg__chips" role="group" aria-label="${this._attr(`End ${end} loop presets`)}">` +
        `${chips.join('')}</div>` +
        `</div>`
      );
    }

    // Step 2 — the loop style. A segmented control rather than loose pills:
    // these are the mutually exclusive answers to ONE question, and a
    // joined track says that where separate pills only implied it. The
    // radios stay radios (same name, same data-bc-treatment hook, same
    // disabled rule under the sleeve floor); No loop is a BUTTON inside the
    // same track because it doesn't set a treatment so much as end the
    // step — it runs _setEndNoLoop, which closes the panel.
    _loopStyleStepHtml(role, end, item, isNone) {
      const offered = (this._sewingData(role).endTreatments || []).filter((t) => t.kind !== 'none');
      const sleeveOk = this._sleeveAllowed(item, role);
      const opts = offered.map((t) => {
        const off = t.kind === 'sleeved' && !sleeveOk;
        const on = item.treatment === t.kind;
        return (
          `<label class="bc-seg__seg-opt${on ? ' is-active' : ''}${off ? ' is-disabled' : ''}">` +
          `<input type="radio" name="bc-seg-end${end}-treatment" value="${this._attr(t.kind)}" ` +
          `data-bc-treatment${on ? ' checked' : ''}${off ? ' disabled' : ''}>` +
          `<span>${this._esc(t.label)}</span></label>`
        );
      });
      opts.push(
        `<button type="button" class="bc-seg__seg-opt bc-seg__seg-opt--none${isNone ? ' is-active' : ''}" ` +
        `data-bc-no-loop aria-pressed="${isNone ? 'true' : 'false'}">No loop</button>`
      );
      return (
        `<div class="bc-seg__step">` +
        `<span class="bc-seg__step-label">Loop style</span>` +
        `<div class="bc-seg__seg" role="group" aria-label="${this._attr(`End ${end} loop style`)}">` +
        `${opts.join('')}</div>` +
        // The one hint in the panel that explains a control you can SEE
        // but can't use, so it doesn't get to be muted like the rest
        // (Jerry, 2026-07-31: "the indication below the sleeved button
        // needs to be more obvious. Maybe make it red?"). --warn takes it
        // to the panel's danger red at full opacity; every other hint
        // stays as it was.
        //
        // Always RENDERED (hidden when the opening allows a sleeve) rather
        // than conditionally emitted: the opening now moves by drag as well
        // as by typing, and the drag path refreshes this in place instead
        // of rebuilding the panel — it needs an element to toggle. See
        // _syncSleeveAffordance.
        (isNone ? ''
          : `<span class="bc-seg__hint bc-seg__hint--warn" data-bc-sleeve-floor-hint${sleeveOk ? ' hidden' : ''}>`
            + `Sleeves need an opening of at least ${SLEEVE_MIN_OPENING_MM} mm</span>`) +
        `</div>`
      );
    }

    // Step 3 — which face this loop folds OUTWARD (Jerry, 2026-07-31).
    // Orientation used to be one line-level control on the dimension line;
    // it is per SEWN LOOP now, because that is where the decision
    // physically is — a loop can fold either way, and the two ends of one
    // piece need not agree. The line's drawn colors follow from these
    // (_lineFaces), which is why there is no line-level control left to
    // contradict them.
    //
    // Same segmented control as Loop style — mutually exclusive answers to
    // one question — and the same radios/name/disabled conventions, so the
    // two rows read as one sequence. Labels are the faces' own names
    // ("Purple out" / "Dots Up"), which is also what the cart property and
    // the build sheet's End row carry.
    //
    // Empty string when the webbing has no named faces: nothing to choose,
    // and an inert row explaining that would be worse than no row.
    _orientationStepHtml(role, end, item) {
      const entry = this._entry(role);
      if (!this._flippable(entry)) return '';
      const names = this._faceNames(entry);
      const opts = ['top', 'bottom'].map((side) => {
        const on = (item.faceOut === 'bottom' ? 'bottom' : 'top') === side;
        // Standard/Reversed is unreachable while faceChoice implies two
        // distinct names — kept as a guard against a malformed config.
        const label = names
          ? this._faceOutLabel(side === 'bottom' ? names.bot : names.top)
          : (side === 'bottom' ? 'Reversed' : 'Standard');
        return (
          `<label class="bc-seg__seg-opt${on ? ' is-active' : ''}">` +
          `<input type="radio" name="bc-seg-end${end}-face" value="${side}" ` +
          `data-bc-face${on ? ' checked' : ''}>` +
          `<span>${this._esc(label)}</span></label>`
        );
      });
      return (
        `<div class="bc-seg__step">` +
        `<span class="bc-seg__step-label">Orientation</span>` +
        `<div class="bc-seg__seg" role="group" aria-label="${this._attr(`End ${end} orientation`)}">` +
        `${opts.join('')}</div>` +
        `<span class="bc-seg__hint">Which face of the webbing shows on the outside of this loop</span>` +
        `</div>`
      );
    }

    // One mm dimension row: the exact value as a typed mm input plus its
    // inches readout, over a hint stating the default and the range.
    //
    // It USED to lead with a rail slider (the tension calculator's control
    // pattern). Jerry replaced that 2026-07-31 with DIRECT DRAG on the
    // zoomed end diagram — drag the opening dimension to resize the
    // opening, drag the sleeve end to resize the sleeve — so the "dial it
    // in by feel" half of the row moved onto the thing it was describing,
    // and a second abstract copy of it beside the number would only
    // compete. What stays here is the half a diagram can't do: entering an
    // exact number, and STATING the limits. The drag and the input are two
    // views of ONE value (see _onDimDrag / _commitBoundedInput), and the
    // hint is now the only place the bounds are written down — the drag
    // itself only demonstrates them by refusing to go further.
    //
    // NOT a <label>: the row owns a value, not a single control, and the
    // caption is pointed at with aria-labelledby so the hint can be
    // attached with aria-describedby (which is what carries the range to a
    // screen reader). The bounds hint keeps data-bc-bounds-hint because
    // _flagDimBounds reddens it in step with the input's out-of-bounds
    // ring — selecting it as "the row's last .bc-seg__hint" would break
    // the moment this row grows another hint.
    _dimStepHtml({ key, label, attr, convAttr, value, min, max, defaultMm, disabled }) {
      const hintBits = [];
      if (defaultMm != null) hintBits.push(`Default ${defaultMm} mm`);
      hintBits.push(`${min}–${max} mm`);
      const capId = `bc-seg-${this._uid}-${key}-cap`;
      const hintId = `bc-seg-${this._uid}-${key}-hint`;
      const off = disabled ? ' disabled' : '';
      return (
        `<div class="bc-seg__step bc-seg__step--dim">` +
        `<span class="bc-seg__step-label" id="${this._attr(capId)}">${this._esc(label)}</span>` +
        `<span class="bc-seg__dim">` +
        `<span class="bc-seg__nums">` +
        `<span class="bc-seg__num">` +
        `<input type="text" inputmode="numeric" autocomplete="off" ${attr} value="${this._attr(value)}" ` +
        `data-min="${this._attr(min)}" data-max="${this._attr(max)}" ` +
        `aria-labelledby="${this._attr(capId)}" aria-describedby="${this._attr(hintId)}"${off}>` +
        `<span class="bc-seg__num-unit" aria-hidden="true">mm</span>` +
        `</span>` +
        `<span class="bc-seg__num bc-seg__num--alt">` +
        `<span class="bc-seg__num-val" ${convAttr}>${this._esc(this._mmConvValue(value))}</span>` +
        `<span class="bc-seg__num-unit" aria-hidden="true">in</span>` +
        `</span>` +
        `</span>` +
        `</span>` +
        `<span class="bc-seg__hint" id="${this._attr(hintId)}" data-bc-bounds-hint>${this._esc(hintBits.join(' · '))}</span>` +
        `</div>`
      );
    }

    // The reference block, collapsed: what the loop is sewn WITH rather
    // than what the customer is choosing. Its summary line names the
    // pattern, which is why there is no "Sewing pattern" static row inside
    // for the single-pattern case — the disclosure header already said it.
    // Open state rides this._stitchOpen (see _onClick), so it survives the
    // panel rebuilds that every field commit triggers; it is deliberately
    // NOT in the URL, being a reading preference rather than part of the
    // build.
    _stitchingHtml(pattern, patterns, item) {
      let body = '';
      if (patterns.length > 1) {
        body +=
          `<label class="bc-seg__field"><span>Sewing pattern</span>` +
          `<select data-bc-pattern>${patterns.map((p) => (
            `<option value="${this._attr(p.handle)}"${p.handle === item.pattern ? ' selected' : ''}>` +
            `${this._esc(this._patternLabel(p))}</option>`
          )).join('')}</select></label>`;
      }
      if (pattern && pattern.openingMm == null && pattern.openingText) {
        // No numeric opening to edit — surface the authored text instead
        // of fabricating a spec.
        body +=
          `<div class="bc-seg__field"><span>Opening</span>` +
          `<span class="bc-seg__static">${this._esc(pattern.openingText)}</span></div>`;
      }
      if (pattern) {
        // What the pattern is actually sewn WITH (Jerry, 2026-07-31) — the
        // flat pattern artwork and its sewn-area dims used to live here and
        // are now drawn to scale on the webbing itself, so thread + stitch
        // count, the part of the spec no drawing states, take the space.
        body += this._threadRowHtml('Needle thread', pattern.needleThread, pattern.needleThreadColor);
        body += this._threadRowHtml('Bobbin thread', pattern.bobbinThread, pattern.bobbinThreadColor);
        if (pattern.stitchCount != null) {
          body +=
            `<div class="bc-seg__field"><span>Stitches</span>` +
            `<span class="bc-seg__static">${this._esc(pattern.stitchCount)}</span></div>`;
        }
        // Sewn loop strength — the PATTERN-ON-THIS-WEBBING number from the
        // per-product sewing_spec join (same value as the PDP's Sewing
        // section). Deliberately the ONLY strength shown here, and the hint
        // names the combination it rates: a bare webbing MBS next to it
        // read as if it applied to the loop.
        if (pattern.mbsKn != null || pattern.meanKn != null) {
          let val = pattern.mbsKn != null ? `${pattern.mbsKn} kN MBS` : `mean ${pattern.meanKn} kN`;
          if (pattern.mbsKn != null && pattern.meanKn != null) val += ` (mean ${pattern.meanKn} kN)`;
          body +=
            `<div class="bc-seg__field"><span>Sewn loop strength</span>` +
            `<span class="bc-seg__static">${this._esc(val)}</span>` +
            (pattern.strengthNote ? `<span class="bc-seg__hint">${this._esc(pattern.strengthNote)}</span>` : '') +
            `</div>`;
        }
      }
      if (!body) return '';
      return (
        `<details class="bc-seg__stitch"${this._stitchOpen ? ' open' : ''}>` +
        `<summary class="bc-seg__stitch-summary" data-bc-stitch-toggle>` +
        `<span class="bc-seg__stitch-title">Stitching details</span>` +
        (pattern ? `<span class="bc-seg__stitch-sub">${this._esc(this._patternLabel(pattern))}</span>` : '') +
        `<svg class="bc-seg__stitch-chev" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">` +
        `<path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>` +
        `</svg>` +
        `</summary>` +
        `<div class="bc-seg__stitch-body">${body}</div>` +
        `</details>`
      );
    }

    // The foot of the flow: save THIS design as a loop, and manage the
    // saved design it currently matches. Split off the preset chips (which
    // moved to the top of the panel) because the two are opposite
    // directions of the same feature — chips load a design, this stores
    // one, and storing only makes sense after the rows above have been
    // dialed in. Always rendered: [data-bc-loop-msg] is where _loopMsg
    // reports a preset that couldn't be applied in full, so it has to
    // exist whatever the login state.
    _loopToolsHtml(item) {
      let activeSaved = null;
      this.savedLoops.forEach((loop) => {
        if (!activeSaved && this._itemMatchesLoop(item, loop)) activeSaved = loop;
      });
      // Share/Remove for the design the end currently matches — managing a
      // loop means dialing it in first, which keeps the row uncluttered.
      const manageHtml = activeSaved
        ? `<div class="bc-seg__loop-manage">` +
          `<button type="button" data-bc-loop-share="${this._attr(activeSaved.id)}">Copy share link</button>` +
          `<button type="button" data-bc-loop-delete="${this._attr(activeSaved.id)}">Remove</button>` +
          `</div>`
        : '';

      // Saving works LOGGED OUT (Jerry, 2026-07-31): a guest's designs go
      // to the device (see _localLoopsWrite) and are carried onto the
      // profile at the next login. What used to be a wall in place of the
      // form is now a hint beside it — the account buys cross-device
      // persistence, it is not the price of saving at all.
      let saveHtml = '';
      if (item.treatment !== 'none') {
        saveHtml = this._loopSaveOpen
          ? `<span class="bc-seg__loop-save">` +
            `<input type="text" data-bc-loop-name maxlength="60" placeholder="Name this loop" ` +
            `value="${this._attr(this._loopSaveName || '')}" aria-label="Loop name">` +
            `<button type="button" data-bc-loop-save>Save loop</button>` +
            `<button type="button" data-bc-loop-save-cancel>Cancel</button>` +
            `</span>`
          : `<button type="button" class="bc-seg__loop-save-open" data-bc-loop-save-open>Save current loop&hellip;</button>`;
        if (!this.customer.loggedIn) {
          saveHtml += `<a class="bc-seg__hint bc-seg__loop-login" href="${this._attr(this._loginHref())}">`
            + `Log in to keep these across devices</a>`;
        }
      }

      const msgHtml = `<span class="bc-seg__hint bc-seg__loop-msg" data-bc-loop-msg hidden></span>`;
      // With nothing to offer (a no-loop end with no matching design) the
      // message element goes out on its own — the wrapper would otherwise
      // draw its separator across an empty strip.
      if (!manageHtml && !saveHtml) return msgHtml;
      return `<div class="bc-seg__loop-tools">${manageHtml}${saveHtml}${msgHtml}</div>`;
    }

    // One thread row for the info column: the descriptor the sewing_pattern
    // metaobject carries ("Type Size Color", composed in
    // bc-sewing-patterns-json so this panel and the PDP's written Sewing
    // rows read the same string) with the RESOLVED thread hex as a swatch —
    // the same color the diagram strokes its stitching in, so the words and
    // the drawing name one thread. Empty string when the metaobject has no
    // descriptor at all: every thread field is optional, and a caption over
    // nothing is worse than a missing row.
    _threadRowHtml(label, text, color) {
      const v = String(text == null ? '' : text).trim();
      if (!v) return '';
      return (
        `<div class="bc-seg__field"><span>${this._esc(label)}</span>` +
        `<span class="bc-seg__static bc-seg__thread">` +
        (color ? `<span class="bc-seg__thread-swatch" style="background:${this._attr(color)}"></span>` : '') +
        `${this._esc(v)}</span></div>`
      );
    }

    _patternLabel(p) {
      return p.code ? `${p.code} — ${p.name}` : p.name;
    }

    // ---- loop presets (catalog defaults + customer-saved designs) ---------

    // Customer context from the config JSON (bc-segment-config.liquid):
    // login state, login URL, and the profile's saved loops (customer
    // metafield custom.saved_loops). Validated defensively — the metafield
    // is hand-editable in admin.
    _readCustomer() {
      const c = (this.config && this.config.customer) || {};
      return {
        loggedIn: !!c.loggedIn,
        loginUrl: typeof c.loginUrl === 'string' && c.loginUrl ? c.loginUrl : '/account/login',
        savedLoops: this._validLoops(c.savedLoops),
      };
    }

    _validLoops(list) {
      if (!Array.isArray(list)) return [];
      const out = [];
      list.forEach((l) => {
        if (!l || typeof l !== 'object') return;
        const id = typeof l.id === 'string' ? l.id : '';
        const name = typeof l.name === 'string' ? l.name.trim().slice(0, 60) : '';
        const treatment = typeof l.treatment === 'string' ? l.treatment : '';
        if (!id || !name || !treatment) return;
        const mm = (v) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.round(v) : null);
        out.push({
          id,
          name,
          treatment,
          pattern: typeof l.pattern === 'string' && l.pattern ? l.pattern : null,
          openingMm: mm(l.openingMm),
          // Carried through rather than read: it orders a device list and
          // rides along when local loops are pushed to the profile. The
          // server sends one too, so this is one shape for both stores.
          createdAt: typeof l.createdAt === 'string' ? l.createdAt : null,
          sleeveLengthMm: mm(l.sleeveLengthMm),
          // Absent on every loop saved before orientation existed, which
          // is why nothing downstream requires it (see _applyLoopPreset).
          // The proxy stores the array verbatim, so a new field needs no
          // app change — it just starts appearing.
          faceOut: l.faceOut === 'bottom' ? 'bottom' : (l.faceOut === 'top' ? 'top' : null),
        });
      });
      return out;
    }

    // ---- guest storage ------------------------------------------------------
    //
    // A guest's designs live in localStorage under the SAME record shape the
    // customer metafield holds, which is what makes the merge on login a
    // replay of the ordinary save call rather than a second format
    // (_mergeLocalLoops). Every entry point is failure-tolerant: private
    // mode, a full quota and a disabled-storage browser all read as "no
    // device storage", which the save path reports as the old
    // login-required message.

    _readLocalLoops() {
      try {
        return this._validLoops(JSON.parse(window.localStorage.getItem(LOCAL_LOOPS_KEY) || '[]'));
      } catch (_e) {
        return [];
      }
    }

    _writeLocalLoops(list) {
      try {
        window.localStorage.setItem(LOCAL_LOOPS_KEY, JSON.stringify(list));
        return true;
      } catch (_e) {
        return false;
      }
    }

    _clearLocalLoops() {
      try {
        window.localStorage.removeItem(LOCAL_LOOPS_KEY);
      } catch (_e) { /* nothing to clear if we can't reach storage */ }
    }

    // The device-storage half of _loopsWrite: the proxy's own add/delete
    // semantics, run locally. Upsert is by case-insensitive NAME (saving
    // "Highline" twice edits the design rather than growing a second chip
    // that reads the same), newest first, capped like the server's list.
    _localLoopsWrite(body) {
      const list = this._readLocalLoops();
      let next;
      if (body.op === 'delete') {
        next = list.filter((l) => l.id !== body.id);
      } else {
        const loop = body.loop || {};
        const name = String(loop.name || '').trim().slice(0, 60);
        if (!name) throw new Error('Give the loop a name first.');
        const key = name.toLowerCase();
        const prev = list.find((l) => l.name.toLowerCase() === key);
        const record = {
          id: prev ? prev.id : `loc-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
          name,
          treatment: loop.treatment,
          pattern: loop.pattern || null,
          openingMm: loop.openingMm != null ? loop.openingMm : null,
          sleeveLengthMm: loop.sleeveLengthMm != null ? loop.sleeveLengthMm : null,
          faceOut: loop.faceOut || null,
          createdAt: (prev && prev.createdAt) || new Date().toISOString(),
        };
        next = [record].concat(list.filter((l) => l.name.toLowerCase() !== key)).slice(0, MAX_SAVED_LOOPS);
      }
      if (!this._writeLocalLoops(next)) {
        // No device storage to fall back on, so the account IS the only
        // way to keep this — which is what the original wall said.
        throw new Error('Please log in to save loops.');
      }
      this.savedLoops = this._validLoops(next);
      return this.savedLoops;
    }

    // Where a loop write GOES. Logged in: the app proxy, byte-for-byte the
    // call this always made. Logged out: the device. One seam, so the save
    // / delete / shared-banner callers stay identical for both.
    async _loopsWrite(body) {
      if (this.customer.loggedIn) return this._loopsRequest(body);
      return this._localLoopsWrite(body);
    }

    // Carry a guest's device loops onto the profile they just logged into.
    // Replays the ordinary save call per loop — the proxy upserts by name,
    // so this is idempotent and a half-finished merge simply resumes next
    // load. SEQUENTIAL on purpose: the proxy read-modify-writes one
    // metafield, so parallel adds would race and silently drop loops.
    //
    // Local storage is cleared only after every POST landed; any failure
    // leaves it untouched and says nothing (the customer didn't ask for
    // this — it's a migration, and it retries on the next visit).
    async _mergeLocalLoops() {
      if (!this.customer.loggedIn) return;
      const local = this._readLocalLoops();
      if (!local.length) return;
      try {
        for (const loop of local) {
          await this._loopsRequest({
            op: 'add',
            loop: {
              name: loop.name,
              treatment: loop.treatment,
              pattern: loop.pattern,
              openingMm: loop.openingMm,
              sleeveLengthMm: loop.sleeveLengthMm,
              faceOut: loop.faceOut,
            },
          });
        }
      } catch (_e) {
        return; // leave the device list in place; next load retries
      }
      this._clearLocalLoops();
      // _loopsRequest already replaced savedLoops with the server's
      // authoritative list — show it.
      if (this.state.endView != null) this._renderPicker();
    }

    // The catalog default item for one end, optionally forced into a
    // GIVEN treatment: the default pattern, its default opening, and the
    // sewing type's default sleeve length — exactly the state a fresh
    // visit starts with. `kind` null = the webbing's own default
    // treatment (Sleeved unless the webbing doesn't offer it).
    //
    // The forced kind runs back through _enforceSleeveRule, so asking for
    // sleeved on a pattern whose default opening sits under the floor
    // hands back a NAKED item — which is exactly the signal the chip row
    // reads to disable that chip (see _defaultChips). Nothing here
    // fabricates a legal-looking illegal loop.
    _defaultItemFor(role, end, kind) {
      const key = this._lineWebbing(role);
      const item = this._defaultSewingItem(role, key, end);
      if (kind && item.treatment !== kind) {
        item.treatment = kind;
        this._enforceSleeveRule(item, role, key);
      }
      return item;
    }

    // The same default expressed as a LOOP DESIGN — the shape saved loops
    // use, so _itemMatchesLoop treats catalog and customer designs
    // identically and the chip row needs no second matcher.
    _defaultLoop(role, end, kind) {
      const it = this._defaultItemFor(role, end, kind);
      return {
        treatment: it.treatment,
        pattern: it.pattern,
        openingMm: it.openingMm,
        sleeveLengthMm: it.sleeveLengthMm,
        faceOut: it.faceOut,
      };
    }

    // Reset one end to a catalog default. Also the loop-less auto-fill
    // (_setEndView) with no kind, i.e. the webbing's own default.
    _restoreDefaults(role, end, kind) {
      const line = this._lineState(role);
      const list = (line && line.sewing) || [];
      const idx = list.findIndex((it) => it.location && it.location.mode === 'end' && it.location.end === end);
      if (idx === -1) return;
      list[idx] = this._defaultItemFor(role, end, kind);
    }

    // Apply a saved/shared loop design to one end. Same policy as the e1/e2
    // permalink tokens: treatment only if the webbing offers it, pattern
    // only if offered (missing pattern reported so the caller can say so),
    // opening clamped to the pattern's bounds, then the sleeve rule.
    _applyLoopPreset(role, end, loop) {
      const item = this._endItem(role, end);
      if (!item) return { applied: false, patternMissing: false };
      // Picking a design IS choosing a treatment, so nothing is owed back
      // (see _clearSleeveMemory). _restoreDefaults needs no equivalent —
      // it replaces the item outright, memory and all.
      this._clearSleeveMemory(item);
      const ends = this._sewingData(role).endTreatments || [];
      if (loop.treatment && ends.some((t) => t.kind === loop.treatment)) {
        item.treatment = loop.treatment;
      }
      let patternMissing = false;
      if (loop.pattern) {
        if (this._patternsFor(role, 'end_loop').some((p) => p.handle === loop.pattern)) {
          if (item.pattern !== loop.pattern) {
            item.pattern = loop.pattern;
            const np = this._pattern(item, role);
            item.openingMm = np && np.openingMm != null ? np.openingMm : null;
          }
        } else {
          patternMissing = true;
        }
      }
      const p = this._pattern(item, role);
      if (loop.openingMm != null && p && p.openingMm != null) {
        const minB = p.openingMinMm != null ? p.openingMinMm : 50;
        const maxB = p.openingMaxMm != null ? p.openingMaxMm : 500;
        item.openingMm = Math.min(maxB, Math.max(minB, loop.openingMm));
      }
      // Carried even when the treatment ends up non-sleeved (same as the
      // webbing-switch carry-over) — toggling to Sleeved later resumes it.
      if (loop.sleeveLengthMm != null) item.sleeveLengthMm = loop.sleeveLengthMm;
      // Which face the loop folds outward travels with the design — it is
      // part of what the design IS, so a copied or saved loop reproduces
      // it. Only where the webbing has faces to choose between; on a
      // single-faced one the field stays at its meaningless default rather
      // than recording a choice nobody made. Designs saved before
      // orientation existed carry no faceOut and leave the end's alone.
      if (loop.faceOut && this._flippable(this._entry(role))) {
        item.faceOut = loop.faceOut === 'bottom' ? 'bottom' : 'top';
      }
      this._enforceSleeveRule(item, role);
      return { applied: true, patternMissing };
    }

    // Does the end currently match this design? Only the fields the loop
    // would drive count, and the sleeve length only when it's sleeved.
    _itemMatchesLoop(item, loop) {
      if (!item || !loop) return false;
      if (item.treatment !== loop.treatment) return false;
      if (loop.pattern && item.pattern !== loop.pattern) return false;
      if (loop.openingMm != null && item.openingMm !== loop.openingMm) return false;
      if (loop.treatment === 'sleeved' && loop.sleeveLengthMm != null
        && item.sleeveLengthMm !== loop.sleeveLengthMm) return false;
      // A design that predates orientation matches whatever the end has.
      if (loop.faceOut && (item.faceOut || 'top') !== loop.faceOut) return false;
      return true;
    }

    // A design's identity for the preset row's dedup — the fields
    // _itemMatchesLoop compares, flattened to a key. Sleeve length only
    // counts on a sleeved design, and a missing faceOut reads as 'top'
    // (the meaningless default on single-faced webbings, and what
    // designs saved before orientation existed meant).
    _loopSig(loop) {
      return [
        loop.treatment,
        loop.pattern || '',
        loop.openingMm != null ? loop.openingMm : '',
        loop.treatment === 'sleeved' && loop.sleeveLengthMm != null ? loop.sleeveLengthMm : '',
        loop.faceOut === 'bottom' ? 'bottom' : 'top',
      ].join('|');
    }

    // The catalog defaults as chips — ONE PER LOOP TREATMENT the webbing
    // offers (Jerry, 2026-07-31): "Default Sleeved" and "Default Naked"
    // instead of a single "BC Default", so both starting points are one
    // click away rather than one of them being a default-then-repick
    // dance. A webbing that offers only one loop treatment shows only
    // that chip; 'none' is never a chip — a preset is a loop DESIGN, and
    // "no loop" is the Loop style control's answer, not a design.
    //
    // The webbing's OWN default leads the row: it's the recommendation,
    // it's what an untouched end starts on, and it's what the loop-less
    // auto-fill applies.
    _defaultChips(role, end, item) {
      const offered = (this._sewingData(role).endTreatments || []).filter((t) => t.kind !== 'none');
      if (!offered.length) return [];
      const lead = this._defaultTreatment(role);
      const ordered = offered.slice().sort((a, b) => (b.kind === lead) - (a.kind === lead));
      return ordered.map((t) => {
        const loop = this._defaultLoop(role, end, t.kind);
        // DISABLED, not silently-falls-back (Jerry, 2026-07-31): on a
        // pattern whose default opening sits under the sleeve floor
        // there IS no sleeved default, and a chip that answers "Default
        // Sleeved" with a naked loop lies about what it did. Disabling
        // wears the same is-disabled affordance as the Sleeved SEGMENT,
        // which is off for exactly the same reason at exactly the same
        // moment — the two controls read as one rule, and the red hint
        // under the Loop style control is already on screen explaining it.
        const disabled = loop.treatment !== t.kind;
        return {
          value: `${DEFAULT_PRESET}${t.kind}`,
          label: `Default ${t.label}`,
          active: !disabled && this._itemMatchesLoop(item, loop),
          disabled,
          sig: this._loopSig(loop),
        };
      });
    }


    // One end's current configuration expressed as a LOOP DESIGN — the
    // same shape saved loops and catalog defaults use, so every consumer
    // (_itemMatchesLoop, _applyLoopPreset, the share URL) takes it as-is.
    _loopFromItem(item) {
      if (!item) return null;
      return {
        treatment: item.treatment,
        pattern: item.pattern,
        openingMm: item.openingMm,
        sleeveLengthMm: item.sleeveLengthMm,
        faceOut: item.faceOut,
      };
    }

    // "Copy from this build": one chip per OTHER sewn end of the current
    // build (Jerry, 2026-07-31). With up to six ends across a mainline, a
    // backup and an extender, copying a dialed-in loop onto the next end
    // is the commonest thing anyone wants from this row — and unlike the
    // saved designs it needs no account and no storage at all.
    //
    // Skipped: the end being edited (nothing to copy), and 'none' ends (a
    // preset is a loop DESIGN; "no loop" is the Loop style control's
    // answer). Applying goes through _applyLoopPreset like every other
    // preset, so a chip copied onto a DIFFERENT webbing gets the same
    // offered-treatment / offered-pattern / clamp treatment as a share
    // link — copying between lines is exactly when that matters.
    _buildCopyChips(role, end, item, seen = new Set()) {
      const solo = this._presentRoles().length === 1;
      const chips = [];
      ROLES.forEach((r) => {
        if (!this._lineState(r)) return;
        END_SLOTS.forEach((e) => {
          if (r === role && e === end) return;
          const src = this._endItem(r, e);
          if (!src || src.treatment === 'none') return;
          const loop = this._loopFromItem(src);
          // Two ends holding the identical design need one chip, not two
          // that do the same thing — the first in build order keeps it.
          // The set arrives pre-loaded with the enabled catalog defaults'
          // signatures, so an end that simply sits on a default gets no
          // second chip either — that design is already on the row.
          const sig = this._loopSig(loop);
          if (seen.has(sig)) return;
          seen.add(sig);
          chips.push({
            value: `${COPY_PRESET}${r}:${e}`,
            // On a solo build there is only one line, so naming it would
            // be noise — the same rule _roleTitle follows for the panel.
            label: solo ? `End ${e}` : `${this._roleLabel(r)} · End ${e}`,
            active: this._itemMatchesLoop(item, loop),
            disabled: false,
          });
        });
      });
      return chips;
    }

    _presetChipHtml(value, label, active, disabled) {
      return (
        `<button type="button" class="bc-seg__chip${active ? ' is-active' : ''}${disabled ? ' is-disabled' : ''}" ` +
        `data-bc-loop-preset="${this._attr(value)}" aria-pressed="${active ? 'true' : 'false'}"` +
        `${disabled ? ' disabled' : ''}>` +
        `${this._esc(label)}</button>`
      );
    }

    // Saved-loop ids reach selectors from two places we don't control: the
    // proxy, and the customer metafield an admin can hand-edit. A quote or
    // a bracket in one turns `[data-x="<id>"]` into a SyntaxError that
    // takes out the click handler around it, so every id goes through this.
    // CSS.escape is everywhere that matters (2016+); the fallback quotes
    // the two characters that can break out of a double-quoted attribute
    // selector, which is enough for a value we only ever compare.
    _sel(value) {
      const v = String(value == null ? '' : value);
      if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(v);
      return v.replace(/["\\]/g, '\\$&');
    }

    _loginHref() {
      const base = this.customer.loginUrl;
      const ret = encodeURIComponent(location.pathname + location.search);
      return `${base}${base.indexOf('?') === -1 ? '?' : '&'}return_url=${ret}`;
    }

    _loopMsg(text) {
      const el = this.pickerHost && this.pickerHost.querySelector('[data-bc-loop-msg]');
      if (!el) return;
      el.textContent = text || '';
      el.hidden = !text;
    }

    _onPresetClick(value) {
      const view = this.state.endView;
      if (!view) return;
      const { role, end } = view;
      let missing = false;
      if (value.indexOf(DEFAULT_PRESET) === 0) {
        // "__default:<kind>" — the catalog default in one treatment. A
        // kind this webbing doesn't offer (stale DOM after a webbing
        // switch) falls through to the webbing's own default rather than
        // writing a treatment that isn't on the menu.
        const kind = value.slice(DEFAULT_PRESET.length);
        const offered = (this._sewingData(role).endTreatments || [])
          .some((t) => t.kind === kind && t.kind !== 'none');
        this._restoreDefaults(role, end, offered ? kind : null);
      } else if (value.indexOf(COPY_PRESET) === 0) {
        // "__copy:<role>:<end>" — read the SOURCE END LIVE rather than off
        // anything the chip captured: the row is re-rendered with the
        // panel, but a stale chip (a design changed in another editor
        // since this one opened) must copy what that end holds NOW.
        const bits = value.slice(COPY_PRESET.length).split(':');
        const src = this._endItem(ROLES.includes(bits[0]) ? bits[0] : 'main', Number(bits[1]));
        if (!src || src.treatment === 'none') return;
        missing = this._applyLoopPreset(role, end, this._loopFromItem(src)).patternMissing;
      } else {
        const loop = this.savedLoops.find((l) => l.id === value);
        if (!loop) return;
        missing = this._applyLoopPreset(role, end, loop).patternMissing;
      }
      this._renderPicker(`[data-bc-loop-preset="${this._sel(value)}"]`);
      this._afterChange();
      if (missing) this._loopMsg('That design’s sewing pattern isn’t offered on this webbing — kept the current pattern.');
    }

    // One POST to the app proxy; the response's authoritative list replaces
    // savedLoops. 401 = not logged in (session expired mid-visit).
    async _loopsRequest(body) {
      let res;
      try {
        res = await fetch(this.loopsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
      } catch (_e) {
        throw new Error('Network error — please try again.');
      }
      if (res.status === 401) throw new Error('Please log in to save loops.');
      let data = null;
      try { data = await res.json(); } catch (_e) { /* non-JSON error body */ }
      if (!res.ok || !data || !Array.isArray(data.loops)) {
        throw new Error('Could not update your loops right now. Please try again.');
      }
      this.savedLoops = this._validLoops(data.loops);
      return this.savedLoops;
    }

    async _saveCurrentLoop() {
      const view = this.state.endView;
      const item = view ? this._endItem(view.role, view.end) : null;
      if (!item || item.treatment === 'none') return;
      const input = this.pickerHost && this.pickerHost.querySelector('[data-bc-loop-name]');
      const name = ((input && input.value) || '').trim();
      if (!name) {
        this._loopMsg('Give the loop a name first.');
        if (input) input.focus();
        return;
      }
      const btn = this.pickerHost && this.pickerHost.querySelector('[data-bc-loop-save]');
      if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
      try {
        await this._loopsWrite({
          op: 'add',
          loop: {
            name,
            treatment: item.treatment,
            pattern: item.pattern,
            openingMm: item.openingMm,
            sleeveLengthMm: item.sleeveLengthMm,
            // Only when the webbing HAS a face choice — otherwise the
            // design would carry an orientation nobody picked into every
            // webbing it is later applied to.
            faceOut: this._flippable(this._entry(view.role)) ? item.faceOut : null,
          },
        });
        this._loopSaveOpen = false;
        this._loopSaveName = '';
        this._renderPicker();
      } catch (err) {
        if (btn) { btn.disabled = false; btn.textContent = 'Save'; }
        this._loopMsg(err.message || 'Could not save right now.');
      }
    }

    async _deleteLoop(id) {
      const btn = this.pickerHost && this.pickerHost.querySelector(`[data-bc-loop-delete="${this._sel(id)}"]`);
      if (btn) { btn.disabled = true; btn.textContent = 'Removing…'; }
      try {
        await this._loopsWrite({ op: 'delete', id });
        this._renderPicker();
      } catch (err) {
        if (btn) { btn.disabled = false; btn.textContent = 'Remove'; }
        this._loopMsg(err.message || 'Could not remove right now.');
      }
    }

    // A loop share is a URL, not a server object: the design rides the
    // same token grammar as e1/e2 and imports on load (see _applyUrl).
    _loopShareUrl(loop) {
      const params = new URLSearchParams();
      const current = new URLSearchParams(location.search);
      if (current.has('view')) params.set('view', current.get('view'));
      const bits = [loop.treatment, loop.pattern || ''];
      bits.push(loop.openingMm != null ? String(loop.openingMm) : '');
      if (loop.treatment === 'sleeved') bits.push(loop.sleeveLengthMm != null ? String(loop.sleeveLengthMm) : '');
      if (loop.faceOut === 'bottom') bits.push('fb');
      params.set('loop', bits.join('.'));
      params.set('loopname', loop.name);
      return `${location.origin}${location.pathname}?${params.toString()}`;
    }

    _shareLoop(id) {
      const loop = this.savedLoops.find((l) => l.id === id);
      if (!loop) return;
      const url = this._loopShareUrl(loop);
      const sel = `[data-bc-loop-share="${this._sel(id)}"]`;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(
          () => this._flash(sel, 'Link copied!'),
          () => this._flash(sel, url)
        );
      } else {
        this._flash(sel, url);
      }
    }

    // The shared-loop banner under the heading: names the imported design
    // (already applied to both ends by _applyUrl) and offers to save it —
    // login-gated like every save.
    _renderLoopBanner() {
      let el = this.querySelector('[data-bc-loop-banner]');
      if (!this._sharedLoop) {
        if (el) el.remove();
        return;
      }
      if (!el) {
        const head = this.querySelector('.bc-segment__head');
        if (!head) return;
        el = document.createElement('div');
        el.className = 'bc-seg__loop-banner';
        el.setAttribute('data-bc-loop-banner', '');
        head.appendChild(el);
      }
      // Same rule as the panel's save (see _loopToolsHtml): a guest saves
      // to the device, so the offer stands logged out and only the WHERE
      // differs — which the done state names, since "your loops" would
      // overstate what a device save bought.
      let action;
      if (this._sharedSaved) {
        action = this.customer.loggedIn
          ? `<span class="bc-seg__loop-banner-done">Saved to your loops.</span>`
          : `<span class="bc-seg__loop-banner-done">Saved on this device.</span>`;
      } else {
        action = `<button type="button" class="bc-seg__loop-banner-save" data-bc-loop-banner-save>Save to my loops</button>`;
        if (!this.customer.loggedIn) {
          action += ` <a class="bc-seg__loop-banner-login" href="${this._attr(this._loginHref())}">`
            + `Log in to keep it across devices</a>`;
        }
      }
      el.innerHTML =
        `<span class="bc-seg__loop-banner-text">Shared loop design ` +
        `<strong>${this._esc(this._sharedLoop.name)}</strong> — applied to both ends.</span> ` +
        action +
        `<span class="bc-seg__hint" data-bc-loop-banner-msg hidden></span>` +
        `<button type="button" class="bc-seg__loop-banner-dismiss" data-bc-loop-banner-dismiss aria-label="Dismiss">&times;</button>`;
    }

    async _saveSharedLoop() {
      if (!this._sharedLoop) return;
      const btn = this.querySelector('[data-bc-loop-banner-save]');
      if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
      try {
        await this._loopsWrite({ op: 'add', loop: this._sharedLoop });
        this._sharedSaved = true;
        this._sharedLoopToken = null; // saved — a refresh needn't re-offer
        this._renderLoopBanner();
        this._writeUrl();
        if (this.state.endView != null) this._renderPicker();
      } catch (err) {
        if (btn) { btn.disabled = false; btn.textContent = 'Save to my loops'; }
        const msg = this.querySelector('[data-bc-loop-banner-msg]');
        if (msg) { msg.textContent = err.message || 'Could not save.'; msg.hidden = false; }
      }
    }

    _dismissSharedLoop() {
      this._sharedLoop = null;
      this._sharedLoopToken = null;
      this._sharedSaved = false;
      this._renderLoopBanner();
      this._writeUrl();
    }

    // ---- pricing / totals -------------------------------------------------

    // One role's cart line, or null when it can't resolve (missing variant /
    // zero custom length). qty is the SYSTEM count, so every line of a build
    // scales by the same number.
    _line(role) {
      const line = this._lineState(role);
      const entry = this._entry(role);
      if (!line || !entry) return null;
      const qty = Math.max(1, parseInt(this.state.qty, 10) || 1);

      if (line.lengthMode === 'custom') {
        if (!entry.custom) return null;
        const length = Number(line.length) || 0;
        if (length <= 0) return null;
        // Same rule as bc-custom-length/bc-price-total/bc-cart-length:
        // round the TOTAL up, never per piece.
        const cartQuantity = Math.max(1, Math.ceil(length * qty));
        return {
          variantId: entry.custom.id,
          quantity: cartQuantity,
          available: entry.custom.available !== false,
          totalCents: (entry.custom.price || 0) * cartQuantity,
          custom: true,
          length,
          unit: entry.custom.unit || 'm',
          qty,
          // How much of this variant the cart may hold at all, when Shopify
          // tracks it and denies overselling — UNDEFINED means no ceiling
          // (snippets/bc-kit-variant-json.liquid emits the key only when
          // there is one). Read by the add path's stock gate, never by the
          // price/weight arithmetic. On a custom line the units are METERS
          // or FEET, not pieces.
          maxQty: entry.custom.maxQty,
          // UNIT price and compare-at, both in cents, for the `Sale` property
          // (_saleProperty). Deliberately not totalCents: the percent off is a
          // ratio, and on a custom line the "unit" is one meter/foot, so the
          // ratio is the same either way — but reading the unit pair keeps it
          // independent of how quantity was rounded. compareAtCents is 0 when
          // the variant is not discounted (bc-kit-variant-json.liquid omits the
          // key entirely in that case).
          priceCents: entry.custom.price || 0,
          compareAtCents: entry.custom.compareAtPrice || 0,
        };
      }

      const v = entry.variants && entry.variants[line.fixedValue];
      if (!v) return null;
      return {
        variantId: v.id,
        quantity: qty,
        available: v.available !== false,
        totalCents: (v.price || 0) * qty,
        custom: false,
        length: null,
        unit: '',
        qty,
        maxQty: v.maxQty, // see above — pieces here, one unit per piece
        priceCents: v.price || 0, // see above
        compareAtCents: v.compareAtPrice || 0,
      };
    }

    // The roles this build has a line for, in draw / permalink / cart order.
    _presentRoles() {
      return ROLES.filter((role) => this._lineState(role));
    }

    // Every present line resolved, or NULL when any one of them can't
    // resolve (blank custom length, missing variant). All-or-nothing on
    // purpose: a build with an unpriceable piece has no total, and showing
    // the resolvable ones' sum would understate what the cart is about to
    // charge. The add path refuses the same builds (_validateLines).
    // The length check is not redundant: [].every() is TRUE, so a build
    // with no lines at all would otherwise come back as a truthy empty set
    // and callers reading set[0] would throw. Unreachable while the
    // mainline is always present — which is exactly when it would bite.
    _lineSet() {
      const set = this._presentRoles().map((role) => {
        const line = this._line(role);
        return line ? { role, ...line } : null;
      });
      return set.length && set.every(Boolean) ? set : null;
    }

    recompute() {
      // Every line of the build, so the figure under the sheet is what the
      // cart will charge for all of it — one system quantity across the
      // lot (see _line).
      const set = this._lineSet();
      const priceEl = this.querySelector('[data-bc-total-price]');
      if (priceEl) {
        const qty = set ? set[0].qty : 0;
        const totalCents = set ? set.reduce((sum, l) => sum + l.totalCents, 0) : 0;
        if (!set) {
          priceEl.textContent = '—';
        } else if (qty > 1) {
          // unit absorbs the ceil slack so unit × qty = total stays literally
          // true — same trade-off as assets/bc-price-total.js.
          const unitCents = Math.round(totalCents / qty);
          priceEl.innerHTML =
            `${this._esc(this._money(unitCents))}` +
            `<span class="bc-seg__price-math"> × ${qty} = ${this._esc(this._money(totalCents))}</span>`;
        } else {
          priceEl.textContent = this._money(totalCents);
        }
      }
      this._renderSummary();
      this._renderWebbingSpecs();
      this._renderWebbingButtonSwatch();
      this._syncStageActions();
      // The open pill's value and the backup's recommendation — refreshed
      // on every change like the rest of the derived UI, and IN PLACE,
      // because the overlay must not rebuild mid-keystroke. A no-op with
      // no pill open, which is most of the time.
      this._syncOverlay();
      this.dispatchEvent(new CustomEvent('bc:segment:state', { bubbles: true, detail: { state: this.state } }));
    }

    // Each webbing-picker button's swatch — the SAME .bc-seg__tile-swatch
    // dot the picker tiles and orientation pills use, filled with that
    // LINE's own line color so the button states what you have, not just
    // what it does. (A neutral shape here read as an unchecked checkbox; a
    // colored dot reads as a swatch, which is what it is.) With one button
    // per line the swatches are what make three near-identical pills
    // scannable, so each is keyed to its role.
    //
    // KEYED, like _renderWebbingSpecs: recompute() runs per keystroke and a
    // line's colour moves only when its webbing (or its orientation) does,
    // so the common pass is three map lookups and no DOM at all. The
    // querySelector is inside the guard for the same reason — the cheapest
    // DOM read is the one not taken.
    _renderWebbingButtonSwatch() {
      const seen = this._swatchColors || (this._swatchColors = {});
      ROLES.forEach((role) => {
        const entry = this._lineState(role) ? this._effectiveEntry(role) : null;
        const color = (entry && entry.scene && entry.scene.color) || '';
        if (seen[role] === color) return;
        const dot = this.querySelector(`[data-bc-webbing-swatch="${role}"]`);
        // No dot yet (a render before the section markup settled) leaves the
        // key UNSET, so the next pass tries again rather than banking a
        // colour it never applied.
        if (!dot) { delete seen[role]; return; }
        seen[role] = color;
        dot.style.background = color;
        dot.hidden = !color;
      });
    }

    // The stage row: one "Change <line>" button per present line, plus the
    // "+ Add backup line" entry point that sits with the diagram the backup
    // will appear IN (the build sheet carries the other one, at the foot of
    // the rows it would add to; the add button is scoped by class here so
    // the summary's identical data-bc-add-line is not toggled from here).
    //
    // In-place text/hidden sync rather than a rebuild: this runs on every
    // recompute(), and the buttons are focus targets the modal hands focus
    // back to (see _closeWebbingPanel) — replacing them mid-flow would drop
    // that handoff on the floor.
    //
    // The mainline's label is the only one that MOVES: "Change webbing" on
    // a solo build, "Change Mainline" once there is a second line to tell
    // it apart from. Same don't-name-the-only-line rule as _roleTitle and
    // the copy-from-this-build chips.
    //
    // Also the SECOND face of the remove two-step: the backup's and the
    // extender's × mirror the state _askRemoveLine set, so pressing either
    // control shows the confirm in both places and any path that clears
    // `_removeConfirm` clears both. Hence the calls added to
    // _askRemoveLine / _cancelRemoveLine / _activatePart — those re-render
    // the sheet WITHOUT a recompute, which is the only thing that used to
    // reach this.
    //
    // KEYED on everything it can write (which lines are present, whether the
    // mainline has to name itself, and which line — if any — is asking to be
    // removed): recompute() runs per keystroke and a length edit moves none
    // of it, so the common pass is one string compare instead of a dozen
    // querySelectors. The row is STATIC section markup — _renderSummary's
    // rebuild does not reach it — so nothing can silently undo what a
    // skipped pass would have written. The one thing that can is the row
    // itself not existing yet, which leaves the key unset.
    _syncStageActions() {
      const row = this.querySelector('.bc-segment__stage-actions');
      if (!row) { this._stageActionsSig = null; return; }
      const present = ROLES.map((role) => (this._lineState(role) ? '1' : '0'));
      const sig = `${present.join('')}|${this._removeConfirm || ''}`;
      if (this._stageActionsSig === sig) return;
      this._stageActionsSig = sig;
      const multi = ROLES.filter((role) => this._lineState(role)).length > 1;
      ROLES.forEach((role) => {
        const btn = row.querySelector(`[data-bc-part="${this._partId(role, 'webbing')}"]`);
        if (!btn) return;
        const present = !!this._lineState(role);
        // Hidden at the WRAPPER, not the button: the × and its confirm ride
        // with it, and hiding only the button would leave a bare × standing
        // for a line that isn't in the build.
        const wrap = btn.closest('[data-bc-stage-line]') || btn;
        wrap.hidden = !present;
        const label = btn.querySelector('[data-bc-pick-label]');
        const text = (role === 'main' && !multi) ? 'Change webbing' : `Change ${this._roleLabel(role)}`;
        if (label && label.textContent !== text) label.textContent = text;
        const asking = present && this._removeConfirm === role;
        const x = wrap.querySelector('[data-bc-remove-line]');
        const confirm = wrap.querySelector('[data-bc-stage-confirm]');
        if (x) x.hidden = asking;
        if (!confirm) return;
        confirm.hidden = !asking;
        // The compact pair has room for "Remove?" and nothing else, so the
        // extender warning lives in the accessible name (see _removeAsk).
        // Written only while asking — this runs on every keystroke.
        const ask = asking && confirm.querySelector('[data-bc-remove-confirm]');
        if (ask) ask.setAttribute('aria-label', this._removeAsk(role));
      });
      const add = row.querySelector('[data-bc-add-line="backup"]');
      if (add) add.hidden = !!this._lineState('backup');
      // The extender's stage entry point shows while there is a backup to
      // extend and no extender yet — the same window the build sheet's
      // offer has. Covered by this function's signature: both terms are
      // line presence.
      const addExt = row.querySelector('[data-bc-add-line="extender"]');
      if (addExt) addExt.hidden = !this._lineState('backup') || !!this._lineState('extender');
    }

    // The chosen webbing's quick-spec line, ONE PER LINE: a non-interactive
    // line INSIDE the summary list, right below that role's Webbing row —
    // the SAME server-rendered bc-card-specs markup the picker tiles carry
    // (entry.specsHtml, per product), so it can never disagree with the
    // collection cards. Every present line gets one, since every line is its
    // own webbing choice made on its own specs; in a grouped sheet each
    // lands inside its role's box, because it is inserted after that box's
    // own Webbing row (data-bc-part is role-prefixed for everything but the
    // mainline — see _partId).
    //
    // Runs after _renderSummary each recompute(): the summary's full-rebuild
    // path wipes foreign children, so each line re-inserts itself when
    // missing (sitting between the buttons doesn't disturb the differential
    // update, which matches on .bc-seg__sum-row only, and a group wrapper
    // doesn't renumber that walk either). No row means no host to hang off
    // and nothing stale to clear — the rebuild that dropped the row took its
    // host with it. The innerHTML write is keyed by product handle PER ROLE
    // — recompute() runs per keystroke, and specsHtml moves only when one
    // line switches webbing.
    _renderWebbingSpecs() {
      const list = this.querySelector('[data-bc-summary]');
      if (!list) return;
      const keys = this._specsHandles;
      ROLES.forEach((role) => {
        let host = list.querySelector(`[data-bc-webbing-specs="${role}"]`);
        if (!host) {
          const webbingRow = list.querySelector(`[data-bc-part="${this._partId(role, 'webbing')}"]`);
          if (!webbingRow) {
            delete keys[role];
            return;
          }
          host = document.createElement('div');
          host.className = 'bc-segment__specs';
          host.setAttribute('data-bc-webbing-specs', role);
          host.hidden = true;
          webbingRow.after(host);
          delete keys[role]; // fresh node — force the fill below
        }
        // The row's own entry (_summaryRowsFor), not _effectiveEntry: the
        // specs describe the PRODUCT, which a line hanging upside down does
        // not change. A line with no entry shows no specs, same as the row
        // above it shows "—".
        const entry = this._entry(role);
        const key = (entry && entry.productHandle) || '';
        if (keys[role] === key) return;
        keys[role] = key;
        const html = (entry && entry.specsHtml) || '';
        host.innerHTML = html; // trusted Liquid output, same as the tiles
        host.hidden = !html;
      });
    }

    _money(cents) {
      return this.money.format((cents || 0) / 100);
    }

    // ---- summary (the build sheet) ----------------------------------------

    _renderSummary() {
      const host = this.querySelector('[data-bc-summary]');
      if (!host) return;
      const blocks = this._summaryBlocks();
      const rows = blocks.filter((b) => b.type === 'row').map((b) => b.row);

      // Differential update: recompute() runs on every state change, and
      // the old whole-list innerHTML rebuild paid a full synchronous style
      // pass per call (element removal + re-insert invalidation on this
      // heavy page) — text swaps on the existing buttons don't. Also keeps
      // focus alive when a summary row is activated by keyboard (and when
      // a disclosure chevron is toggled). Falls back to a full build when
      // the STRUCTURE moves (first render, a row gaining or losing its
      // disclosure, a line being added or removed, the remove-confirm
      // opening) — everything the signature covers. Values that move
      // within a fixed structure — the row values, the recommendation
      // note's text and its warning state — are synced in place instead,
      // which is what keeps a per-keystroke length edit off the rebuild
      // path. The rows-length re-check is the safety net for a DOM that
      // drifted from the signature some other way.
      const sig = this._summarySig(blocks);
      const btns = host.querySelectorAll('.bc-seg__sum-row');
      if (sig === this._summaryStructSig && btns.length === rows.length) {
        rows.forEach((r, i) => {
          const btn = btns[i];
          const val = btn.querySelector('.bc-seg__sum-val');
          if (val && val.textContent !== r.value) val.textContent = r.value;
          if (r.value === '—') btn.setAttribute('data-empty', '1');
          else btn.removeAttribute('data-empty');
          this._syncSumDetail(r, host);
        });
        this._syncSumNotes(blocks, host);
        return;
      }
      host.innerHTML = this._summaryGroupsHtml(blocks);
      this._summaryStructSig = sig;
      // The rebuild just wiped every quick-spec line with the rows it hung
      // them on, so it puts them back itself rather than leaving that to
      // recompute()'s call: the remove-confirm paths (_askRemoveLine,
      // _cancelRemoveLine, the clear in _activatePart) re-render the sheet
      // WITHOUT a recompute, and the specs would sit missing until the next
      // unrelated change.
      this._renderWebbingSpecs();
    }

    // Each line's blocks in a BOX of their own (Jerry, staging 2026-07-31:
    // the sections "need more visual separation" — flush headers in one
    // continuous list read as a single sheet with captions in it). Only
    // ever wraps blocks that carry a `section`, so a SOLO mainline still
    // renders the flat, chrome-free list it always did — the separation
    // arrives with the headers, and for the same reason.
    //
    // Consecutive-run grouping rather than a group-by: the block order IS
    // the reading order (contract C7), and a run keeps the "+ Add
    // extender" affordance inside the backup's box where its reason is.
    // The differential path is unaffected — it walks `.bc-seg__sum-row`
    // across the whole host, which a wrapper doesn't renumber.
    _summaryGroupsHtml(blocks) {
      const out = [];
      let i = 0;
      while (i < blocks.length) {
        const section = blocks[i].section;
        if (!section) {
          out.push(this._summaryBlockHtml(blocks[i]));
          i += 1;
          continue;
        }
        const parts = [];
        let confirm = false;
        while (i < blocks.length && blocks[i].section === section) {
          if (blocks[i].type === 'head' && blocks[i].confirm) confirm = true;
          parts.push(this._summaryBlockHtml(blocks[i]));
          i += 1;
        }
        // The pending-remove state rides the BOX, not just its header, so
        // what is about to be discarded is outlined as one thing.
        out.push(
          `<section class="bc-seg__sum-group${confirm ? ' bc-seg__sum-group--confirm' : ''}" ` +
          `data-bc-sum-group="${this._attr(section)}">${parts.join('')}</section>`
        );
      }
      return out.join('');
    }

    // The build sheet as an ordered list of blocks — one per role's header
    // and rows, plus the recommendation note and the add/remove
    // affordances (contract C7). A SOLO mainline renders exactly what it
    // always did: no headers (there is nothing to tell apart), its five
    // rows, and one "+ Add backup line" affordance at the foot.
    _summaryBlocks() {
      const multi = !!this._lineState('backup');
      const blocks = [];
      // `section` names the role whose BOX a block is drawn in, which is
      // not always its own role: the "+ Add extender" affordance belongs
      // under the backup, beside the shortfall that motivates it. Unset on
      // a solo build, which is what keeps that sheet's markup flat and
      // chrome-free — see _summaryGroupsHtml.
      ROLES.forEach((role) => {
        if (!this._lineState(role)) return;
        const section = multi ? role : null;
        if (multi) {
          blocks.push({
            type: 'head',
            role,
            section,
            // The mainline's header can't be removed — it IS the build.
            removable: role !== 'main',
            confirm: this._removeConfirm === role,
          });
        }
        this._summaryRowsFor(role).forEach((row) => blocks.push({ type: 'row', role, section, row }));
        if (role === 'backup') {
          const note = this._recommendationNote();
          if (note) blocks.push({ type: 'note', role, section, text: note.text, warn: note.warn });
          // The extender is offered where its reason is stated: right
          // under the shortfall the note just described. Offered even
          // WITHOUT a shortfall (the copy inside the backup's own length
          // pill gates itself on one, and is only on screen while that
          // pill is) — "short backup + extender" is a mode the customer
          // may want outright, and a webbing carrying no backup percentage
          // produces no note and no shortfall, which would otherwise leave
          // no way in at all. This one is ALWAYS on screen, which is what
          // keeps the advisory discoverable with every pill closed.
          if (!this._lineState('extender')) blocks.push({ type: 'add', role: 'extender', section });
        }
      });
      if (!multi) blocks.push({ type: 'add', role: 'backup' });
      return blocks;
    }

    // Everything that decides the MARKUP rather than the text in it. Note
    // text and warning state are deliberately absent: they move with the
    // mainline's length, and a rebuild per keystroke is exactly what the
    // differential path exists to avoid.
    _summarySig(blocks) {
      return blocks.map((b) => {
        if (b.type === 'row') return `r:${b.row.part}:${b.row.detail && b.row.detail.length ? 1 : 0}`;
        if (b.type === 'head') return `h:${b.role}:${b.removable ? 1 : 0}:${b.confirm ? 1 : 0}`;
        return `${b.type[0]}:${b.role}`;
      }).join('|');
    }

    _summaryBlockHtml(b) {
      if (b.type === 'row') return this._summaryRow(b.row);
      if (b.type === 'head') return this._summaryHeadHtml(b);
      if (b.type === 'note') return this._summaryNoteHtml(b);
      return this._summaryAddHtml(b.role);
    }

    // A line's section header. Its Remove control is a two-step: the first
    // click swaps the header for the confirm (see _askRemoveLine), so no
    // browser dialog is involved and nothing is discarded on one stray
    // click. Removing the backup takes the extender with it, which the
    // confirm says out loud.
    _summaryHeadHtml(b) {
      const label = this._roleLabel(b.role);
      if (!b.removable) {
        return `<p class="bc-seg__sum-sec"><span class="bc-seg__sum-sec-name">${this._esc(label)}</span></p>`;
      }
      if (b.confirm) {
        return (
          `<p class="bc-seg__sum-sec bc-seg__sum-sec--confirm">` +
          `<span class="bc-seg__sum-sec-name">${this._esc(this._removeAsk(b.role))}</span>` +
          `<button type="button" class="bc-seg__sum-sec-confirm" data-bc-remove-confirm="${this._attr(b.role)}">Remove</button>` +
          `<button type="button" class="bc-seg__sum-sec-cancel" data-bc-remove-cancel>Keep</button>` +
          `</p>`
        );
      }
      return (
        `<p class="bc-seg__sum-sec">` +
        `<span class="bc-seg__sum-sec-name">${this._esc(label)}</span>` +
        `<button type="button" class="bc-seg__sum-sec-remove" data-bc-remove-line="${this._attr(b.role)}" ` +
        `aria-label="${this._attr(`Remove ${label.toLowerCase()} line`)}">Remove</button>` +
        `</p>`
      );
    }

    // Advisory, never blocking (plan assumption 1): the recommendation is
    // stated, the shortfall is flagged, and the build adds to the cart
    // either way.
    _summaryNoteHtml(b) {
      return (
        `<p class="bc-seg__sum-note${b.warn ? ' bc-seg__sum-note--warn' : ''}" ` +
        `data-bc-sum-note="${this._attr(b.role)}">${this._esc(b.text)}</p>`
      );
    }

    _summaryAddHtml(role) {
      const label = role === 'backup' ? '+ Add backup line' : '+ Add extender';
      return (
        `<button type="button" class="bc-seg__sum-add" data-bc-add-line="${this._attr(role)}">` +
        `${this._esc(label)}</button>`
      );
    }

    _syncSumNotes(blocks, host) {
      blocks.forEach((b) => {
        if (b.type !== 'note') return;
        const el = host.querySelector(`[data-bc-sum-note="${b.role}"]`);
        if (!el) return;
        if (el.textContent !== b.text) el.textContent = b.text;
        el.classList.toggle('bc-seg__sum-note--warn', !!b.warn);
      });
    }

    // One line's build-sheet rows. `part` is the DOM id the row activates
    // (bare for the mainline, see _partId); `role` + `key` are how the
    // disclosure reads and writes that line's own endDetails.
    _summaryRowsFor(role) {
      const line = this._lineState(role);
      if (!line) return [];
      const entry = this._entry(role);
      const row = (part, name, value, extra) => Object.assign(
        { part: this._partId(role, part), role, key: part, name, value }, extra || {}
      );

      const lengthText = this._lengthTextFor(role) || '—';

      // No line-level "Orientation" row any more: orientation is per SEWN
      // LOOP (see _lineFaces), so it belongs with the end that owns it —
      // it moved into the End N disclosure (_sewingDetailRows).
      const rows = [row('webbing', 'Webbing', entry ? entry.label : '—')];
      rows.push(row('length', 'Length', lengthText));
      END_SLOTS.forEach((end) => {
        // The End rows read PROTECTION at rest and keep their dimensions
        // behind a disclosure (see _sewingDetailRows).
        rows.push(row(`end${end}`, `End ${end}`, this._sewingSummaryText(role, end), {
          detail: this._sewingDetailRows(role, end),
        }));
      });
      return rows;
    }

    // Is this row's disclosure open? The flag lives on the row's own LINE,
    // keyed by the bare part id — one line's End 1 disclosure says nothing
    // about another's.
    _sumDetailOpen(row) {
      const line = this._lineState(row.role);
      return !!(line && (line.endDetails || {})[row.key]);
    }

    // `host` is passed by _renderSummary (which already has it) so the
    // per-keystroke path doesn't re-find the list for every row.
    _sumDetailEl(part, host) {
      const list = host || this.querySelector('[data-bc-summary]');
      return list ? list.querySelector(`[data-bc-sum-detail="${part}"]`) : null;
    }

    // Differential half of the disclosure: refresh the disclosed rows and
    // mirror the open/closed state onto the DOM. The content write is
    // keyed off a signature attribute — recompute() runs per keystroke and
    // these values only move when an end is edited.
    _syncSumDetail(row, host) {
      if (!row.detail || !row.detail.length) return;
      const el = this._sumDetailEl(row.part, host);
      if (!el) return;
      const sig = this._sumDetailSig(row.detail);
      if (el.getAttribute('data-sig') !== sig) {
        el.setAttribute('data-sig', sig);
        el.innerHTML = this._sumDetailRowsHtml(row.detail);
      }
      const open = this._sumDetailOpen(row);
      el.hidden = !open;
      const btn = el.parentElement && el.parentElement.querySelector('[data-bc-sum-toggle]');
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    _toggleSumDetail(id) {
      const { role, part } = this._parsePart(id);
      const line = this._lineState(role);
      if (!line) return;
      const map = line.endDetails || (line.endDetails = {});
      map[part] = !map[part];
      // _renderSummary's differential path applies it in place, so the
      // chevron that was just clicked keeps focus.
      this._renderSummary();
    }

    // Panel footer: the explicit way OUT of a step. Every edit in these
    // panels already applies live (the diagram updates as you type), so
    // "Save" persists nothing — it's the "I'm done here" the panels were
    // missing, when the × was the only exit. Enter does the same thing.
    // It carried a second, destructive action until 2026-07-31 (the end
    // editor's "No Loop"); that moved INTO the flow, as the third segment
    // of the Loop style control, where the decision it answers is asked.
    _panelFootHtml() {
      return (
        `<div class="bc-seg__panel-foot">` +
        `<button type="button" class="bc-seg__panel-save" data-bc-panel-save>Save</button>` +
        `</div>`
      );
    }

    // Save = commit whatever field has focus, then close the panel.
    _savePanel() {
      const view = this.state.endView;
      const panel = this.state.panel;
      if (!view && !panel) return;
      // The webbing modal's own exit: nothing in it holds an uncommitted
      // value (picking a tile applies immediately), so Save is purely
      // "I'm done here" and _closeWebbingPanel owns the whole close.
      if (!view && panel) {
        this._closeWebbingPanel();
        return;
      }
      const part = this._partId(view.role, `end${view.end}`);
      // Commit the focused field BEFORE the panel is torn down, or a
      // just-typed value dies with the field it was typed into — but BLUR
      // FIRST and let the explicit commit cover only what the blur cannot
      // (Jerry, 2026-08-01: "can pressing enter on the loop editor save
      // the changes?" — it couldn't, and this is why).
      //
      // Committing first THREW. _commitBoundedInput's opening branch
      // rebuilds the panel, and writing host.innerHTML while the dirtied
      // field still HAS focus removes a focused, user-edited input — which
      // makes Chrome dispatch its `change` synchronously, in the middle of
      // that assignment. The reentrant _onChange committed the same field
      // again (closest() still resolves the by-then detached
      // [data-bc-sewing-item] wrapper, and state.endView is still set) and
      // rebuilt the panel a second time; when the outer assignment resumed,
      // the nodes it was still removing were no longer children of the host
      // and Blink threw NotFoundError ("The node to be removed is no longer
      // a child of this node. Perhaps it was moved in a 'blur' event
      // handler?"). That exception unwound out of _savePanel before
      // _zoomOut() ever ran — so Enter applied the number (through the
      // reentrant commit) and then left the editor open, i.e. did visibly
      // nothing. The Save BUTTON never hit it: a pointer press blurs the
      // field on the way in, so its `change` had already been dispatched
      // and the field was clean by the time _savePanel ran.
      //
      // Blurring first dispatches that same `change` one level deep, where
      // the rebuild it triggers is the only one in flight. What is left for
      // the explicit commit is exactly the case blur CANNOT cover: a field
      // whose value was set some way other than by the user carries no
      // dirty flag, so fires no change, so nothing else would commit it.
      // That case never rebuilt the panel, which is why the field is still
      // connected when the check below runs — and it is no longer focused,
      // so the rebuild it does trigger can't fire a change either.
      const active = document.activeElement;
      if (active && this.pickerHost && this.pickerHost.contains(active)) {
        const bounded = !!(active.matches
          && active.matches('[data-bc-opening],[data-bc-sleeve-length]'));
        if (typeof active.blur === 'function') active.blur();
        if (bounded && active.isConnected) {
          this._commitBoundedInput(active, { target: active });
        }
      }
      this._zoomOut();
      // Same handoff as Escape — the focused control is gone, so land on
      // the summary row that reopens this step.
      const row = this._summaryRowEl(part);
      if (row) row.focus();
      // After the focus, since focusing can scroll too (see _ensureStageVisible).
      this._ensureStageVisible();
    }

    // The red "No loop" action. It lives in the Loop style segmented
    // control (2026-07-31; it was a footer button, and before that a plain
    // "None" pill), because whether this end is sewn at all is the same
    // question the control's other answers belong to — but it is still the
    // only one that ENDS the step, so it keeps its own path here rather
    // than riding the treatment radios: 'none' is set, the build sheet and
    // the URL follow, and the panel closes.
    _setEndNoLoop() {
      const view = this.state.endView;
      const item = view ? this._endItem(view.role, view.end) : null;
      if (!item) return;
      item.treatment = 'none';
      // The most explicit treatment choice there is (see _clearSleeveMemory).
      this._clearSleeveMemory(item);
      // recompute() + _zoomOut() + _writeUrl() rather than _afterChange()
      // around the zoom, because _afterChange() renders the scene and
      // _zoomOut() renders it AGAIN — and the second render lands mid
      // zoom-tween, which cancels the transition so its settle event never
      // arrives — and the settle is what re-aligns the page under the
      // grown scene (_alignOnSettle). Keep the tween-starting render LAST
      // and let it finish. (This used to matter twice over: a cancelled
      // settle also left the on-canvas length editor stuck at display:none,
      // since only the settle re-showed it. That half went with the
      // editors on 2026-08-02.)
      this.recompute();
      this._zoomOut();
      this._writeUrl();
      const row = this._summaryRowEl(this._partId(view.role, `end${view.end}`));
      if (row) row.focus();
      // _zoomOut already ran this, but focusing can scroll too — same
      // re-check _savePanel makes after its own focus handoff.
      this._ensureStageVisible();
    }

    // Summary rows are the stable focus targets when a panel closes.
    // Scoped to the build sheet on purpose — the stage's "Change webbing"
    // button carries the same data-bc-part and must not win here.
    _summaryRowEl(part) {
      const host = this.querySelector('[data-bc-summary]');
      return host ? host.querySelector(`[data-bc-part="${part}"]`) : null;
    }

    // One build-sheet row. Rows with a `detail` list are wrapped so the
    // disclosure chevron can sit BESIDE the row button (a button inside a
    // button is invalid markup, and the chevron must not re-open the
    // editor) with the disclosed dimensions spanning underneath.
    _summaryRow(row) {
      const empty = row.value === '—' ? ' data-empty="1"' : '';
      const btn =
        `<button type="button" class="bc-seg__sum-row" data-bc-part="${this._attr(row.part)}"${empty}>` +
        `<span class="bc-seg__sum-name">${this._esc(row.name)}</span>` +
        `<span class="bc-seg__sum-val">${this._esc(row.value)}</span>` +
        `</button>`;
      if (!row.detail || !row.detail.length) return btn;
      const open = this._sumDetailOpen(row);
      const id = `bc-seg-detail-${this._uid}-${row.part}`;
      return (
        `<div class="bc-seg__sum-item">` +
        btn +
        `<button type="button" class="bc-seg__sum-more" data-bc-sum-toggle="${this._attr(row.part)}" ` +
        `aria-expanded="${open ? 'true' : 'false'}" aria-controls="${this._attr(id)}" ` +
        `aria-label="${this._attr(`${row.name} dimensions`)}">` +
        `<svg class="bc-seg__sum-chev" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">` +
        `<path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>` +
        `</svg></button>` +
        `<dl class="bc-seg__sum-detail" id="${this._attr(id)}" data-bc-sum-detail="${this._attr(row.part)}" ` +
        `data-sig="${this._attr(this._sumDetailSig(row.detail))}"${open ? '' : ' hidden'}>` +
        this._sumDetailRowsHtml(row.detail) +
        `</dl>` +
        `</div>`
      );
    }

    // What the disclosed rows are made of, flattened — the differential
    // update rewrites the list only when this moves (recompute() runs per
    // keystroke; these values move only when an end is edited).
    _sumDetailSig(detail) {
      return detail.map((d) => `${d.label}=${d.value}`).join('|');
    }

    _sumDetailRowsHtml(detail) {
      return detail.map(({ label, value, alt }) => (
        `<div class="bc-seg__sum-detail-row">` +
        `<dt>${this._esc(label)}</dt>` +
        `<dd>${this._esc(value)}${alt ? ` <span class="bc-seg__sum-alt">(${this._esc(alt)})</span>` : ''}</dd>` +
        `</div>`
      )).join('');
    }

    // The End row at rest states PROTECTION and nothing else (Jerry,
    // 2026-07-31): "Sleeved" / "Naked" is the answer to the question the
    // row is asking, and the dimensions that used to trail it ("Sleeved ·
    // 512 · 139 mm · sleeve 200 mm") turned the build sheet into a spec
    // dump. They live behind the row's disclosure now. A no-loop end
    // reads with its treatment label as before.
    _sewingSummaryText(role, end) {
      const item = this._endItem(role, end);
      if (!item) return '—';
      const ends = this._sewingData(role).endTreatments || [];
      const t = ends.find((e) => e.kind === item.treatment);
      return t ? t.label : item.treatment;
    }

    // The disclosed spec for one end: DIMENSIONS ONLY (Jerry, 2026-07-31
    // — "I don't think we need to list the pattern for now as we are only
    // using default patterns"; the cart properties drop it for the same
    // reason, see _properties). Protection isn't repeated here either —
    // it IS the row's collapsed value, sitting directly above. Empty for
    // a no-loop end, which is what hides its chevron entirely.
    _sewingDetailRows(role, end) {
      const item = this._endItem(role, end);
      if (!item || item.treatment === 'none') return [];
      const out = [];
      const opening = this._effectiveOpeningMm(item, role);
      if (opening != null) {
        out.push({ label: 'Opening', value: `${opening} mm`, alt: this._mmConv(opening) });
      }
      if (item.treatment === 'sleeved' && item.sleeveLengthMm) {
        out.push({
          label: 'Sleeve length',
          value: `${item.sleeveLengthMm} mm`,
          alt: this._mmConv(item.sleeveLengthMm),
        });
      }
      // Which face this loop folds outward — a per-END decision since
      // 2026-07-31, so it reads here rather than as a line-level row. Only
      // where the webbing offers the choice, and named the way the cart
      // property and the editor's control name it, so the three agree.
      const entry = this._entry(role);
      if (this._flippable(entry)) {
        out.push({ label: 'Orientation', value: this._faceOutName(entry, item.faceOut) });
      }
      return out;
    }

    // ---- events -----------------------------------------------------------

    _wireEvents() {
      this.addEventListener('change', (e) => this._onChange(e));
      this.addEventListener('input', (e) => this._onInput(e));
      this.addEventListener('click', (e) => this._onClick(e));
      // Diagram hotspots (assets/bc-segment-diagram.js) bubble part clicks;
      // `via: 'key'` moves focus into the opened editor (the focused SVG
      // rect is destroyed by the re-render).
      this.addEventListener('bc:segment:part-click', (e) => {
        if (e.detail && e.detail.part) this._activatePart(e.detail.part, { via: e.detail.via });
      });
      // Dragging a dimension handle in the zoomed end view. The scene
      // reports raw millimetres and this side owns the clamp — see
      // _onDimDrag for why that split, and why 'drag' must not rebuild.
      this.addEventListener('bc:segment:dim-drag', (e) => {
        if (e.detail) this._onDimDrag(e.detail);
      });
      // Buttons the scene draws ON an end in the zoom view — today only
      // "Add sleeve" on a naked end. Same trust model as the drag: the
      // scene says what was pressed, this side decides whether it's legal.
      this.addEventListener('bc:segment:end-action', (e) => {
        if (e.detail) this._onEndAction(e.detail);
      });
      // The scene settling. Two jobs: re-pin the open length pill off the
      // anchor this draw published — the span it is centred on moves with
      // every keystroke — and spend the align one-shot below.
      //
      // The CLASS hides the overlay while the viewBox is tweening, because
      // mid-tween the anchors don't match what is on screen and the pill
      // would be pinned to nothing. It is a BACKSTOP in round 5: the only
      // thing that tweens is entering or leaving an end view, and the pill
      // is closed outright on the way in (_setEndView), so in practice
      // there is nothing to hide. Kept because it fails safe — a tween
      // that somehow starts with a pill up hides it rather than dragging
      // it across the drawing. (v4 paired this with revealing a "stowed"
      // value on each dimension, so the numbers landed on the canvas as
      // the boxes left it. Round 5 draws every value except the open row's
      // all the time, so there is nothing to stow and that half is gone.)
      //
      // geometryChanged=false draws (colour-only redraws, e.g. a webbing
      // switch) skip the reposition — its getBoundingClientRect right
      // after the svg rebuild forced a full synchronous style recalc +
      // layout for what would be a positioning no-op.
      this.addEventListener('bc:segment:scene-rendered', (e) => {
        const tweening = !!(e.detail && e.detail.tweening);
        this.classList.toggle('bc-segment--tweening', tweening);
        if (tweening) return;
        if (!e.detail || e.detail.geometryChanged !== false) this._positionOverlay();
        // A zoom-in GROWS the scene (the zoom composition is taller than
        // the full view), and _setEndView aligned the panel against the
        // stage as it was BEFORE that growth — so the panel it just put
        // under the customer's thumb slid down by the difference. Re-align
        // once the grown scene has actually landed. Alignment only: focus
        // was placed inside the activating gesture (iOS opens the keyboard
        // for nothing else) and must not be taken and given back.
        // Phones only, for the same reason _setEndView aligns there only.
        //
        // ONE-SHOT, armed by _setEndView — the OPEN is what wants this, not
        // the editor being open (Jerry, 2026-08-02: "on the segment builder
        // on mobile, when changing any of the settings for the sewn loops,
        // it scrolls you to the top of the loop editor"). Gated on
        // state.endView alone it fired on every settled draw, and every
        // field commit draws (_afterChange → _renderScene), so committing a
        // sleeve length or picking a pattern near the foot of the panel
        // yanked the page back to the panel's top edge — under the thumb
        // that was still on the control. It hit the diagram drags too,
        // whose commit deliberately refuses to move FOCUS for exactly this
        // reason (see _onDimDrag).
        //
        // Spent unconditionally rather than only when it fires: the arm
        // belongs to the open that set it, so a first settled draw that
        // isn't a phone (or arrives after the editor already closed —
        // _zoomOut's own tween settles the same way) consumes it instead of
        // banking it for the next commit.
        const alignOnSettle = this._alignOnSettle;
        this._alignOnSettle = false;
        if (alignOnSettle && this.state && this.state.endView != null && this._isNarrow()) {
          this._alignUnderStage(this.pickerHost);
        }
      });
      this.addEventListener('keydown', (e) => {
        // Enter in the loop-name field commits the save (the field sits in
        // no <form>, so Enter would otherwise do nothing).
        if (e.key === 'Enter' && e.target && e.target.matches
          && e.target.matches('[data-bc-loop-name]')) {
          e.preventDefault();
          this._saveCurrentLoop();
          return;
        }
        // Enter in the length pill's typed field = "that's the length"
        // (Jerry, 2026-08-02: "can we make it so pressing enter in the
        // custom length input confirms the length?"). It did nothing
        // whatsoever before — the pill sits in no <form>, so there was
        // nothing for the key to do by default, and the panel branch below
        // only claims Enter inside pickerHost / the webbing modal, neither
        // of which is the overlay host the pill lives in.
        //
        // ABOVE that branch by necessity, not by taste: it returns for
        // every Enter it sees, so anything under it is unreachable. Scoped
        // to the INPUT rather than the pill, deliberately — Done is a
        // <button> and already fires its own click on Enter, and the
        // select's Enter belongs to the select.
        //
        // THE ORDER IS THE WHOLE THING: blur, then commit what the blur
        // could not, then close. That is _savePanel's shape, and it is
        // here for the NotFoundError _savePanel documents. Closing wipes
        // the overlay host's innerHTML, and wiping it while a FOCUSED,
        // user-edited input is still inside makes Chrome dispatch that
        // input's `change` synchronously, mid-wipe; the reentrant _onChange
        // runs recompute() → _syncOverlay, which sees the signature drift
        // the close just created (a role, then null) with focus no longer
        // inside — and rebuilds the overlay from within the removal that is
        // still unwinding. Blurring FIRST spends that `change` one level
        // deep, where the commit it triggers rebuilds nothing at all, so
        // the node the close then removes is clean and unfocused and
        // dispatches nothing. It also drops the touch keyboard, which is
        // what "done" means on the device this key is labelled for.
        //
        // The explicit commit after it covers the one case a blur cannot:
        // a field whose value was never typed into (the mode switch
        // prefills it) carries no dirty flag, fires no change, and so is
        // committed by nothing else. Byte-identical to a change-event
        // commit either way — it IS _commitBoundedInput, same sanitizer,
        // same clamp, same _afterChange({ light: true }) — and idempotent,
        // so paying it twice on a dirty field costs a recompute and
        // changes nothing.
        //
        // The drawing lands with the close, not before it: `light` owes a
        // frame draw, and _closeLengthEditor's _flushScene() is the same
        // point Done and a tap outside settle it at.
        if (e.key === 'Enter' && e.target && e.target.matches
          && e.target.matches('[data-bc-custom-length]')) {
          e.preventDefault();
          const field = e.target;
          if (typeof field.blur === 'function') field.blur();
          // isConnected, because the blur's own commit runs recompute(),
          // and a rebuild banked while the caret was in the field
          // (_syncOverlay's _overlayDirty) is released the moment focus
          // leaves it — replacing this very input.
          if (field.isConnected) this._commitBoundedInput(field, { target: field });
          // fromInside: we blurred it a line ago, so the DOM can no longer
          // testify that focus was in the pill. Lands on the value chip the
          // pill was covering, like Done and Escape.
          this._closeLengthEditor({ refocus: true, fromInside: true });
          return;
        }
        // Enter anywhere in an open panel = Save (see _savePanel). Buttons
        // fire their own click on Enter, so leave those to themselves —
        // otherwise Enter on "No Loop" would both remove the loop and run
        // the save path.
        //
        // ONE exception: a preset chip that is ALREADY ACTIVE (Jerry,
        // 2026-08-01: "I clicked 'Default Sleeved' on End 2 of the mainline
        // and Enter is not doing anything"). Applying a chip re-renders the
        // panel and puts the focus back ON that chip (see _onPresetClick,
        // which passes it as the refocus selector so the row stays
        // keyboard-navigable), so the very next Enter hit the button skip
        // above and re-fired the chip's own click — re-applying a design
        // the end already carries, which is a no-op by definition. With
        // nothing useful left for Enter to mean there, it means what it
        // means everywhere else in the panel: I'm done here.
        //
        // An INACTIVE chip keeps its native behaviour deliberately: moving
        // along the row trying designs out has to apply them and leave the
        // panel open, not close the editor from under a keyboard user on
        // the first chip they reach. Either way the flow reads the same —
        // first Enter applies, second Enter closes.
        if (e.key === 'Enter') {
          const host = this.state.panel ? this.webbingModal : this.pickerHost;
          const inPanel = host && !host.hidden && e.target && host.contains(e.target);
          const closest = (sel) => !!(e.target.closest && e.target.closest(sel));
          const onActiveChip = closest('[data-bc-loop-preset][aria-pressed="true"]');
          if (inPanel && (onActiveChip || !closest('button'))) {
            e.preventDefault();
            this._savePanel();
          }
          return;
        }
        if (e.key !== 'Escape') return;
        // A pending remove-confirm is the shallowest thing open, so
        // Escape backs out of THAT first — same rule as the loop-save
        // form below it.
        if (this._removeConfirm) {
          // Hand focus back to whichever of the two confirms Escape came
          // from — landing in the sheet's when the stage's was the one open
          // sends the customer under the pinned diagram to find it.
          const from = e.target && e.target.closest && e.target.closest('[data-bc-stage-line]');
          this._cancelRemoveLine(from);
          return;
        }
        // Then the LENGTH PILL. Its position in this ladder is mostly
        // documentation: it cannot coexist with anything below it (opening
        // an end view or the webbing picker closes it, and vice versa), so
        // there is no arbitration to get wrong there. It sits under the
        // remove-confirm because that one CAN be open over it — a confirm
        // is raised by a button outside the pill, which does not close it —
        // and a confirm is the shallower, more urgent thing to back out of.
        if (this._lengthOpen) {
          this._closeLengthEditor({ refocus: true });
          return;
        }
        // Escape closes the loop-save form before it closes the panel.
        if (this.state.endView != null && this._loopSaveOpen) {
          this._loopSaveOpen = false;
          this._renderPicker('[data-bc-loop-save-open]');
          return;
        }
        if (this.state.panel) {
          // A native dialog closes ITSELF on Escape and fires `close`,
          // which is what clears the state (see connectedCallback) — so
          // this must not also do it, or the two would race and the focus
          // handoff would run twice. Closing it by hand covers the
          // fallback case where the modal never opened (no showModal).
          if (this.webbingModal && this.webbingModal.open) return;
          this._closeWebbingPanel();
        } else if (this.state.endView != null) {
          const part = this._partId(this.state.endView.role, `end${this.state.endView.end}`);
          const hadFocus = this.pickerHost && this.pickerHost.contains(document.activeElement);
          this._zoomOut();
          if (hadFocus) {
            // Same handoff as the webbing picker: the focused panel field
            // was just destroyed — land on the matching summary row.
            const row = this._summaryRowEl(part);
            if (row) row.focus();
          }
        }
      });

      // The other half of "sometimes Enter doesn't work" (Jerry,
      // 2026-08-01): focus can end up OUTSIDE this component with a panel
      // still open — click the page background, or activate something the
      // browser doesn't focus on click (Safari doesn't focus buttons) —
      // and the active element becomes <body>. keydown then fires on body,
      // which is an ANCESTOR of this element, so the listener above never
      // sees the event at all and Enter does nothing whatsoever. A
      // document-level fallback is the only way to close that gap.
      //
      // Deliberately the narrowest net that closes it: only with a panel
      // actually open, only for a target OUTSIDE this component (inside,
      // the listener above owns it and must not run twice), and only when
      // that target is the body or the document root — which is to say,
      // only when nothing is focused at all. The page around us carries
      // real focusables (Horizon's header search, the cart drawer, the
      // Shopify Inbox embed) and Enter in any of them must never reach a
      // panel save. Torn down in disconnectedCallback with the other
      // outside-the-component listeners.
      this._docKeydown = (e) => {
        if (e.key !== 'Enter') return;
        if (!this.state || (this.state.endView == null && !this.state.panel)) return;
        if (this.contains(e.target)) return;
        if (e.target !== document.body && e.target !== document.documentElement) return;
        e.preventDefault();
        this._savePanel();
      };
      document.addEventListener('keydown', this._docKeydown);

      // TAP OUTSIDE closes the length pill. Document level and CAPTURING,
      // because "outside" includes the whole page, not just this element —
      // scrolling on to the build sheet, the header, anywhere.
      //
      // On POINTERDOWN, not click: the pill can be closed by a press that
      // never becomes a click on anything (a drag, a scroll gesture), and
      // waiting for click would leave it up through all of it.
      //
      // Two exemptions, and both are load-bearing:
      //   - inside the OVERLAY, which is the pill itself. Its Done button
      //     closes it through the normal click path.
      //   - on ANY scene hotspot ([data-bc-hotspot]). Closing here would
      //     re-render the scene, and _draw replaceChildren()s the svg — so
      //     the rect this pointerdown started on is detached before
      //     pointerup and the browser never fires its click: the end
      //     editors would take two taps to open while a pill is up. Every
      //     hotspot already closes the pill through its own activation
      //     (_setEndView and _activatePart's webbing branch clear
      //     _lengthOpen; a length chip is a toggle whose open/switch/close
      //     decision belongs to _activatePart, which sees both roles), so
      //     nothing is left open by deferring to them. Blank diagram space
      //     has no [data-bc-hotspot] ancestor, so tap-on-the-drawing still
      //     closes.
      //     (Since 2026-08-02 a close no longer redraws the scene by
      //     itself, so the detached-rect half of that argument now only
      //     covers the END hotspots — whose activation does redraw. The
      //     TOGGLE half covers the length chips and is the reason the
      //     exemption stays whole: a chip must reach _activatePart to
      //     decide open-vs-close, and this listener closing first would
      //     turn every second tap into a re-open.)
      //   - on a build-sheet LENGTH row ([data-bc-part]) — same toggle
      //     argument as the chip. The [data-bc-part] rows are HTML and
      //     survive a scene redraw, so only the length ones (which toggle
      //     in _activatePart) need exempting; every other row closes the
      //     pill here and then activates normally.
      //     (Both toggle exemptions moved into _ownsPillToggle on
      //     2026-08-02 — the blur close below needs exactly the same pair,
      //     and two copies of "which controls own the toggle" is two
      //     answers waiting to disagree.)
      // Everything else — the stage buttons, the rest of the build sheet,
      // the page — closes it, which is the behaviour the mockup
      // established and Jerry approved.
      this._docPointerDown = (e) => {
        if (!this._lengthOpen) return;
        const t = e.target;
        // Recorded for the blur close, which has no other way to know a
        // press is in flight ON the pill — see _onOverlayFocusOut. Written
        // before any of the exits below, since every one of them is a press
        // this question has an answer for. Cleared by the release, so it
        // can never go stale into a later gesture.
        this._ovPointerHeld = !!(t && this.overlayHost && this.overlayHost.contains(t));
        if (!t || typeof t.closest !== 'function') { this._closeLengthEditor(); return; }
        if (this.overlayHost && this.overlayHost.contains(t)) return;
        if (this._ownsPillToggle(t)) return;
        // No refocus: the customer is pressing something else, and taking
        // focus back to the chip they just left would fight the press.
        this._closeLengthEditor();
      };
      document.addEventListener('pointerdown', this._docPointerDown, true);

      // The other half of the flag above. Document level and capturing for
      // the same reason as the press: a release can land anywhere, and a
      // gesture that never releases at all (a system swipe, the page
      // scrolling away under the finger) ends as pointercancel.
      this._docPointerUp = () => { this._ovPointerHeld = false; };
      document.addEventListener('pointerup', this._docPointerUp, true);
      document.addEventListener('pointercancel', this._docPointerUp, true);

      // FOCUS LEAVING the pill closes it too — which is how iOS's Done
      // key reaches it at all (Jerry, 2026-08-02: "Same for the checkmark
      // on iOS?"). An inputmode="decimal" keyboard has no return key, so
      // there is no Enter for the branch above to catch: the checkmark
      // over the pad only BLURS the field. Nothing listened for that, so
      // the keyboard came down and the pill stayed up over the drawing
      // with no visible reason left for it to be there.
      //
      // Bound on the OVERLAY HOST, not the document: focusout bubbles, and
      // the only focus moves this cares about start inside the pill. It
      // needs no teardown either — the host is this component's own child
      // (see connectedCallback) and is never replaced, only refilled, so
      // the listener outlives every pill and dies with the element.
      if (this.overlayHost) {
        this.overlayHost.addEventListener('focusout', (e) => this._onOverlayFocusOut(e));
      }
    }

    // The two things OUTSIDE the pill whose own activation decides what
    // happens to it: the scene's hotspots and the build sheet's length
    // rows. Both are toggles — _activatePart sees the open role and closes
    // it — so anything that closes the pill on the way to one of them
    // turns every second tap into a re-open. Shared by the two closes that
    // can beat a toggle to it (the outside pointerdown and the blur), so
    // the pair cannot drift apart on which controls are exempt.
    _ownsPillToggle(node) {
      if (!node || typeof node.closest !== 'function') return false;
      if (node.closest('[data-bc-hotspot]')) return true;
      const row = node.closest('[data-bc-part]');
      return !!(row && this._parsePart(row.getAttribute('data-bc-part')).part === 'length');
    }

    // "Did focus actually LEAVE the pill?" — asked on the NEXT TICK, never
    // inside the focusout itself.
    //
    // relatedTarget answers it outright when the browser fills it in, and
    // the fast path takes that answer for the moves that must close
    // nothing: select → input on the mode switch, input → Done, input →
    // "+ Add extender" on the backup's recommendation. But iOS hands out a
    // NULL relatedTarget for the Done key, so null cannot be read as
    // "focus went nowhere" — it only means the browser isn't saying. The
    // one reliable answer is where focus ENDED UP, and that is not settled
    // during the event: Blink clears the focused element before dispatching
    // focusout and sets the new one after, so document.activeElement reads
    // <body> mid-transfer even for a move that never left the pill.
    //
    // A TIMER and not a microtask, for that exact reason — a microtask
    // checkpoint runs between the focusout and the matching focusin, which
    // is the window that lies. On the next task the transfer is complete,
    // and every transient blur-and-refocus inside one turn (the mode
    // switch's fallback rebuild, one pill closing as the next opens, the
    // Enter commit blurring before it closes) is invisible here.
    _onOverlayFocusOut(e) {
      if (!this._lengthOpen || !this.overlayHost) return;
      // Moving between the pill's own controls is not leaving it.
      if (e.relatedTarget && this.overlayHost.contains(e.relatedTarget)) return;
      clearTimeout(this._ovBlurTimer);
      this._ovBlurTimer = setTimeout(() => {
        this._ovBlurTimer = null;
        const host = this.overlayHost;
        // Already closed by whatever the focus went to — the outside
        // pointerdown, Done's click, Escape, the Enter commit. This is
        // where closing being idempotent earns its keep: _closeLengthEditor
        // returns on a null _lengthOpen, so the two arriving in either
        // order is a no-op rather than a double close, and the one that got
        // there first keeps its refocus decision.
        if (!this._lengthOpen || !host) return;
        // A PRESS STILL IN FLIGHT on the pill. Safari focuses no buttons on
        // click, so pressing Done or "+ Add extender" with the caret in the
        // field blurs it with a null relatedTarget and nothing focused —
        // and closing on that would remove the button before its own click
        // could fire, so "+ Add extender" would simply never add one. The
        // press is a promise that a click is coming; let it arrive and do
        // the closing itself. Chrome and Firefox never reach this line —
        // they focus the button, so relatedTarget answered above.
        if (this._ovPointerHeld) return;
        const to = document.activeElement;
        if (to && host.contains(to)) return;
        // Same exemption, same reason, as the outside pointerdown: a
        // hotspot or a length row owns its own toggle.
        if (this._ownsPillToggle(to)) return;
        // No refocus. Focus has already gone somewhere of the customer's
        // choosing — or nowhere, which on iOS is the keyboard coming down —
        // and pulling it back to the value chip would fight whatever put it
        // there. (Escape, Done and Enter all refocus, because in those the
        // pill IS where focus was when the decision was made.)
        this._closeLengthEditor();
      }, 0);
    }

    // The end editor is the only panel carrying sewing fields, so the ROLE
    // is whichever line the zoom is on; the END comes from the wrapper the
    // field sits in. Null when either is missing.
    _sewingTargetFromEvent(e) {
      const wrap = e.target.closest('[data-bc-sewing-item]');
      const view = this.state.endView;
      if (!wrap || !view) return null;
      const end = Number(wrap.getAttribute('data-bc-sewing-item'));
      const item = this._endItem(view.role, end);
      return item ? { role: view.role, end, item } : null;
    }

    _onChange(e) {
      const t = e.target;
      if (t.matches('[data-bc-webbing]')) {
        // Whose line this picker is editing is written on the tile list
        // (see _webbingPanelHtml), with the open panel as the fallback.
        const list = t.closest('[data-bc-webbing-role]');
        const role = (list && list.getAttribute('data-bc-webbing-role'))
          || (this.state.panel && this.state.panel.role);
        if (role) this._changeWebbing(role, t.value);
      } else if (t.matches('[data-bc-length-select]')) {
        // Which line an overlay control edits is the anchor name of the
        // group it sits in — see _overlayRoleFromEvent.
        const role = this._overlayRoleFromEvent(e);
        const line = this._lineState(role);
        if (!line) return;
        // Only a MODE change is structural (custom mode adds the number
        // input beside this select, fixed mode takes it away), and only a
        // structural change may rebuild — a rebuild replaces this very
        // select, so picking one fixed length after another would drop and
        // re-take focus on every step of an arrow-key traversal. Picking a
        // different fixed value writes state and lets _syncOverlay carry
        // the ft/m readout; the select already holds the new value.
        const wasCustom = line.lengthMode === 'custom';
        const wantCustom = t.value === '__custom__';
        if (wantCustom) {
          line.lengthMode = 'custom';
          if (!(line.length > 0)) line.length = 1;
        } else {
          line.lengthMode = 'fixed';
          line.fixedValue = t.value;
        }
        // THE ORDER HERE IS HALF THE FIX (Jerry, 2026-08-02, Mac Firefox:
        // "its mainly when I first select custom length, it takes about 1
        // second to be able to edit anything"). Everything between the
        // customer's pick and the field being editable is now: one node
        // inserted, then focus. The reposition, the recompute and the
        // redraw all come after it, and two of the three no longer happen
        // in this turn at all.
        //
        // Before, that same window ran a whole-pill innerHTML rebuild
        // (destroying the <select> that was still dispatching this event),
        // then _positionOverlay — three getBoundingClientRects, i.e. a
        // forced layout of a document that is overflow:hidden html/body
        // plus a 100dvh .page-wrapper plus two sticky columns — and only
        // then focused an input that had existed for microseconds.
        // See _swapOverlayCustomInput for what each of those cost.
        //
        // The OTHER half is the preventScroll below, which is what actually
        // accounts for a whole second rather than tens of milliseconds.
        if (wasCustom !== wantCustom) {
          let f = this._swapOverlayCustomInput(role, wantCustom);
          if (!f) {
            // The pill wasn't patchable — rebuild it and find the control
            // there instead. Scoped by anchor name, which is what kept a
            // backup length switch out of the mainline's field back when
            // three of these were on screen at once.
            this._renderOverlay();
            const row = this._overlayGroup(role);
            f = row && (wantCustom
              ? row.querySelector('[data-bc-custom-length]')
              : row.querySelector('[data-bc-length-select]'));
          }
          if (f) {
            // preventScroll IS THE HEADLINE FIX (Jerry, 2026-08-02, Mac
            // FIREFOX: "when I first select custom length, it takes about 1
            // second to be able to edit anything").
            //
            // Horizon sets scroll-behavior:smooth on BOTH <html> and
            // .page-wrapper — and on desktop (≥990px) .page-wrapper IS the
            // scroller, since base.css puts overflow:hidden + 100dvh on
            // html/body. A browser-initiated "scroll the focus target into
            // view" obeys that, so it ANIMATES. This component has already
            // been bitten by it twice and measured it at ~400ms a glide:
            // see _ensureStageVisible and _scrollPanelIntoView, both of
            // which compute their delta by hand and pass behavior:'instant'
            // for exactly this reason, and the end panel's own opening
            // field, which has passed preventScroll since 2026-07-31.
            //
            // This was the ONE focus() in the component that never got the
            // treatment — and it is the worst placed to go without it. The
            // pill is an ABSOLUTELY positioned box inside a position:sticky
            // stage, and it had just been destroyed and rebuilt, so the
            // browser was deciding "is it in view?" about a box with no
            // layout history at all — precisely the sticky-resolution case
            // _ensureStageVisible documents ("scrollIntoView resolves
            // against the PINNED box"), where browsers guess wrong and
            // over-scroll. A ~400ms Firefox glide, with the field moving
            // under the caret the whole way, IS "about a second before I
            // can edit anything".
            //
            // Safe to suppress unconditionally here: the pill is drawn ON
            // the pinned stage, so if the customer can see the select they
            // just used, they can see the field beside it. There is nothing
            // to scroll to.
            f.focus({ preventScroll: true });
            // Land in the number with its value selected, so typing
            // replaces the prefill instead of appending to it.
            if (wantCustom && typeof f.select === 'function') f.select();
          }
          // The pill just grew or lost a field and is pinned by its CENTRE,
          // so it has to be re-pinned — but from a frame, after the focus,
          // rather than as a forced layout standing between the tap and the
          // caret.
          this._scheduleOverlayPosition();
        }
        // The DEFERRED path, deliberately, for a control that commits
        // (Jerry, 2026-08-02: picking "Custom" in the pill felt as slow as
        // opening it). `light` puts the scene redraw on the next frame and
        // debounces the URL write; the full path runs both inside the
        // gesture, and doing them here meant a whole svg rebuild plus a
        // replaceState style-flush between the customer's tap and the
        // number input appearing under it.
        //
        // Safe HERE in a way it wouldn't be elsewhere: this select only
        // exists inside the open pill, so the one row whose drawn value
        // just changed is the one row whose chip is hidden behind that
        // pill — a frame-later redraw is invisible on it. What the redraw
        // is really for is everything else (the band split on a multi-line
        // build, the backup's shortfall frame), which is exactly what a
        // per-keystroke length edit already defers. The build sheet, the
        // price and the pill's own ft/m readout still update synchronously
        // in recompute().
        this._afterChange({ light: true });
      } else if (t.matches('[data-bc-treatment]')) {
        const target = this._sewingTargetFromEvent(e);
        if (target) {
          const { role, end, item } = target;
          item.treatment = t.value;
          // An explicit answer to "what loop style?" — so whatever the rule
          // took away earlier is no longer owed back (see _clearSleeveMemory).
          this._clearSleeveMemory(item);
          // Entering sleeved clamps the carried sleeve length into the
          // current opening's bounds (the pill is disabled when the
          // opening disallows sleeves, so no allowed-check needed here).
          this._enforceSleeveRule(item, role);
          this._renderPicker(`[name="bc-seg-end${end}-treatment"]:checked`);
          this._afterChange();
        }
      } else if (t.matches('[data-bc-face]')) {
        const target = this._sewingTargetFromEvent(e);
        if (target) {
          const { role, end, item } = target;
          item.faceOut = t.value === 'bottom' ? 'bottom' : 'top';
          // The LINE's drawn orientation derives from this (_lineFaces), so
          // the scene has to redraw — _afterChange does that. The panel
          // rebuild keeps the active segment in step and returns focus to
          // the control just used, like the treatment row above.
          this._renderPicker(`[name="bc-seg-end${end}-face"]:checked`);
          this._afterChange();
        }
      } else if (t.matches('[data-bc-pattern]')) {
        const target = this._sewingTargetFromEvent(e);
        if (target) {
          const { role, item } = target;
          item.pattern = t.value;
          const p = this._pattern(item, role);
          item.openingMm = p && p.openingMm != null ? p.openingMm : null;
          // A pattern switch moves the opening to the new default, which
          // can change what the sleeve rule allows.
          this._enforceSleeveRule(item, role);
          this._renderPicker('[data-bc-pattern]');
          this._afterChange();
        }
      } else if (t.matches('[data-bc-opening]') || t.matches('[data-bc-sleeve-length]') || t.matches('[data-bc-custom-length]') || t.matches('[data-bc-qty]')) {
        // Commit-time clamp (blur/Enter fires change) — keystrokes only
        // enforce shape, mirroring bc-custom-length's enforceBounds timing.
        this._commitBoundedInput(t, e);
      }
    }

    // Direct drag on the ZOOMED END DIAGRAM (Jerry, 2026-07-31, replacing
    // the panel's rail sliders): the scene renders grab handles on the
    // opening dimension and the sleeve end, pointer-captures the gesture,
    // converts px→mm off the view's own scale and emits
    // `bc:segment:dim-drag {part, field, valueMm, phase}` WITHOUT clamping.
    // This side is authoritative — it clamps, writes state, and lets the
    // redraw carry the handle, so a handle can never sit somewhere the
    // value isn't and stops dead at the bounds.
    //
    // Unlike the typed field — which holds a DRAFT until blur/Enter, so
    // the scene never echoes a half-typed number — every position of a
    // drag is a real value, so 'drag' writes state live and redraws
    // through the light (rAF-coalesced, URL-debounced) path.
    //
    // What 'drag' does NOT do is re-render the panel: it would destroy the
    // fields mid-gesture (and, on the phone, thrash a layout the finger is
    // sitting on). The dependent bits update IN PLACE — the paired input,
    // the inches readout, and, when the opening moves, the sleeve row's
    // whole range, since its cap is (opening − 10) × 2. Structure settles
    // on 'commit', and only when the sleeve rule actually changed
    // something.
    //
    // Focus is deliberately NOT moved on commit: the hand is on the
    // diagram, and pulling focus into the panel would scroll the phone
    // away from the thing just dragged. (The slider this replaced could
    // refocus itself because it WAS the control being operated.)
    _onDimDrag(detail) {
      const { role, part } = this._parsePart(detail && detail.part);
      const m = /^end([12])$/.exec(part || '');
      if (!m) return;
      const item = this._endItem(role, Number(m[1]));
      // Defensive: a 'none' end has no dimensions to drag, and a stale
      // event could name a line that has since been removed. The scene
      // shouldn't emit either — trusting it is what makes it a bug.
      if (!item || item.treatment === 'none') return;
      const opening = detail.field === 'opening';
      if (!opening && detail.field !== 'sleeve') return;
      if (!opening && item.treatment !== 'sleeved') return;
      let v = Math.round(Number(detail.valueMm));
      if (!Number.isFinite(v)) return;

      if (opening) {
        const p = this._pattern(item, role);
        if (!p || p.openingMm == null) return;   // nothing editable here
        const min = p.openingMinMm != null ? p.openingMinMm : 50;
        const max = p.openingMaxMm != null ? p.openingMaxMm : 500;
        item.openingMm = Math.min(max, Math.max(min, v));
        this._setDimValue('[data-bc-opening]', item.openingMm, '[data-bc-opening-conv]');
        // The sleeve rule now runs LIVE on the opening, in both directions
        // (Jerry, staging 2026-07-31) — crossing the floor downward drops
        // the sleeve and crossing back up restores it, so the drawing under
        // the finger always shows what the build actually is. Structure
        // settles here too, but only on the frames where it moved: the
        // panel is not under the pointer during a diagram drag (the scene
        // holds pointer capture), so rebuilding it is safe — it just isn't
        // free, which is why it is gated on a real change.
        if (this._applyOpeningChange(role, item)) this._renderPicker();
      } else {
        const { min, max } = this._sleeveBounds(item, role);
        item.sleeveLengthMm = Math.min(max, Math.max(min, v));
        this._setDimValue('[data-bc-sleeve-length]', item.sleeveLengthMm, '[data-bc-sleeve-conv]');
      }

      this._afterChange(detail.phase === 'commit' ? {} : { light: true });
    }

    // One-shot buttons pressed in the zoomed end view (the scene draws
    // them; this decides). Every one of them is a shortcut to something the
    // end panel already offers, and each is deliberately written as the
    // panel control's own steps rather than as its own idea of the
    // end-state, so the two entry points cannot disagree.
    //
    // The scene's own gating is re-checked here throughout: a scene that
    // got it wrong must not be able to write a state the panel forbids.
    //
    // No focus handoff on the two that keep the panel open: the hand is on
    // the diagram, and pulling focus into the rebuilt panel would scroll a
    // phone away from the button just pressed (same reason _onDimDrag's
    // commit doesn't). 'remove-loop' is the exception — it CLOSES the step,
    // so it hands focus to the summary row like every other close does.
    _onEndAction(detail) {
      if (!detail) return;
      const { role, part } = this._parsePart(detail.part);
      const m = /^end([12])$/.exec(part || '');
      if (!m) return;
      const end = Number(m[1]);
      const item = this._endItem(role, end);
      if (!item || item.treatment === 'none') return;

      if (detail.action === 'add-sleeve') {
        // Deliberately the segmented control's own two steps — set the
        // treatment, let _enforceSleeveRule fill in / clamp the length — so
        // the two entry points can't disagree about what a sleeve starts as.
        //
        // The scene hides the button below the floor and on webbings with no
        // sleeved treatment; re-checked here for the reason above.
        if (!this._sleeveAllowed(item, role)) return;
        if (!(this._sewingData(role).endTreatments || []).some((t) => t.kind === 'sleeved')) return;
        item.treatment = 'sleeved';
        this._clearSleeveMemory(item);   // an explicit choice, like the control
        this._enforceSleeveRule(item, role);
        this._renderPicker();
        this._afterChange();
        return;
      }

      if (detail.action === 'remove-sleeve') {
        // The ✕ beside the sleeve's value (Jerry, 2026-08-01). Exactly what
        // moving the segmented control off Sleeved does: fall back to the
        // webbing's non-sleeved loop treatment (the same preference order
        // _enforceSleeveRule uses when the RULE strips a sleeve), and KEEP
        // sleeveLengthMm, so choosing Sleeved again resumes the length that
        // was there rather than the type default.
        if (item.treatment !== 'sleeved') return;
        const ends = this._sewingData(role).endTreatments || [];
        const fallback = ends.find((t) => t.kind === 'naked')
          || ends.find((t) => t.kind !== 'sleeved' && t.kind !== 'none');
        item.treatment = fallback ? fallback.kind : 'naked';
        // An explicit choice, so nothing is owed back (see
        // _clearSleeveMemory) — a rule-stripped sleeve returns when the
        // opening comes back over the floor; one taken away by hand must not.
        this._clearSleeveMemory(item);
        this._renderPicker();
        this._afterChange();
        return;
      }

      if (detail.action === 'remove-loop') {
        // The red ✕ off the fold tip (Jerry, 2026-08-01) — the diagram's
        // copy of the panel's "No loop" segment, so it routes through the
        // one path that owns that answer: _setEndNoLoop sets the treatment,
        // closes the step and zooms out in the order the tween needs.
        //
        // It reads state.endView, so refuse anything the OPEN editor isn't
        // about. The zoomed view only exists while an end editor is open,
        // so they should always agree — which is precisely why a
        // disagreement means the scene is stale and must not be obeyed.
        const view = this.state.endView;
        if (!view || view.role !== role || view.end !== end) return;
        this._setEndNoLoop();
      }
    }

    // Write one committed mm value into its typed field + inches readout.
    // Also re-runs the bounds flag: the value came from a control that
    // cannot leave its range, so this is what clears a red left behind by
    // something typed before it.
    _setDimValue(sel, v, convSel) {
      const f = this.pickerHost && this.pickerHost.querySelector(sel);
      if (f) {
        f.value = v;
        this._flagDimBounds(f);
      }
      this._updateDimConv(convSel, v);
    }

    // The sleeve's range follows the OPENING (see _sleeveBounds), so a
    // live opening drag has to carry the sleeve row with it: the input's
    // data-min/max (which _flagDimBounds and the commit clamp both read),
    // its stated hint — now the ONLY written statement of the limits, so
    // it can least of all be allowed to go stale — and the value itself
    // when the new cap is below it. All in place: the panel must not
    // rebuild under an active drag.
    //
    // The item's value is normalized whether or not the panel is on
    // screen; only the DOM half is conditional. A drag with no panel
    // (defensive — the zoom view always shows one today) must still leave
    // the state coherent.
    // EVERYTHING that follows from an end's opening moving, wherever the
    // move came from — the typed field, a diagram drag, live or committed.
    // Both paths call this so the two can't drift, which is the bug it was
    // written for (Jerry, staging 2026-07-31): dragging under 50 mm dropped
    // the sleeve, but dragging back over 50 left the Sleeved option greyed
    // out until the numeric input was touched, because only the input path
    // ever re-judged sleeve-ability.
    //
    // Order matters: restore before enforce, so a restored sleeve is then
    // clamped into the CURRENT bounds by the same rule as any other.
    // Returns true when the panel's STRUCTURE changed (the treatment moved,
    // or the Sleeved option flipped between usable and not) — the callers
    // rebuild only then, so a drag redraws the panel at most a couple of
    // times per gesture instead of every frame.
    _applyOpeningChange(role, item) {
      const wasTreatment = item.treatment;
      const wasAllowed = this._sleeveAllowed(item, role);

      // Auto-resleeve: the opening is back over the floor and the rule owes
      // this end a sleeve (see _clearSleeveMemory for who is owed one).
      if (item._sleeveMemoryMm != null && item.treatment !== 'none'
        && this._sleeveAllowed(item, role)
        && (this._sewingData(role).endTreatments || []).some((t) => t.kind === 'sleeved')) {
        const { min, max } = this._sleeveBounds(item, role);
        item.treatment = 'sleeved';
        item.sleeveLengthMm = Math.min(max, Math.max(min, item._sleeveMemoryMm));
        this._clearSleeveMemory(item);
      }
      this._enforceSleeveRule(item, role);
      this._syncSleeveRange(role, item);
      this._syncSleeveAffordance(role, item);
      return item.treatment !== wasTreatment
        || this._sleeveAllowed(item, role) !== wasAllowed;
    }

    // The Loop style step's sleeve-ability, refreshed IN PLACE: the Sleeved
    // option's disabled state (and the matching class on its track segment)
    // and the red floor hint below it. Cheap enough to run on every drag
    // frame, which is the point — the structural rebuild that used to be
    // the only way these updated is reserved for a real change.
    _syncSleeveAffordance(role, item) {
      const host = this.pickerHost;
      if (!host) return;
      const allowed = this._sleeveAllowed(item, role);
      const radio = host.querySelector('[data-bc-treatment][value="sleeved"]');
      if (radio) {
        radio.disabled = !allowed;
        const opt = radio.closest('.bc-seg__seg-opt');
        if (opt) opt.classList.toggle('is-disabled', !allowed);
      }
      const hint = host.querySelector('[data-bc-sleeve-floor-hint]');
      if (hint) hint.hidden = allowed;
    }

    _syncSleeveRange(role, item) {
      const { min, max } = this._sleeveBounds(item, role);
      const type = this._endLoopType();
      const def = Math.min(max, Math.max(min, type.sleeveDefaultMm != null ? type.sleeveDefaultMm : 200));
      // Only a LIVE sleeve is clamped. On a naked end the length is a
      // CARRIED preference — the value Sleeved would resume at — and
      // clamping it against an opening no sleeve is using degraded it for
      // good: winding down to 60 mm and back cost a naked end's carried
      // 200 mm sleeve permanently, since nothing ever winds it back up.
      // Re-entry clamps it (_enforceSleeveRule), which is the moment the
      // number becomes real. The FIELD's bounds and hint below still track
      // the opening either way — they describe the range, not the value.
      if (item.treatment === 'sleeved') {
        item.sleeveLengthMm = Math.min(max, Math.max(min, Number(item.sleeveLengthMm) || def));
      }
      const host = this.pickerHost;
      const field = host && host.querySelector('[data-bc-sleeve-length]');
      if (!field) return;
      field.dataset.min = String(min);
      field.dataset.max = String(max);
      this._setDimValue('[data-bc-sleeve-length]', item.sleeveLengthMm, '[data-bc-sleeve-conv]');
      const step = field.closest('.bc-seg__step');
      const hint = step && step.querySelector('[data-bc-bounds-hint]');
      if (hint) hint.textContent = `Default ${def} mm · ${min}–${max} mm`;
    }

    _commitBoundedInput(t, e) {
      if (t.matches('[data-bc-custom-length]')) {
        // One length editor per line — the group it sits in names which.
        const line = this._lineState(this._overlayRoleFromEvent(e));
        if (!line) return;
        const v = this._sanitizeLength(t.value);
        const num = Number(v) || 0;
        line.length = num > 0 ? num : 0;
        t.value = line.length || '';
        // The COMMIT (blur/Enter/Done) draws next frame, not inside the
        // gesture. It is one event rather than a stream, but it is an event
        // that fires while the customer's finger is still on the way to
        // somewhere — a phone's blur commit lands on the same tap that is
        // trying to open the next control — and a synchronous full svg
        // rebuild plus a history.replaceState style flush in that window is
        // exactly the stall the typing path already refuses to pay.
        //
        // `light` and not `defer`: nothing follows a commit, so there is
        // no interaction left to wait out and the drawing should land at
        // once. Its frame draw also spends the dirty flag left by the
        // typing, so the commit is never the thing that leaves the drawing
        // behind.
        //
        // Safe for everything downstream: state is already written, the
        // cart reads state and never the address bar (_lines / _buildParams
        // lift only `view` from location.search), and the paths that end
        // the interaction flush both (see _flushScene / _flushUrl).
        this._afterChange({ light: true });
      } else if (t.matches('[data-bc-qty]')) {
        const q = Math.min(999, Math.max(1, parseInt(t.value, 10) || 1));
        this.state.qty = q;
        t.value = q;
        this._afterChange({ light: true });
      } else if (t.matches('[data-bc-opening]')) {
        const target = this._sewingTargetFromEvent(e);
        if (!target) return;
        const { role, item } = target;
        const min = Number(t.dataset.min);
        const max = Number(t.dataset.max);
        let v = parseInt(t.value, 10);
        if (!Number.isFinite(v)) {
          const p = this._pattern(item, role);
          v = p && p.openingMm != null ? p.openingMm : min;
        }
        v = Math.min(max, Math.max(min, v));
        item.openingMm = v;
        t.value = v;
        this._updateDimConv('[data-bc-opening-conv]', v);
        // The sleeve rule keys off the opening (allowed at all? capped at
        // what?), so a committed opening re-normalizes the item and
        // re-renders the panel — pill disabled state, sleeve bounds hint,
        // and a clamped/dropped sleeve all follow. Commit fires on
        // blur/Enter, so this never rebuilds mid-keystroke. The rebuild also
        // discards any out-of-bounds flag along with the old field — hence
        // no _flagDimBounds call here (the sleeve branch, which doesn't
        // re-render, needs one).
        //
        // The refocus is DESKTOP-ONLY (Jerry, 2026-07-31): there it keeps
        // the caret where the user was typing across the rebuild, but on a
        // phone the commit is the user CLOSING the keyboard (Enter/Done or
        // tapping away), and refocusing the rebuilt input re-opened it —
        // the page refusing to let go of a field the user just left.
        //
        // Through the SAME seam the drag uses (auto-resleeve included), so
        // typing 60 into a field the rule stripped a sleeve from restores
        // it exactly as dragging back over the floor does. This path
        // rebuilds unconditionally — it already did, and a commit is one
        // event, not a stream of them.
        this._applyOpeningChange(role, item);
        this._renderPicker(this._isNarrow() ? null : '[data-bc-opening]');
        this._afterChange();
      } else if (t.matches('[data-bc-sleeve-length]')) {
        const target = this._sewingTargetFromEvent(e);
        if (!target) return;
        const { item } = target;
        const min = Number(t.dataset.min);
        const max = Number(t.dataset.max);
        let v = parseInt(t.value, 10);
        if (!Number.isFinite(v)) v = Number(this._endLoopType().sleeveDefaultMm) || 200;
        v = Math.min(max, Math.max(min, v));
        item.sleeveLengthMm = v;
        t.value = v;
        this._updateDimConv('[data-bc-sleeve-conv]', v);
        // Unlike the opening, this branch does NOT re-render the panel, so
        // the field survives the commit and would keep an out-of-bounds flag
        // describing a value that has just been clamped away. Re-run the
        // check against the committed (in-range) value to clear it.
        this._flagDimBounds(t);
        this._afterChange();
      }
    }

    // The in-box carries its own "in" suffix, so only the VALUE goes in.
    _updateDimConv(sel, mm) {
      const conv = this.pickerHost && this.pickerHost.querySelector(sel);
      if (conv) conv.textContent = this._mmConvValue(mm);
    }

    // Live out-of-bounds indication for the end editor's two mm fields
    // (Jerry, 2026-07-31: "we have no way of knowing a number we entered is
    // out of bounds"). Until now the first sign of a rejected number was the
    // commit-time clamp silently rewriting the field on blur/Enter.
    // INDICATION ONLY — nothing here touches the value, the item or the
    // scene; _commitBoundedInput still owns the clamp, so the field still
    // snaps into range exactly as before.
    // Bounds are read off data-min/max at keystroke time, which is what
    // makes the sleeve field correct for free: its cap follows the opening
    // (_sleeveBounds) and the panel re-renders on every opening commit, so
    // the attributes on screen are always the live range.
    // A too-LARGE value flags on the keystroke — more digits only grow it,
    // so it is wrong the moment it exists. A too-SMALL one may just be a
    // prefix still being typed ("5" on the way to "500"), so instead of
    // reddening every such prefix the current state is HELD and re-judged
    // once the digits stop for a beat (`settled`, via the timer below) —
    // the resting too-small value still flags, which is the case that
    // prompted all this; only the mid-entry flash is filtered out.
    _flagDimBounds(t, settled) {
      const min = Number(t.dataset.min);
      const max = Number(t.dataset.max);
      const v = parseInt(t.value, 10);
      const finite = Number.isFinite(v) && Number.isFinite(min) && Number.isFinite(max);
      // Every call re-arms judgment: a keystroke obsoletes a pending one.
      clearTimeout(this._dimFlagTimer);
      if (finite && v < min && v <= max && !settled) {
        // Hold whatever the field shows now (an already-red field stays
        // red — clearing it for a prefix would blink) and re-judge at
        // rest. The commit path can't race this: it clamps first, so its
        // call lands here with an in-range value and just clears.
        this._dimFlagTimer = setTimeout(() => {
          if (t.isConnected) this._flagDimBounds(t, true);
        }, 700);
        return;
      }
      // Empty field is "not yet a number", not a violation (and the commit
      // path fills it from the default rather than clamping).
      const bad = finite && (v < min || v > max);
      t.classList.toggle('is-out-of-bounds', bad);
      if (bad) t.setAttribute('aria-invalid', 'true');
      else t.removeAttribute('aria-invalid');
      // The bounds line goes red WITH the border: a red edge says "wrong",
      // the reddened "Default 130 mm · 50–300 mm" says what would be right.
      const field = t.closest('.bc-seg__step');
      const hint = field && field.querySelector('[data-bc-bounds-hint]');
      if (hint) hint.classList.toggle('bc-seg__hint--warn', bad);
    }

    _onInput(e) {
      const t = e.target;
      if (t.matches('[data-bc-custom-length]')) {
        const line = this._lineState(this._overlayRoleFromEvent(e));
        if (!line) return;
        const v = this._sanitizeLength(t.value);
        if (v !== t.value) t.value = v;
        line.length = Number(v) || 0;
        // Everything downstream of the keystroke follows from state, but on
        // TWO different clocks (Jerry, 2026-08-02: "it still feels slow on
        // the custom length", then "its still slow, now while inside the
        // editor"):
        //
        //   NOW — the pill's own ft/m readout and the backup's
        //   recommendation (_syncOverlay), the build sheet's Length row and
        //   the price (_renderSummary, differential). All plain text writes
        //   into DOM that already exists, so they cost nothing and the
        //   number under the finger answers character by character.
        //
        //   NOT UNTIL YOU'RE DONE — the DRAWN geometry: the band's span,
        //   the backup split, the shortfall frame, and with them the pill's
        //   own re-pinning (its anchor is that moving span). Each of those
        //   is a full svg rebuild plus a forced whole-page layout, and no
        //   schedule made them cheap enough to run while typing — per frame
        //   nor per pause. So they do not run while typing at all (Jerry:
        //   "maybe the redrawing of the diagram should only happen when
        //   done entering the length"); the commit draws them, once.
        //
        // What that costs is the drawn geometry lagging the field while the
        // caret is in it — and it costs nothing at all on the row being
        // typed into, which draws no value while its pill is open (the chip
        // is hidden behind the pill). What lags is everything the length
        // moves AROUND it, until the moment you look up from the number.
        this._afterChange({ light: true, defer: true });
      } else if (t.matches('[data-bc-qty]')) {
        const v = t.value.replace(/[^0-9]/g, '');
        if (v !== t.value) t.value = v;
        this.state.qty = Math.max(1, parseInt(v, 10) || 1);
        // Same two clocks: the price is the thing a quantity keystroke is
        // asked about and recompute() has it instantly, while the drawing
        // (which carries qty only as a multiplier caption) waits for the
        // commit like the length does.
        this._afterChange({ light: true, defer: true });
      } else if (t.matches('[data-bc-loop-name]')) {
        // Track the draft name across panel re-renders (a failed save or
        // Enter-commit rebuilds the panel; the field must not blank).
        this._loopSaveName = t.value;
      } else if (t.matches('[data-bc-opening]') || t.matches('[data-bc-sleeve-length]')) {
        // Keystrokes only shape the digits and track the inches readout —
        // the diagram labels and the URL wait for the commit-time clamp
        // (blur/Enter, _commitBoundedInput), so the scene never echoes an
        // out-of-bounds value mid-entry (Jerry, 2026-07-30). State stays at
        // the last committed value for the same reason.
        const v = t.value.replace(/[^0-9]/g, '');
        if (v !== t.value) t.value = v;
        const num = parseInt(v, 10);
        const opening = t.matches('[data-bc-opening]');
        this._updateDimConv(opening ? '[data-bc-opening-conv]' : '[data-bc-sleeve-conv]',
          Number.isFinite(num) ? num : '');
        // The one thing that DOES react mid-entry: flagging the field when
        // the typed number is outside its range. Shared by both fields —
        // the check is entirely data-min/max driven (see _flagDimBounds).
        this._flagDimBounds(t);
      }
    }

    // Single-comma→dot then digits/dot only, one decimal place — the same
    // sanitizer contract as assets/bc-custom-length.js (comma-decimal
    // locales: stripping the comma would read "1,5" as 15 = a ×10 charge).
    _sanitizeLength(raw) {
      let v = String(raw || '');
      if (v.indexOf(',') !== -1 && v.indexOf('.') === -1 && v.split(',').length === 2) {
        v = v.replace(',', '.');
      }
      v = v.replace(/[^0-9.]/g, '');
      const firstDot = v.indexOf('.');
      if (firstDot !== -1) {
        v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
        v = v.slice(0, firstDot + 2);
      }
      return v;
    }

    _onClick(e) {
      const t = e.target;
      if (t.closest('[data-bc-qty-plus]') || t.closest('[data-bc-qty-minus]')) {
        const delta = t.closest('[data-bc-qty-plus]') ? 1 : -1;
        this.state.qty = Math.min(999, Math.max(1, (parseInt(this.state.qty, 10) || 1) + delta));
        const input = this.querySelector('[data-bc-qty]');
        if (input) input.value = this.state.qty;
        this._afterChange();
      } else if (t.closest('[data-bc-length-done]')) {
        // The pill's visible way out. BEFORE the generic branches, and
        // ahead of the outside-pointerdown close in particular: that
        // listener already skips anything inside the overlay, so Done is
        // the only thing that acts on this click.
        this._closeLengthEditor({ refocus: true });
      } else if (t.closest('[data-bc-zoom-out]')) {
        this._zoomOut();
      } else if (t.closest('[data-bc-panel-save]')) {
        this._savePanel();
      } else if (t.closest('[data-bc-no-loop]')) {
        this._setEndNoLoop();
      } else if (t.closest('[data-bc-add-line]')) {
        const role = t.closest('[data-bc-add-line]').getAttribute('data-bc-add-line');
        if (role === 'backup') this._addBackup();
        else if (role === 'extender') this._addExtender();
        // Adding a line and choosing its webbing is ONE gesture (Jerry,
        // 2026-07-31): a backup lands on the collection's FIRST webbing,
        // which is a placeholder rather than a decision, so the picker
        // opens on it straight away instead of leaving the customer to
        // find the row. The extender mirrors it — its default (the
        // backup's webbing) IS considered, so the modal offers the change
        // rather than demanding it, but the two adds behaving differently
        // would be the surprise. Cancelling keeps the line exactly as it
        // stands — removal is the explicit two-step control on its header,
        // and _closeWebbingPanel touches nothing but state.panel.
        // No `opener` is handed along on purpose — every "add" control only
        // exists while the line doesn't, so the one just clicked is either
        // gone (the summary affordance) or hidden (the stage button) by now.
        // Without one, _closeWebbingPanel lands on the new section's first
        // summary row, which is where this used to send focus outright.
        this._activatePart(this._partId(role, 'webbing'));
      } else if (t.closest('[data-bc-remove-line]')) {
        // The stage row's × and the sheet header's "Remove" are the same
        // control; the wrapper (null in the sheet) is only how focus finds
        // its way back to the one that was pressed.
        const el = t.closest('[data-bc-remove-line]');
        this._askRemoveLine(el.getAttribute('data-bc-remove-line'), el.closest('[data-bc-stage-line]'));
      } else if (t.closest('[data-bc-remove-confirm]')) {
        const el = t.closest('[data-bc-remove-confirm]');
        const role = el.getAttribute('data-bc-remove-confirm');
        const fromStage = !!el.closest('.bc-segment__stage-actions');
        if (role === 'backup') this._removeBackup();
        else if (role === 'extender') this._removeExtender();
        // Same handoff in reverse: the confirm went with the section, so
        // land on the affordance that would put the line back — the stage
        // row's own when that is where the × was, since the sheet may be
        // scrolled away under the pinned diagram. Both roles have one there
        // since 2026-08-02 (the extender's stage entry point); the sheet's
        // remains the fallback for a stage row that isn't in the DOM.
        const back = (fromStage && this.querySelector(
          `.bc-segment__stage-actions [data-bc-add-line="${role}"]:not([hidden])`
        )) || this.querySelector(`[data-bc-summary] [data-bc-add-line="${role}"]`);
        if (back) back.focus();
      } else if (t.closest('[data-bc-remove-cancel]')) {
        this._cancelRemoveLine(t.closest('[data-bc-stage-line]'));
      } else if (t.closest('[data-bc-stitch-toggle]')) {
        // <details> toggles itself; this only MIRRORS the state it is
        // about to land in, so the panel rebuild that every field commit
        // triggers re-renders the disclosure as the customer left it.
        const d = t.closest('details');
        this._stitchOpen = !!(d && !d.open);
      } else if (t.closest('[data-bc-picker-close]')) {
        if (this.state.endView != null) this._zoomOut();
        else this._closeWebbingPanel();
      } else if (t === this.webbingModal) {
        // A click that lands on the DIALOG itself is a click outside its
        // card: the inner .bc-seg__modal-inner covers the whole box, so
        // the only way to hit the dialog is the backdrop around it.
        this._closeWebbingPanel();
      } else if (this.state.panel && e.detail !== 0 && t.closest('.bc-seg__tile')) {
        // Picking a webbing with a pointer IS the whole point of the
        // modal, so it applies and closes in one gesture. Deferred a
        // frame because this delegate sees the LABEL's click before the
        // radio it wraps fires `change` — the apply has to land first.
        // Gated on a real pointer click (detail 0 = keyboard-synthesized):
        // arrow-keying through the radios changes the selection too, and
        // closing the dialog out from under that would make the list
        // impossible to browse. Keyboard users leave by Enter, Save or
        // Escape, like every other panel.
        requestAnimationFrame(() => this._closeWebbingPanel());
      } else if (t.closest('[data-bc-sum-toggle]')) {
        // BEFORE the [data-bc-part] branch: the chevron is a SIBLING of
        // its summary row (not a descendant, so closest() can't reach the
        // row's data-bc-part anyway) — but disclosing dimensions must
        // never also open that end's editor, and the ordering states it.
        this._toggleSumDetail(t.closest('[data-bc-sum-toggle]').getAttribute('data-bc-sum-toggle'));
      } else if (t.closest('[data-bc-part]')) {
        // The clicked control is handed along as the opener, so a modal
        // opened from it returns focus there rather than to a guessed
        // equivalent (see _closeWebbingPanel).
        const btn = t.closest('[data-bc-part]');
        this._activatePart(btn.getAttribute('data-bc-part'), { opener: btn });
      } else if (t.closest('[data-bc-add]')) {
        this._addToCart();
      } else if (t.closest('[data-bc-copy-link]')) {
        this._copyLink();
      } else if (t.closest('[data-bc-loop-preset]')) {
        this._onPresetClick(t.closest('[data-bc-loop-preset]').getAttribute('data-bc-loop-preset'));
      } else if (t.closest('[data-bc-loop-save-open]')) {
        this._loopSaveOpen = true;
        this._renderPicker('[data-bc-loop-name]');
      } else if (t.closest('[data-bc-loop-save-cancel]')) {
        this._loopSaveOpen = false;
        this._loopSaveName = '';
        this._renderPicker('[data-bc-loop-save-open]');
      } else if (t.closest('[data-bc-loop-save]')) {
        this._saveCurrentLoop();
      } else if (t.closest('[data-bc-loop-share]')) {
        this._shareLoop(t.closest('[data-bc-loop-share]').getAttribute('data-bc-loop-share'));
      } else if (t.closest('[data-bc-loop-delete]')) {
        this._deleteLoop(t.closest('[data-bc-loop-delete]').getAttribute('data-bc-loop-delete'));
      } else if (t.closest('[data-bc-loop-banner-save]')) {
        this._saveSharedLoop();
      } else if (t.closest('[data-bc-loop-banner-dismiss]')) {
        this._dismissSharedLoop();
      }
    }

    _changeWebbing(role, key) {
      const prev = this._lineState(role);
      if (!prev || !this.webbings[key]) return;
      const next = this._defaultLine(role, key);
      // Carry over what still applies: the face flip and the disclosure
      // state always; length when the modes still line up; per-end
      // treatment/opening/sleeve when the new webbing offers the same
      // treatment and pattern bounds allow it. (The open panel and the
      // system qty aren't the line's to carry.)
      next.endDetails = prev.endDetails;
      const nextEntry = this._entry(role, key);
      if (prev.lengthMode === 'custom' && nextEntry && nextEntry.custom && prev.length > 0) {
        next.lengthMode = 'custom';
        next.length = prev.length;
      } else if (prev.lengthMode === 'fixed' && this._fixedValues(role, key).includes(prev.fixedValue)) {
        next.fixedValue = prev.fixedValue;
      }
      const ends = this._sewingData(role, key).endTreatments || [];
      END_SLOTS.forEach((end) => {
        const prevItem = (prev.sewing || []).find((it) => it.location && it.location.end === end);
        const nextItem = next.sewing.find((it) => it.location.end === end);
        if (!prevItem || !nextItem) return;
        if (ends.some((tr) => tr.kind === prevItem.treatment)) nextItem.treatment = prevItem.treatment;
        // Pattern: kept only when the new webbing offers the same one —
        // the e1/e2 token and loop-preset policy. Most webbings offer
        // exactly one loop pattern, so this usually falls through to the
        // new webbing's default, which is fine.
        if (prevItem.pattern
          && this._patternsFor(role, 'end_loop', key).some((p) => p.handle === prevItem.pattern)) {
          nextItem.pattern = prevItem.pattern;
        }
        // Opening: a DIALED-IN opening is the customer's and carries over
        // (clamped into the new pattern's bounds); an untouched one keeps
        // TRACKING defaults, so it becomes the new pattern's default
        // rather than freezing the old webbing's number.
        // Bug fixed here (Jerry, 2026-07-31): the switch used to take
        // _defaultSewingItem's opening wholesale, so dialing End 2 to
        // 50 mm and then changing webbing silently threw it away.
        // "Dialed in" is read by VALUE — differs from the default of the
        // pattern it was set against. There is no null "untouched" marker
        // to read: _defaultSewingItem materializes the pattern default
        // into openingMm, and a permalink round-trip writes every opening
        // back out (_buildParams), so a flag couldn't survive either. The
        // one cost is that typing the default by hand reads as untouched
        // — same outcome the customer sees anyway on a webbing whose
        // default matches.
        const nextPattern = this._pattern(nextItem, role, key);
        const nextDefault = nextPattern && nextPattern.openingMm != null ? nextPattern.openingMm : null;
        const prevPattern = this._pattern(prevItem, role, prev.webbing);
        const prevDefault = prevPattern && prevPattern.openingMm != null ? prevPattern.openingMm : null;
        const dialedIn = prevItem.openingMm != null && prevItem.openingMm !== prevDefault;
        if (dialedIn && nextDefault != null) {
          const minB = nextPattern.openingMinMm != null ? nextPattern.openingMinMm : 50;
          const maxB = nextPattern.openingMaxMm != null ? nextPattern.openingMaxMm : 500;
          nextItem.openingMm = Math.min(maxB, Math.max(minB, prevItem.openingMm));
        } else {
          // Includes the new pattern having NO numeric opening: there are
          // no bounds to clamp into and the editor offers no opening
          // field, so the carried number would be unreachable data (same
          // call _applyLoopPreset makes).
          nextItem.openingMm = nextDefault;
        }
        nextItem.sleeveLengthMm = prevItem.sleeveLengthMm;
        // Which face the loop folds outward carries over — but only into a
        // webbing that HAS a face choice; on one that doesn't, the field
        // goes back to its meaningless default rather than smuggling the
        // old webbing's decision through (see _lineFaces).
        nextItem.faceOut = this._flippable(nextEntry) ? (prevItem.faceOut || 'top') : 'top';
        // The opening decides what the carried sleeve is allowed to be, so
        // this runs LAST — before the carry-over it judged the new
        // webbing's default instead of the opening actually in effect.
        this._enforceSleeveRule(nextItem, role, key);
      });
      this.state.lines[role] = next;
      // ALL of the switch's DOM work lands in ONE coalesced frame — the
      // change handler itself only swaps state. Writing DOM synchronously
      // here made every OTHER change/click listener on the page (Horizon
      // globals, Shopify feature scripts) that reads layout pay a full
      // forced style recalc mid-dispatch, which is what froze the page on
      // tile clicks. The rAF also coalesces rapid arrow-key traversal
      // (one change event per step) into a single render.
      if (this._switchRaf) cancelAnimationFrame(this._switchRaf);
      this._switchRaf = requestAnimationFrame(() => {
        this._switchRaf = null;
        // Targeted picker update (active tile + flip row) — focus never
        // leaves the clicked radio, so arrow-key traversal keeps working
        // with no re-focus needed.
        this._updatePickerForWebbing(role);
        // The length pill is entry-specific — the select is the ENTRY's
        // own fixed lengths, and whether there is a custom input at all is
        // the entry's too — so a switch that changes them needs it rebuilt
        // (if one is open at all; the picker is a modal, so it usually is
        // not). recompute() → _syncOverlay does it, through the
        // structural-drift branch: the tile radio holds focus, not the
        // overlay, so the rebuild is allowed and can't eat a caret. The
        // ft/m readout is synced by the same call.
        this.recompute();
        this._renderScene();
        // Debounced like the keystroke path: replaceState is a style-flush
        // point (:target invalidation), so running it here — right after
        // the svg rebuild dirtied the tree — paid a full synchronous
        // recalc inside the frame. From the idle timer the tree is clean
        // and it costs ~nothing.
        this._scheduleUrl();
      });
    }

    // THREE draw schedules, and which one a change gets is about how fast
    // the drawing has to answer it, not about what the change is:
    //
    //   full (default)   — draw NOW. Structural changes the customer is
    //                      waiting to see (a webbing switch, a treatment,
    //                      a preset, a line added or removed).
    //   { light: true }  — draw NEXT FRAME. A stream of values arriving
    //                      faster than frames, where the drawing must still
    //                      track the hand: the diagram dimension drags, and
    //                      the field commits (blur/Enter), which are one
    //                      event but land inside a gesture.
    //   { light: true,
    //     defer: true }  — don't draw at all yet: mark the scene DIRTY and
    //                      let the END of the interaction draw it, once.
    //                      Typing. See the deferral block at the head of
    //                      this file for why.
    //
    // NOT called `settle`: nothing settles on a timer here. A deferred draw
    // has no scheduled moment at all — it waits for a commit, a close, an
    // add, or any full render, and it will wait indefinitely if none of
    // those ever comes (which is Jerry's call: "the redrawing of the
    // diagram should only happen when done entering the length"). The word
    // would also collide with the scene's own `scene-rendered` SETTLE
    // handler, which is a different thing on the same page.
    //
    // `defer` implies `light` — it is a longer wait, not a different kind
    // of one — and the two are mutually exclusive: whichever was asked for
    // last is the one that fires, so a commit arriving mid-typing spends
    // the dirty flag on a next-frame draw rather than queueing behind it.
    _afterChange(opts = {}) {
      this.recompute();
      if (opts.light) {
        if (opts.defer) this._deferSceneDraw();
        else this._scheduleSceneFrame();
        // history.replaceState is a documented style-flush point, so it is
        // debounced on every deferred path — running it inside the gesture,
        // right after the svg rebuild dirtied the tree, paid a full
        // synchronous recalc for a value nobody reads until the customer
        // shares or refreshes the page.
        if (opts.writeUrl !== false) this._scheduleUrl();
        return;
      }
      // A pending deferred draw is DISCARDED rather than drawn twice —
      // _renderScene clears the flag at its head, since the render below
      // draws the same state anyway. Same rule for the URL write, which has
      // no such hook and so cancels its timer here by hand.
      this._renderScene();
      if (opts.writeUrl !== false) {
        clearTimeout(this._urlTimer);
        this._urlTimer = null;
        this._writeUrl();
      }
    }

    _scheduleSceneFrame() {
      // A frame draw satisfies the dirty flag — it IS the owed draw.
      this._sceneDirty = false;
      if (this._sceneRaf) return;
      this._sceneRaf = requestAnimationFrame(() => {
        this._sceneRaf = null;
        this._renderScene();
      });
    }

    // No timer, deliberately: a trailing debounce still drew on every
    // typing PAUSE, and each of those is a full svg rebuild plus the
    // scene-rendered handler's forced page layout (Jerry, 2026-08-02, Mac
    // Firefox: "its still slow, now while inside the editor" → "maybe the
    // redrawing of the diagram should only happen when done entering the
    // length"). So this records that a draw is OWED and names no moment for
    // it; the moment is whichever interaction end arrives first (see
    // _flushScene).
    //
    // A frame-scheduled draw already owed is cancelled rather than left to
    // fire — the same reasoning one level up: it would land mid-word.
    _deferSceneDraw() {
      if (this._sceneRaf) { cancelAnimationFrame(this._sceneRaf); this._sceneRaf = null; }
      this._sceneDirty = true;
    }

    _cancelSceneDraw() {
      if (this._sceneRaf) { cancelAnimationFrame(this._sceneRaf); this._sceneRaf = null; }
      this._sceneDirty = false;
    }

    // Draw NOW whatever was owed, and say whether anything was. This is the
    // guarantee behind the dirty flag: no path that ENDS an interaction may
    // leave the drawing a keystroke behind the build. Callers are every way
    // the typing can be over — the field committing, the pill closing,
    // add-to-cart — plus _renderScene itself, which cancels at its head so
    // any full render anywhere spends the flag too.
    _flushScene() {
      const owed = !!(this._sceneRaf || this._sceneDirty);
      this._cancelSceneDraw();
      if (owed) this._renderScene();
      return owed;
    }

    // The id is cleared BY the timer as well as by the cancel, so
    // `_urlTimer` means "a write is still owed" rather than "a write was
    // scheduled at some point" — which is what _flushUrl tests.
    _scheduleUrl() {
      clearTimeout(this._urlTimer);
      this._urlTimer = setTimeout(() => {
        this._urlTimer = null;
        this._writeUrl();
      }, 250);
    }

    // Same contract as _flushScene, for the address bar: the permalink must
    // be current before anything reads the page as a shareable state.
    _flushUrl() {
      if (!this._urlTimer) return;
      clearTimeout(this._urlTimer);
      this._urlTimer = null;
      this._writeUrl();
    }

    // The open pill is centred on an anchor the scene publishes, so it has
    // to be re-pinned whenever either moves — but _positionOverlay reads
    // getBoundingClientRect three times, which on this page is a forced
    // synchronous layout of the whole document. Run from a rAF it lands
    // after the frame's style work instead of in the middle of it, and
    // repeated calls in one turn collapse into one pass.
    //
    // NOT used by _renderOverlay: a freshly built pill has no position at
    // all, so deferring there would paint it in the stage's corner for a
    // frame.
    _scheduleOverlayPosition() {
      // Nothing open is the common case, and it costs a frame to discover
      // inside _positionOverlay — decide it here instead.
      if (!this._lengthOpen || this._posRaf) return;
      this._posRaf = requestAnimationFrame(() => {
        this._posRaf = null;
        this._positionOverlay();
      });
    }

    _reduceMotion() {
      return typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // ---- scene ------------------------------------------------------------

    // The builder → scene contract: a `lines` array (one per role, in draw
    // order) plus the whole-build values. The scene reaches into nothing —
    // everything it draws comes from here.
    //
    // The MAINLINE's fields are ALSO mirrored flat (entry / lengthText /
    // end1 / end2), because the scene still draws from that shape; it
    // moves to `lines`, and the mirror goes with it. Same reason
    // `view` and `activePart` carry bare part ids for the mainline: a bare
    // id IS the mainline's id (see _partId), so both spellings stay valid
    // at the boundary either way.
    _sceneModel() {
      const lines = ROLES.map((role) => this._sceneLine(role)).filter(Boolean);
      const main = lines.find((l) => l.role === 'main');
      const model = {
        lines,
        qty: Math.max(1, parseInt(this.state.qty, 10) || 1),
        view: this._sceneView(),
        activePart: this.state.panel
          ? this._partId(this.state.panel.role, this.state.panel.part)
          : null,
        recommendation: this._recommendation(),
        // There IS an overlay layer for a pill to open into, so every
        // dimension's value draws as a tappable chip. False when the
        // section rendered no [data-bc-overlay] — the scene then draws the
        // same numbers as plain text with no target, which is what any
        // future embed of the element gets for free.
        lengthEditors: !!this.overlayHost,
        // ...and WHICH row's pill is open, by anchor name. That row draws
        // no value: the pill holds it. Transient UI, so it is read off the
        // instance field rather than `state` — nothing here reaches the URL
        // or the cart.
        lengthOpen: this._lengthOpen ? this._partId(this._lengthOpen, 'length') : null,
      };
      if (main) {
        model.entry = main.entry;
        model.lengthText = main.lengthText;
        model.end1 = main.end1;
        model.end2 = main.end2;
      }
      return model;
    }

    _sceneLine(role) {
      const line = this._lineState(role);
      if (!line) return null;
      // ONE derivation per line, shared by the entry resolution and every
      // end below — which face is up, which way each loop folds, and which
      // face a zoom into it must show are all answers to the same question
      // (see _lineFaces). _effectiveEntry is deliberately not reused here:
      // it would re-derive.
      const faces = this._lineFaces(role);
      const raw = this._entry(role);
      const other = faces.mirrorable ? this._flipEntry(raw, this._mirrorFace(raw)) : null;
      const entry = faces.lineTop === 'bottom' ? other : raw;
      // The line drawn the other way up. A ZOOM into an end whose chosen
      // outside face disagrees with the line's top (end.zoomFlipped) draws
      // from this instead — the full view shows the piece as RIGGED, a
      // zoom shows one loop as PRESENTED, and on a backup those differ
      // even with nothing touched. Absent entirely when the webbing has no
      // second face, which is the scene's signal that there is no
      // alternative to reach for.
      const entryFlipped = faces.mirrorable
        ? (faces.lineTop === 'bottom' ? raw : other)
        : null;
      const ends = {};
      END_SLOTS.forEach((end) => {
        const item = this._endItem(role, end);
        if (!item) { ends[`end${end}`] = null; return; }
        // Treatment 'none' still ships an end object — the scene renders the
        // ghost "add a loop" affordance (full view) / bare cut end (zoom).
        ends[`end${end}`] = {
          treatment: item.treatment,
          foldLiveMm: this._foldLiveMm(item, role),
          openingMm: item.openingMm,
          sleeveLengthMm: item.sleeveLengthMm,
          pattern: this._pattern(item, role),
          // Which side of the line this loop's TAIL sits on — derived, not
          // chosen: it follows from the loop's outside face and the line's
          // resulting orientation (_lineFaces). The scene draws what it is
          // told; every face decision stays on this side of the boundary.
          tailUp: faces.tailUp[end],
          // ...and whether a zoom into THIS end should compose from the
          // line's `entryFlipped` rather than its `entry`.
          zoomFlipped: faces.zoomFlipped[end],
          // Whether a sleeve COULD be added here — the gate for the zoom
          // view's Add-sleeve button, precomputed because every term of it
          // is builder-side knowledge (the sleeve floor, and whether this
          // webbing offers a sleeved treatment at all). Exactly the checks
          // _onEndAction re-runs before honouring the button, so the scene
          // can't offer an action this side would refuse.
          sleeveAvailable: item.treatment !== 'none'
            && this._sleeveAllowed(item, role)
            && (this._sewingData(role).endTreatments || []).some((t) => t.kind === 'sleeved'),
        };
      });
      const out = {
        role,
        entry,
        // The line's length as its own editor states it. This is what the
        // scene DRAWS in that line's dimension since 2026-08-02 (it was a
        // fallback for the split arithmetic before, with the value itself
        // being the HTML editor overlaid on the row), so it moves with
        // every keystroke and the drawing moves with it.
        lengthText: this._lengthTextFor(role),
        // The same length as a NUMBER in metres, whatever unit the entry
        // sells in — the scene splits the backup band proportionally
        // between the backup and the extender, and parsing lengthText is
        // only its fallback.
        lengthM: this._lineLengthM(role),
        end1: ends.end1,
        end2: ends.end2,
      };
      // Only where there IS one — absence is the contract, so the scene
      // never has to distinguish "no other face" from "same face twice".
      if (entryFlipped) out.entryFlipped = entryFlipped;
      return out;
    }

    _renderScene() {
      // ANY real render supersedes a deferred one — the invariant lives
      // here rather than at the dozen call sites, so nothing can be added
      // later that forgets it.
      //
      // It is not merely tidiness: scene.render() begins by CANCELLING an
      // in-flight viewBox tween, so a draw owed from a keystroke and left
      // to fire during a zoom would turn the fly-in into a jump cut. With
      // the rAF path that window was one frame; with the dirty flag it is
      // open-ended — long enough to type a length and then tap an end —
      // which is exactly why spending it HERE, rather than at each call
      // site, is what makes the deferral safe.
      this._cancelSceneDraw();
      if (this.scene && typeof this.scene.render === 'function') {
        try { this.scene.render(this._sceneModel()); } catch (e) { console.error('[bc-segment] scene render', e); }
      }
      this._syncZoomButton();
    }

    // Tell the scene which row's pill is open WITHOUT rebuilding the
    // drawing (2026-08-02, Jerry on Staging: "the custom length loads very
    // slowly with this new setup").
    //
    // Opening the pill was a model change, and every model change is a
    // full _draw: svg.replaceChildren() and then every piece's paths,
    // gradients, loops and hotspots re-created — the same work the
    // keystroke path is careful to coalesce to one a frame — spent to stop
    // drawing one number. And the number is the ONLY difference: the row
    // layout, the bands, the scales and the anchor the pill is pinned to
    // are all computed without reference to it, so the draw it triggers
    // cannot move anything the pill depends on. The scene draws both
    // states now and toggles a class between them (see its setLengthOpen).
    //
    // Falls back to a real render when the scene can't do it in place —
    // nothing drawn yet, an older scene element without the method, or a
    // view with no length rows — so the two paths can never disagree.
    _setSceneLengthOpen() {
      const s = this.scene;
      const name = this._lengthOpen ? this._partId(this._lengthOpen, 'length') : null;
      if (s && typeof s.setLengthOpen === 'function') {
        let ok = false;
        try { ok = s.setLengthOpen(name); } catch (e) { console.error('[bc-segment] scene lengthOpen', e); }
        // No _syncZoomButton here, unlike _renderScene: it mirrors
        // state.endView, which a pill opening or closing cannot move (the
        // two are mutually exclusive, and the paths that leave an end view
        // render properly on the way out).
        if (ok) return;
      }
      this._renderScene();
    }

    _syncZoomButton() {
      const btn = this.querySelector('[data-bc-zoom-out]');
      if (btn) btn.hidden = this.state.endView == null;
    }

    // ---- sticky stage + compact-on-scroll ---------------------------------

    _observeStage() {
      const stage = this.stage;
      if (!stage) return;
      const setH = () => {
        this.style.setProperty('--bc-segment-stage-h', `${Math.round(stage.getBoundingClientRect().height)}px`);
      };
      setH();
      if (typeof ResizeObserver === 'function') {
        this._stageRO = new ResizeObserver(setH);
        this._stageRO.observe(stage);
      } else if (typeof window !== 'undefined') {
        this._stageResize = setH;
        window.addEventListener('resize', setH);
      }
    }

    // Compact-on-scroll, and re-pinning an open pill through it. Both are
    // needed, and the svg's own transitionend as well: the compact
    // max-height transition RESCALES the drawing with no scene re-render at
    // all, so the anchor moves under a pill that nothing else would have
    // repositioned — mid-transition on every frame the rAF catches, and
    // once more at the end for the value it actually lands on.
    _observeScroll() {
      if (typeof window === 'undefined') return;
      let ticking = false;
      this._scrollFn = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.classList.toggle('bc-segment--compact', this.getBoundingClientRect().top < -40);
          this._positionOverlay();
        });
      };
      window.addEventListener('scroll', this._scrollFn, { passive: true });
      window.addEventListener('resize', this._scrollFn, { passive: true });
      const svg = this.scene && this.scene.querySelector('svg');
      if (svg) svg.addEventListener('transitionend', () => this._positionOverlay());
      this._scrollFn();
    }

    // ---- URL state --------------------------------------------------------

    // ?w=<entryKey>&mode=fixed|custom&val=<fixedValue>&len=<n>&q=<n>
    // &e1=<treatment>.<patternHandle>.<openingMm>[.<sleeveMm>][.fb]&e2=...
    // The optional trailing .ft/.fb is that LOOP's outside face, written
    // only when the webbing offers a choice and the choice isn't the
    // default. The old line-level &f=b is no longer written; it still
    // parses, as both ends folding bottom-face-out.
    // The backup and extender repeat the PER-LINE params under their role
    // prefix — bw/bmode/blen/bval/bf/be1/be2 and xw/… — so the mainline's
    // keep the names every permalink and cart Build property already in
    // the wild was written with. `q` is NOT per line: it's how many of the
    // whole system are being ordered, so there is exactly one. Presence of
    // `bw` is what declares a backup; `xw` without it is dropped (an
    // extender extends the backup, so it can't stand alone).
    // (endView/picker are ephemeral UI state and stay out of the permalink.)
    _applyUrl() {
      const params = new URLSearchParams(location.search);
      // Cart-edit handoff: &edit=<buildId|line key> is written ONLY by the
      // cart's Edit segment link, never by _buildParams (permalinks stay
      // clean). A 'bld-' value is a BUILD id and addresses every cart line
      // carrying it; anything else is a single line key (a solo build, and
      // every line written before backups existed). The prefix is the whole
      // of the discrimination — line keys are '<variantId>:<hash>', so the
      // two can't be confused.
      this._editKey = params.get('edit') || null;
      this._editBuildId = this._editKey && this._editKey.startsWith('bld-') ? this._editKey : null;

      ROLES.forEach((role) => {
        const key = params.get(this._param(role, 'w'));
        if (role === 'main') {
          if (key && this.webbings[key]) this.state.lines.main = this._defaultLine('main', key);
        } else {
          if (!key || !this.webbings[key]) return;
          if (role === 'extender' && !this._lineState('backup')) return;
          this.state.lines[role] = this._defaultLine(role, key);
        }
        this._applyLineParams(role, params);
      });

      const q = parseInt(params.get('q'), 10);
      if (q > 1) this.state.qty = Math.min(999, q);

      // Shared loop design: ?loop=<treatment>.<pattern>.<opening>[.<sleeve>]
      // (+ &loopname=). Applied to BOTH ends AFTER e1/e2 (an explicit share
      // wins over stale end tokens in a hand-edited URL) through the same
      // offered/bounds policy (_applyLoopPreset). The token is kept for
      // _writeUrl so a refresh keeps the banner until saved/dismissed.
      const loopTok = params.get('loop');
      if (loopTok) {
        const bits = loopTok.split('.');
        // Same trailing 'ft'/'fb' segment the e-tokens use, so a shared
        // design carries the face it was saved with and older share links
        // (which have none) parse unchanged.
        let loopFace = null;
        if (bits.length > 1 && /^f[tb]$/.test(bits[bits.length - 1])) loopFace = bits.pop();
        const treatment = bits[0] || '';
        if (/^[a-z0-9_-]{1,32}$/.test(treatment) && treatment !== 'none') {
          const int = (v) => {
            const n = parseInt(v, 10);
            return Number.isFinite(n) && n >= 0 ? n : null;
          };
          const name = (params.get('loopname') || '').replace(/\s+/g, ' ').trim().slice(0, 60);
          this._sharedLoop = {
            name: name || 'Shared loop',
            treatment,
            pattern: bits[1] || null,
            openingMm: int(bits[2]),
            sleeveLengthMm: int(bits[3]),
            faceOut: loopFace ? (loopFace === 'fb' ? 'bottom' : 'top') : null,
          };
          this._sharedLoopToken = { loop: loopTok, loopname: name };
          // Mainline scope: a shared loop is an END design, and the ends the
          // link is offering to fill are the ones on screen when it opens.
          END_SLOTS.forEach((end) => this._applyLoopPreset('main', end, this._sharedLoop));
        }
      }
    }

    // One role's param name. The mainline's prefix is empty, which is the
    // whole of the back-compat story.
    _param(role, name) {
      return `${ROLE_PREFIX[role] || ''}${name}`;
    }

    // Restore ONE line from its own prefixed params. Every value passes the
    // same offered/bounds policy the editors enforce — a permalink is a
    // boundary, not a trusted state dump.
    _applyLineParams(role, params) {
      const line = this._lineState(role);
      if (!line) return;
      const get = (name) => params.get(this._param(role, name));
      const entry = this._entry(role);

      if (get('mode') === 'custom' && entry && entry.custom) {
        line.lengthMode = 'custom';
        const len = Number(get('len'));
        if (len > 0) line.length = len;
        else if (!(line.length > 0)) line.length = 1;
      } else if (params.has(this._param(role, 'val')) && this._fixedValues(role).includes(get('val'))) {
        line.lengthMode = 'fixed';
        line.fixedValue = get('val');
      }

      // LEGACY line-level flip. Orientation moved onto the loops
      // (_lineFaces), so the closest honest reading of "this whole line was
      // flipped" is both of its ends folding their bottom face outward.
      // Applied BEFORE the e-tokens so an explicit per-end face wins — the
      // two can only coexist in a hand-edited URL, but the precedence
      // should still be the more specific one.
      if (get('f') === 'b' && this._flippable(entry)) {
        END_SLOTS.forEach((end) => {
          const item = this._endItem(role, end);
          if (item) item.faceOut = 'bottom';
        });
      }

      END_SLOTS.forEach((end) => {
        const token = get(`e${end}`);
        if (!token) return;
        const item = this._endItem(role, end);
        if (!item) return;
        const bits = token.split('.');
        // The face rides as an optional TRAILING segment ('ft'/'fb'). Taken
        // off the end before anything positional is read, so every token
        // written before orientation existed parses exactly as it always
        // did — and a numeric sleeve can never be mistaken for it.
        let faceBit = null;
        if (bits.length > 1 && /^f[tb]$/.test(bits[bits.length - 1])) faceBit = bits.pop();
        if (faceBit && this._flippable(entry)) {
          item.faceOut = faceBit === 'fb' ? 'bottom' : 'top';
        }
        const ends = this._sewingData(role).endTreatments || [];
        const kinds = ends.length ? ends.map((t) => t.kind) : ['naked', 'sleeved', 'none'];
        if (kinds.includes(bits[0])) item.treatment = bits[0];
        if (bits[1] && this._patternsFor(role, 'end_loop').some((p) => p.handle === bits[1])) {
          item.pattern = bits[1];
          const p = this._pattern(item, role);
          item.openingMm = p && p.openingMm != null ? p.openingMm : null;
        }
        const opening = parseInt(bits[2], 10);
        if (Number.isFinite(opening)) {
          const p = this._pattern(item, role);
          // Same editability + bounds policy as the opening input.
          if (p && p.openingMm != null) {
            const minB = p.openingMinMm != null ? p.openingMinMm : 50;
            const maxB = p.openingMaxMm != null ? p.openingMaxMm : 500;
            item.openingMm = Math.min(maxB, Math.max(minB, opening));
          }
        }
        const sleeve = parseInt(bits[3], 10);
        if (Number.isFinite(sleeve) && sleeve > 0) {
          // Same bounds as the input — the share URL is a boundary too
          // (opening-driven cap included; opening was parsed above).
          const { min: minS, max: maxS } = this._sleeveBounds(item, role);
          item.sleeveLengthMm = Math.min(maxS, Math.max(minS, sleeve));
        }
        // A crafted/stale URL can still pair sleeved with a sub-50 opening
        // — normalize it away like every other entry point.
        this._enforceSleeveRule(item, role);
      });
    }

    _buildParams() {
      const params = new URLSearchParams();
      // `?view=` is Shopify's alternate-template selector — how the builder
      // is mounted while it has no dedicated page (e.g.
      // /pages/faq?view=segment-builder). It's part of the page's ADDRESS,
      // so permalinks, the cart Build property, and the URL sync must all
      // carry it or they resolve to the page's default template. Owned
      // params are rebuilt from state; other foreign params (utm junk,
      // preview ids) are deliberately dropped.
      const current = new URLSearchParams(location.search);
      if (current.has('view')) params.set('view', current.get('view'));
      // Roles in ROLES order, mainline first — which is what keeps a
      // mainline-only build's query string byte-identical to the one this
      // page wrote before backups existed.
      ROLES.forEach((role) => this._writeLineParams(role, params));
      return params;
    }

    _writeLineParams(role, params) {
      const line = this._lineState(role);
      if (!line) return;
      const name = (n) => this._param(role, n);
      params.set(name('w'), line.webbing);
      if (line.lengthMode === 'custom') {
        params.set(name('mode'), 'custom');
        if (line.length > 0) params.set(name('len'), String(line.length));
      } else if (line.fixedValue) {
        params.set(name('val'), line.fixedValue);
      }
      // The SYSTEM quantity, written in the mainline's slot: one build, one
      // q, and emitting it here keeps the pre-backup param ORDER exact.
      if (role === 'main' && this.state.qty > 1) params.set('q', String(this.state.qty));
      // No line-level `f` any more — orientation is per loop and rides the
      // e-tokens below. (Legacy `f=b` still PARSES; see _applyLineParams.)
      const flippable = this._flippable(this._entry(role));
      END_SLOTS.forEach((end) => {
        const item = this._endItem(role, end);
        if (!item) return;
        const bits = [item.treatment];
        if (item.treatment !== 'none') {
          bits.push(item.pattern || '');
          bits.push(item.openingMm != null ? String(item.openingMm) : '');
          if (item.treatment === 'sleeved') bits.push(String(item.sleeveLengthMm || ''));
          // Only when there is a choice AND it isn't the default, so a
          // build that never touched orientation writes the byte-identical
          // token it wrote before this existed.
          if (flippable && item.faceOut === 'bottom') bits.push('fb');
        }
        params.set(name(`e${end}`), bits.join('.'));
      });
    }

    _currentUrl() {
      return `${location.origin}${location.pathname}?${this._buildParams().toString()}`;
    }

    _writeUrl() {
      const params = this._buildParams();
      // Keep the edit key in the ADDRESS BAR (a mid-edit refresh stays in
      // edit mode) even though permalinks/Build properties never carry it.
      if (this._editKey) params.set('edit', this._editKey);
      // Same for an unsaved shared loop — the banner survives a refresh.
      if (this._sharedLoopToken) {
        params.set('loop', this._sharedLoopToken.loop);
        if (this._sharedLoopToken.loopname) params.set('loopname', this._sharedLoopToken.loopname);
      }
      try {
        history.replaceState(null, '', `${location.pathname}?${params.toString()}`);
      } catch (_e) { /* replaceState can throw in sandboxed contexts; ignore */ }
    }

    _copyLink() {
      const url = this._currentUrl();
      const done = () => this._flash('[data-bc-copy-link]', 'Link copied!');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done).catch(() => this._flash('[data-bc-copy-link]', url));
      } else {
        this._flash('[data-bc-copy-link]', url);
      }
    }

    // ---- cart edit mode ----------------------------------------------------

    // The qty input is static section markup (value="1") — reflect state
    // into it whenever qty changes programmatically (permalink q= restore,
    // cart-line sync); the stepper/input handlers cover user edits.
    _syncQtyInput() {
      const input = this.querySelector('[data-bc-qty]');
      if (input) input.value = String(this.state.qty || 1);
    }

    _applyEditMode() {
      if (!this._editKey) return;
      // The LABEL span, never the button: the button also holds the
      // checkmark-burst markup the added animation plays through, and
      // textContent on the button would delete it.
      const btn = this.querySelector('[data-bc-add-label]');
      // Remember what the button said before edit mode renamed it, so
      // _leaveEditMode can put it back rather than hardcoding a second copy
      // of a label that lives in the section markup.
      if (btn) {
        this._addLabelDefault = btn.textContent;
        btn.textContent = 'Save Segment';
      }
      const note = this.querySelector('[data-bc-edit-note]');
      if (note) note.hidden = false;
    }

    // Step OUT of edit mode, back to being a plain builder (Jerry,
    // 2026-08-01). Reached when the cart lines this edit addresses are all
    // gone — the customer emptied the segment out of the cart and then came
    // back through history, so there is nothing to "save" onto. Leaving the
    // Save Segment button and the "saving updates that cart item" banner in
    // place would be a promise about a cart item that no longer exists;
    // _saveEdit's own demote-to-plain-add fallback stays for the race where
    // they vanish between load and save. Callers must _afterChange()
    // afterwards so _writeUrl drops the now-meaningless &edit.
    _leaveEditMode() {
      if (!this._editKey) return;
      this._editKey = null;
      this._editBuildId = null;
      const btn = this.querySelector('[data-bc-add-label]');
      if (btn && this._addLabelDefault) btn.textContent = this._addLabelDefault;
      const note = this.querySelector('[data-bc-edit-note]');
      if (note) note.hidden = true;
    }

    // Say — on the edit banner already on screen — what the reconcile did.
    // Sibling of _noteQtyDesync and deliberately its own element, so a build
    // that drifted BOTH ways reports both. Same "nothing is wrong, nothing
    // is blocked" register: these are consequences of the customer's own
    // cart edits, not errors.
    _noteCartDrift(messages) {
      const note = this.querySelector('[data-bc-edit-note]');
      if (!note || !messages.length || note.querySelector('[data-bc-edit-drift]')) return;
      const warn = document.createElement('span');
      warn.className = 'bc-segment__edit-warn';
      warn.setAttribute('data-bc-edit-drift', '');
      warn.textContent = messages.join(' ');
      note.appendChild(warn);
    }

    // The LINES are the truth about which pieces this build still has
    // (Jerry, 2026-08-01: "I removed an extender in the cart, but the
    // extender is still present when I click edit segment"). The Build
    // permalink every line carries was written at ADD time and describes
    // the whole build as it was then; removing one line in the cart doesn't
    // touch its siblings' copy of it, so _applyUrl faithfully rebuilds a
    // piece the customer has already thrown away — and saving would put it
    // straight back.
    //
    // So after the permalink has been parsed, drop every role whose cart
    // line is gone. Three rules decide the degenerate cases:
    //
    //  - The MAINLINE is never dropped. The model has no build without one
    //    (_lineSet's own comment: "unreachable while the mainline is always
    //    present"), and there is no remove control for it in the UI either.
    //    If its line is the one that went, the permalink's mainline stands
    //    and the notice says saving will add it back — which is true, and
    //    is better than silently re-adding it or refusing to open.
    //  - The EXTENDER goes wherever the BACKUP goes (contract C3, the same
    //    rule _removeBackup and _applyUrl already enforce): an extender
    //    extends the backup, so a cart holding an orphaned extender line is
    //    a state the builder cannot represent. Saving then removes that
    //    orphan, which is why it is called out separately in the notice.
    //  - Anything dropped closes whatever editor was pointing at it, exactly
    //    as the in-builder remove path does.
    //
    // Returns the roles it dropped. Callers re-render; _writeUrl then
    // rewrites the address bar without the dropped roles' tokens, so a
    // mid-edit refresh keeps the reconciled build.
    _reconcileRolesFromCart(items) {
      const inCart = new Set(items.map((it) => this._roleFromCartItem(it)));
      const messages = [];
      const dropped = [];
      const backupGone = !!this._lineState('backup') && !inCart.has('backup');
      if (backupGone) dropped.push('backup');
      if (this._lineState('extender') && (backupGone || !inCart.has('extender'))) {
        dropped.push('extender');
      }
      if (backupGone) {
        messages.push(
          dropped.includes('extender') && inCart.has('extender')
            ? 'The backup line is no longer in your cart, so it has been dropped from this build — and its extender goes with it, since an extender extends the backup.'
            : 'The backup line is no longer in your cart, so it has been dropped from this build.'
        );
      } else if (dropped.includes('extender')) {
        messages.push('The extender is no longer in your cart, so it has been dropped from this build.');
      }
      if (!inCart.has('main')) {
        messages.push('The mainline is no longer in your cart — saving this segment will add it back.');
      }
      dropped.forEach((role) => { this.state.lines[role] = null; });
      if (dropped.length) this._closeViewsFor(dropped);
      this._noteCartDrift(messages);
      return dropped;
    }

    // The build's cart lines disagree about how many systems are on order
    // (someone edited ONE line's pieces in the cart). There is exactly one
    // system qty here, so saving will set them all to it — which is a fine
    // resolution, but not one to spring on anyone silently. Rides the edit
    // banner that is already on screen rather than the error slot: nothing
    // is wrong and nothing is blocked. Written once (the seed runs once per
    // load), and the guard keeps it that way if that ever changes.
    _noteQtyDesync(qty) {
      const note = this.querySelector('[data-bc-edit-note]');
      if (!note || note.querySelector('[data-bc-edit-warn]')) return;
      const warn = document.createElement('span');
      warn.className = 'bc-segment__edit-warn';
      warn.setAttribute('data-bc-edit-warn', '');
      warn.textContent = `Quantities differ between the pieces of this build — saving sets them all to ${qty}.`;
      note.appendChild(warn);
    }

    // Which line of a build a cart item is. The visible role property is
    // the only marker, and only multi-line builds carry it — an item
    // without one is the mainline, which is exactly what a solo build's
    // single line is.
    _roleFromCartItem(item) {
      const label = String(((item && item.properties) || {})['Segment Piece'] || '').toLowerCase();
      return ROLES.find((role) => ROLE_LABELS[role].toLowerCase() === label) || 'main';
    }

    // The cart lines this edit addresses: every line of the build when the
    // edit key is a build id, else the single line with that key.
    //
    // THREE-VALUED, deliberately (review 2026-08-01): [] means the cart was
    // read successfully and holds nothing this edit addresses (removed in
    // another tab, stale back-button edit) — "nothing to replace". null
    // means the cart could not be READ at all: offline at load, a network
    // blip, a 429 from a burst of cart traffic. Collapsing the two, which
    // this did until now, let one failed /cart.js speak for the cart's
    // CONTENTS — _syncFromCartLine took it as "the build is gone" and left
    // edit mode for good, and the next Save Segment then plain-added a
    // second copy of a build whose lines had been sitting in the cart the
    // whole time. Callers must treat null as "don't know" and do nothing.
    async _editTargets() {
      if (!this._editKey) return [];
      let items;
      try {
        const cart = await fetch('/cart.js').then((r) => (r.ok ? r.json() : null));
        // A non-2xx response is a failed READ, not an empty cart — a real
        // empty cart is a 200 with items: [].
        if (!cart) return null;
        items = cart.items || [];
      } catch (_e) { return null; }
      return items.filter((it) => this._isEditTarget(it));
    }

    // The same match as a PREDICATE over a cart item, for the paths that
    // already hold a cart and must not read another (the add path's stock
    // gate, which discounts the very lines the save is about to remove).
    // Shared with _editTargets so the two can't drift apart.
    _isEditTarget(item) {
      if (!this._editKey) return false;
      if (this._editBuildId) return ((item.properties || {})._build_id === this._editBuildId);
      return item.key === this._editKey;
    }

    // Edit mode opens from the cart's Build permalink, but the LINES are the
    // truth for qty/length: they can drift after the permalink was written
    // (the native quantity stepper on fixed-length lines rewrites nothing,
    // the cart's own length/pieces editors write only the line they're on,
    // and any future cart writer can miss the Build q=/len= mirror). On the
    // real cart→builder handoff (nav type 'navigate') read the addressed
    // lines and override PER ROLE, then _writeUrl the corrected values so a
    // refresh keeps them; on reload/back-forward the URL carries
    // IN-PROGRESS edit state and must win (the mid-edit-refresh contract in
    // the header).
    //
    // Since 2026-08-01 the lines are also the truth about WHICH PIECES the
    // build still has, not just their sizes — see _reconcileRolesFromCart.
    // The two overrides run in that order on purpose: reconcile first, then
    // read qty/length off the roles that survived, so a dropped role never
    // contributes a quantity claim to a build it is no longer part of.
    async _syncFromCartLine() {
      if (!this._editKey) return;
      const nav = (performance.getEntriesByType
        && performance.getEntriesByType('navigation')[0]) || null;
      if (nav && nav.type !== 'navigate') return;
      const items = await this._editTargets();
      // The cart could not be read (see _editTargets). Nothing is known
      // about the lines, so nothing is decided about them: edit mode stands
      // and the reconcile is skipped, leaving the permalink's own build on
      // screen. The save path re-reads and surfaces its own error if the
      // cart is still unreachable then.
      if (!items) return;
      if (!items.length) {
        // The whole build is gone from the cart (removed in another tab, or
        // a stale back-button edit). There is nothing to update, so stop
        // claiming to be editing one — open as a fresh build seeded from
        // the permalink, which is what this page is when nothing addresses
        // a cart line. _afterChange rewrites the URL without &edit.
        this._leaveEditMode();
        this._afterChange();
        return;
      }
      this._reconcileRolesFromCart(items);
      // Same newest-first property-name fallback chain as
      // cart-products.liquid — old lines keep pre-rename names.
      const prop = (props, ...names) => {
        for (const n of names) {
          const v = props[n];
          if (v != null && v !== '') return v;
        }
        return null;
      };
      // One system quantity across the build, so the FIRST line that can
      // state it settles it for all of them — they were written from the
      // same count, and first-in-ROLES-order makes the tie-break
      // deterministic. Each line's own claim is collected too: the cart's
      // per-line pieces editor writes only the line it sits on, so a
      // customer who bumped just the backup's pieces has left the build
      // disagreeing with itself, and saving would quietly reset every line
      // to the mainline's number. That is out of scope to RECONCILE (no
      // cross-line sync, per the plan) but not to SAY — see _noteQtyDesync.
      const claims = [];
      ROLES.forEach((role) => {
        const item = items.find((it) => this._roleFromCartItem(it) === role);
        const line = item && this._lineState(role);
        if (!line) return;
        const props = item.properties || {};
        // Per-loop orientation, read back by NAME against this webbing's
        // own faces (see _faceOutFromName): a value the current webbing
        // doesn't recognise — it was changed since, or the metaobject's
        // face renamed — leaves the end's default alone rather than
        // inventing a choice. Lines written before orientation moved onto
        // the loops carry ONE line-level "Top Face", which seeds both ends
        // (Standard = top face out), which is what that property meant.
        const legacyFace = prop(props, 'Top Face');
        const faceEntry = this._entry(role);
        if (this._flippable(faceEntry)) {
          END_SLOTS.forEach((end) => {
            const endItem = this._endItem(role, end);
            if (!endItem) return;
            const raw = prop(props, `End ${end} Orientation`) || legacyFace;
            const side = this._faceOutFromName(faceEntry, raw);
            if (side) endItem.faceOut = side;
          });
        }
        if (line.lengthMode === 'custom') {
          const pieces = parseInt(prop(props, this.piecesProperty, 'Qty', 'Pieces'), 10);
          if (pieces > 0) claims.push(pieces);
          const len = parseFloat(prop(props, this.lengthProperty, 'Piece Length', 'Length per piece'));
          if (len > 0) line.length = len;
        } else if (item.quantity > 0) {
          // Fixed-length lines: one quantity unit IS one piece.
          claims.push(item.quantity);
        }
      });
      const qty = claims.length ? claims[0] : null;
      if (qty > 0) this.state.qty = Math.min(999, qty);
      if (claims.some((n) => n !== qty)) this._noteQtyDesync(this.state.qty);
      this._syncQtyInput();
      // If a pill happens to be OPEN, this corrected `length` has to reach
      // the INPUT's own value, and only a rebuild writes that (_syncOverlay
      // deliberately never touches a field mid-entry). Unless the customer
      // is already typing in it: a focused field must never be destroyed,
      // and their commit wins over ours anyway. With the pill closed there
      // is no field to correct — the value chip the scene draws comes
      // straight from state, and _afterChange redraws it below.
      if (this._lengthOpen
        && (!this.overlayHost || !this.overlayHost.contains(document.activeElement))) {
        this._renderOverlay();
      }
      this._afterChange();
    }

    // Replace every cart line this build was opened from. Remove FIRST,
    // then add — an unchanged config re-added while the old lines still
    // exist would MERGE into them (same variant + properties) and the
    // removal would then wipe both. The old lines are captured up front so
    // a failed re-add can put them back; if they're already gone (removed
    // in another tab, or a stale back-button edit) the save demotes to a
    // plain add — but ONLY on a cart that was actually read, never on one
    // that couldn't be reached (review 2026-08-01, see below).
    async _saveEdit(lines, items) {
      const olds = await this._editTargets();
      // The cart could not be READ (null, not empty — see _editTargets), so
      // whether this build still has lines is unknown (review 2026-08-01).
      // Treating that as "no lines to replace" would fall through to the
      // plain add below and put a SECOND copy of the build in the cart.
      // Abort instead, on the same error the rejected-removal path shows:
      // nothing has been touched, and trying again is the right next move.
      if (!olds) {
        this._showError('Could not update your cart. Please try again.');
        return false;
      }
      // Before anything is removed or added: the replacement lines take
      // over the customer-facing CODE of the lines they replace, so the
      // segment keeps the name it has been carrying (see _reuseIdentity).
      // `items` share these property objects, so this covers both.
      this._reuseIdentity(lines, olds);
      if (olds.length && !(await this._removeLines(olds))) {
        // The removal was rejected, so NOTHING has been added: the cart is
        // exactly as the customer left it. Say so rather than adding on top
        // (which would duplicate the build) or failing silently.
        this._showError('Could not update your cart. Please try again.');
        return false;
      }
      let result;
      try {
        // The re-add folds into another identical line when one exists
        // (editing line A into a copy of line B merges them with truthful
        // pieces/quantity, instead of Shopify's raw-quantity merge). Solo
        // mainline only, same as the plain add path.
        if (lines.length === 1 && await this._mergeAdd(lines[0])) return true;
        result = await window.bcCartAdd.add(items);
      } catch (err) {
        await this._restoreLines(olds);
        throw err; // _addToCart's catch shows the generic error
      }
      if (!result.ok) {
        await this._restoreLines(olds);
        const r = result.response || {};
        this._showError(r.description || r.message || 'Could not save this segment.');
        return false;
      }
      return true;
    }

    // Zero every addressed line in ONE /cart/update.js — a build's lines go
    // together or not at all, where a per-line change.js loop could strand
    // a half-removed build if the second call failed. (bcCartAdd has no
    // update() wrapper; the section ids don't matter here because the add
    // that follows re-renders the cart anyway.)
    async _removeLines(items) {
      const updates = {};
      items.forEach((it) => { updates[it.key] = 0; });
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        return res.ok;
      } catch (_e) { return false; }
    }

    // Put back what _removeLines took, in one add — properties included, so
    // a restored multi-line build keeps its own _build_id and stays
    // editable as one build.
    //
    // This is the one place the id IS carried over verbatim, and the
    // asymmetry with _reuseIdentity is the point (review 2026-08-01): a
    // restore is an UNDO, not a replacement. The customer is still standing
    // on the builder with &edit=<that id> in the address bar, so the lines
    // have to come back addressable by it — mint a fresh one here and the
    // edit key is orphaned, and the retry they are about to make plain-adds
    // a duplicate. The merge hazard _reuseIdentity guards against does
    // exist here in theory (a leftover the removal missed would absorb the
    // restored copy and double it), but it needs BOTH a removal that lied
    // and an add that failed, and the alternative breaks the ordinary path.
    async _restoreLines(olds) {
      if (!olds || !olds.length) return;
      try {
        await window.bcCartAdd.add(olds.map((old) => (
          { id: old.variant_id, quantity: old.quantity, properties: old.properties || {} }
        )));
      } catch (_e) { /* double failure — the save error already showing stands */ }
    }

    // ---- cart -------------------------------------------------------------

    _properties(role) {
      const props = {};
      const line = this._lineState(role);
      if (!line) return props;
      const entry = this._entry(role);
      const flippable = this._flippable(entry);
      const end1Prop = this.settings.end1Property || 'Sewn Loop - End 1';
      const end2Prop = this.settings.end2Property || 'Sewn Loop - End 2';
      const ends = this._sewingData(role).endTreatments || [];
      END_SLOTS.forEach((end) => {
        const item = this._endItem(role, end);
        if (!item) return;
        const t = ends.find((e) => e.kind === item.treatment);
        const label = t ? t.label : (item.treatment.charAt(0).toUpperCase() + item.treatment.slice(1));
        // The PROTECTION property, under the name the rest of the store
        // already uses for it: "Sewn Loop - End N" is a variant-option
        // name on the webbing products, the PDP block's property name
        // (blocks/bc-end-treatment.liquid), the kit builder's, and
        // bc-dashboard's BOM vocabulary. Its VALUE is the protection
        // ("Sleeved" / "Naked" / "None"), which is why the cart display
        // hoists it into the End heading — so it needs no second,
        // builder-only "End N Protection" property saying the same thing
        // under a name nothing else knows.
        props[end === 1 ? end1Prop : end2Prop] = label;
        if (item.treatment === 'none') return;
        // Every dimension row below prints ONLY when it DEVIATES from the
        // BC default (Jerry, 2026-08-05): an absent row always means "the
        // catalog value", never missing data, so a fully default end posts
        // its treatment alone and a customized end states exactly what was
        // changed. The default compared against is the same _defaultLoop
        // the "Default Sleeved"/"Default Naked" chips light up on (default
        // pattern, its default opening, the type's sleeve length with the
        // cap clamp, top face out), forced into the item's OWN treatment —
        // so customize-then-revert posts clean, and picking the non-default
        // treatment at its own defaults still posts treatment-only. The
        // Build permalink below always carries the FULL per-end state, so
        // the line stays recoverable and re-editable regardless of which
        // rows print. Ends are judged independently.
        //
        // If the forced treatment is illegal (sleeved under the opening
        // floor), _defaultLoop hands back a different treatment — there is
        // no default to compare against, so EVERY row prints: the
        // conservative direction.
        const def = this._defaultLoop(role, end, item.treatment);
        const defaultable = def.treatment === item.treatment;
        // NO pattern property (Jerry, 2026-07-31): every segment is built
        // on its webbing's default pattern today, so printing the code on
        // the order said nothing the webbing didn't already imply — but a
        // non-default pattern (permalink hand-edit, future saved loop)
        // forces the pattern-dependent dims below to print, keeping the
        // absent-row-means-catalog reading honest.
        const patternDeviates = !defaultable
          || (def.pattern && item.pattern !== def.pattern);
        // Which face this loop folds OUTWARD, named — the line-level "Top
        // Face: Standard | Reversed" it replaces described the whole piece,
        // which stopped being true once each end could fold its own way
        // (Jerry, 2026-07-31). The value is the face's own NAME ("Purple",
        // "Dots Up") rather than Standard/Reversed: the sewing shop reads
        // these as the build spec, and a name is a thing you can look at
        // the webbing and check. Only ever written for a flippable webbing,
        // so an ABSENT row means no face choice was offered or the loop
        // folds with the default (top) face out.
        // NOTE: bc-dashboard's BOM mirror is still unbuilt; this is the
        // vocabulary to build it against.
        if (flippable && (!defaultable
          || (def.faceOut && (item.faceOut || 'top') !== def.faceOut))) {
          props[`End ${end} Orientation`] = this._faceOutName(entry, item.faceOut);
        }
        const openingDeviates = patternDeviates
          || (def.openingMm != null && item.openingMm !== def.openingMm);
        if (openingDeviates && item.openingMm != null) {
          props[`End ${end} Opening`] = `${item.openingMm} mm`;
        }
        // The DERIVED fold (opening + pattern length + 5, _foldLiveMm) —
        // stated on the order because the sewing shop measures and marks
        // the fold, not the opening (Jerry, 2026-08-05). Derived-only
        // (never in the Build permalink) and a pure function of pattern +
        // opening, so it prints exactly when they deviate.
        if (openingDeviates) {
          const foldMm = this._foldLiveMm(item, role);
          if (foldMm != null) props[`End ${end} Fold`] = `${foldMm} mm`;
        }
        if (item.treatment === 'sleeved' && item.sleeveLengthMm
          && (!defaultable
            || (def.sleeveLengthMm != null && item.sleeveLengthMm !== def.sleeveLengthMm))) {
          props[`End ${end} Sleeve Length`] = `${item.sleeveLengthMm} mm`;
        }
      });
      return props;
    }

    // The id every line of one build shares, so the cart can find them
    // again as a set (the Edit link, _editTargets). Client-generated —
    // there is no server round-trip before the add — and only ever compared
    // for equality, so a timestamp plus six random characters is all the
    // uniqueness it needs. Hidden from the customer by the '_' prefix,
    // which is Shopify's own convention and what the cart display filters
    // on (snippets/cart-products.liquid, bc-cart-segment-props.liquid).
    _newBuildId() {
      return `bld-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    }

    // The build's CUSTOMER-facing name, written as the visible "Segment"
    // property on every one of its lines (Jerry, 2026-08-01). Deliberately
    // not derived from _build_id: that value is a machine handle whose
    // shape may change, and a code people read aloud wants its own
    // alphabet (see SEGMENT_CODE_ALPHABET). The two travel together on
    // every line, so having both is not redundancy — it is one value for
    // people and one for code.
    _newSegmentCode() {
      let out = '';
      for (let i = 0; i < 4; i += 1) {
        out += SEGMENT_CODE_ALPHABET.charAt(Math.floor(Math.random() * SEGMENT_CODE_ALPHABET.length));
      }
      return out;
    }

    // Carry the CODE of the lines an edit REPLACES onto the lines that
    // replace them (Jerry, 2026-08-01). To the customer a saved edit is the
    // same segment they opened — the code is printed above it in the cart,
    // and on the order it is what ties the mainline to its backup — so
    // minting a fresh one on every save would rename a build behind their
    // back and break that tie across a re-order or a support thread. A
    // build with no code yet (written before the property existed) keeps
    // the freshly minted one, which is how it picks up its first.
    //
    // The "_build_id" is deliberately NOT inherited (review 2026-08-01) —
    // this is the "code for people, id for code" split doing real work.
    // Copying BOTH made a replacement line byte-identical to the line it
    // replaces whenever the config came back unchanged (Save with nothing
    // edited, which is an ordinary thing to do), and that identity is
    // exactly what Shopify's native merge keys on: if the batched removal
    // reported success but left a line standing — a line re-keyed between
    // our /cart.js read and the update is enough — the follow-up add folds
    // into the survivor and SUMS the raw quantity, which on a custom-length
    // line is metres. The charge doubles, the "Qty" property still reads
    // the old piece count, and nothing on screen says so. One differing
    // hidden property is all it takes to keep them apart: the stale line
    // then stands beside the saved one, visible and removable.
    //
    // Nothing needs the id to survive a save. The cart's "Edit segment"
    // link is rendered FROM the line's own properties every time the cart
    // draws (snippets/bc-cart-segment-props.liquid), and a successful save
    // navigates back to a freshly rendered cart — so the link points at
    // the new id without anything having to carry the old one forward.
    //
    // Mutates the property objects in place; _addToCart's `items` hold
    // these very objects, so one write covers the add payload as well.
    _reuseIdentity(lines, olds) {
      if (!lines || !lines.length || !olds || !olds.length) return;
      const inherited = (key) => {
        for (const old of olds) {
          const value = ((old && old.properties) || {})[key];
          if (value) return value;
        }
        return null;
      };
      const code = inherited('Segment');
      if (!code) return;
      lines.forEach((line) => { line.properties.Segment = code; });
    }

    // The whole build's cart payload: one item per present line, in ROLES
    // order, each with its own properties. Empty when any line can't
    // resolve (_lineSet); the add path refuses those first.
    //
    // Called ONCE per add, which is what makes the identity per-add rather
    // than per-line: every line of one build shares the "Segment" code and
    // the "_build_id" minted here. Both are written on SOLO builds too —
    // one segment is still a segment, and the cart, the order email and
    // bc-dashboard all want it named the same way as a three-piece one.
    // An EDIT overwrites the CODE from the lines it replaces
    // (_reuseIdentity) but keeps the build id minted here, so a saved edit
    // reads as the same segment while posting lines that are distinguishable
    // from the ones they replace — see _reuseIdentity for why that matters.
    _lines() {
      const set = this._lineSet();
      if (!set) return [];
      const multi = set.length > 1;
      const buildId = this._newBuildId();
      const segmentCode = this._newSegmentCode();
      // One permalink for the whole build, on every line — it describes the
      // build, not the piece, so any line reopens all of it.
      const build = this._currentUrl();
      return set.map((line) => {
        const properties = {};
        // Identity first, then which piece of the build this is, then
        // everything that describes that piece — the reading order of the
        // order email and of the cart's build sheet alike ("Segment K7Q2,
        // the backup line, which is …").
        properties.Segment = segmentCode;
        if (multi) properties['Segment Piece'] = ROLE_LABELS[line.role];
        Object.assign(properties, this._properties(line.role));
        if (line.custom) {
          properties[this.lengthProperty] = `${line.length} ${line.unit}`;
          properties[this.piecesProperty] = String(line.qty);
        }
        // Per LINE, not per build: a three-piece segment can mix a discounted
        // webbing with a full-price one, and each line must say the truth about
        // its own price. Written after _properties() so it can never collide
        // with a sewn-loop end key.
        const sale = this._saleProperty(line);
        if (sale) properties.Sale = sale;
        properties.Build = build;
        properties._build_id = buildId;
        return { ...line, properties };
      });
    }

    // The UN-PREFIXED `Sale` line-item property for one line, or '' when the
    // line's variant is not discounted.
    //
    // WHY IT EXISTS. bc-dashboard runs a sale by EDITING variant prices, not by
    // creating a Shopify discount, so a discounted order line carries nothing
    // that names the sale. This property is that record: un-prefixed so it
    // shows in the customer's confirmation email and the admin order view, and
    // so bc-dashboard can attribute the line to a campaign.
    //
    // THE RULE (documented in full in snippets/bc-campaign-label.liquid, and
    // mirrored in assets/bc-sale-property.js — change all three together):
    // "<n>% off · <campaign name>", where n is THIS line's own percent off from
    // compare_at vs price and the campaign lends only its name — which is what
    // keeps the value truthful when one campaign discounts different products
    // by different amounts. The campaign is matched by percent, else by being
    // the sole "varies" campaign, else by being the sole campaign running at
    // all; failing all three, the percent is said plainly.
    //
    // The campaign list is page-global JSON from snippets/bc-campaign-data.liquid;
    // its absence means "no campaign running", which is not an error.
    _saleProperty(line) {
      const price = Number(line.priceCents);
      const compare = Number(line.compareAtCents);
      if (!Number.isFinite(price) || !Number.isFinite(compare)) return '';
      if (compare <= price || compare <= 0) return '';

      const percent = Math.round(((compare - price) / compare) * 100);
      if (!(percent > 0)) return '';

      let campaigns = [];
      try {
        const el = document.querySelector('script[data-bc-campaigns]');
        const parsed = el ? JSON.parse(el.textContent || '[]') : [];
        // A nameless campaign has nothing to lend, and counting it would only
        // ever stop some other campaign from looking unique below.
        if (Array.isArray(parsed)) {
          campaigns = parsed.filter((c) => c && typeof c.label === 'string' && c.label !== '');
        }
      } catch (_e) {
        // Malformed blob: fall through to the bare percent.
      }

      const exact = campaigns.find((c) => c.varies !== true && Number(c.percent) === percent);
      const varies = campaigns.filter((c) => c.varies === true);
      let name = '';
      if (exact) {
        name = exact.label;
      } else if (varies.length === 1) {
        name = varies[0].label;
      } else if (campaigns.length === 1) {
        name = campaigns[0].label;
      }

      // Ambiguous: a WRONG sale name in a confirmation email is worse than none.
      return name ? `${percent}% off · ${name}` : `${percent}% off`;
    }

    // Everything the add path refuses, checked per line so a three-piece
    // build says WHICH piece needs fixing. A solo mainline keeps the exact
    // wording it always had.
    _validateLines() {
      const roles = this._presentRoles();
      const multi = roles.length > 1;
      for (const role of roles) {
        const name = multi ? `${ROLE_LABELS[role].toLowerCase()} ` : '';
        const state = this._lineState(role);
        if (state.lengthMode === 'custom' && !(Number(state.length) > 0)) {
          return `Enter a ${name}length before adding.`;
        }
        const line = this._line(role);
        if (!line) {
          return multi
            ? `The ${ROLE_LABELS[role].toLowerCase()} can’t be added right now.`
            : 'This configuration can’t be added right now.';
        }
        if (!line.available) {
          return multi
            ? `The ${ROLE_LABELS[role].toLowerCase()} length is sold out.`
            : 'This length is sold out.';
        }
      }
      return '';
    }

    // What the store cannot actually fill, refused BEFORE anything is
    // written to the cart. On a variant that tracks inventory and denies
    // overselling, Shopify accepts an oversized /cart/add and silently
    // CLAMPS the line to what's in stock (200 OK, smaller quantity) while
    // the "Piece Length" property still records the full request — a 40 m
    // line labelled "75 m", wrong for the cutter and for the customer, and
    // reported to neither. Cart quantity units on a custom line are METERS
    // or FEET, so this is stock of webbing, not of pieces. Same gate the PDP
    // runs (assets/bc-custom-length.js), one whole build wide.
    //
    // Never auto-shrinks the build (the customer asked for a length, not for
    // whatever is left) and never rolls back after the fact. `maxQty` is a
    // PAGE-LOAD snapshot, so a concurrent order can make it stale mid-visit;
    // Shopify's clamp stays the backstop for that rare race, which is the
    // price of not refetching inventory on every add.
    //
    // Returns the message to show, or '' when the build fits.
    _stockError(lines, cart) {
      const capped = lines.filter((l) => Number.isFinite(l.maxQty));
      if (!capped.length) return '';
      // A cart that could not be READ (null — a real empty one is a 200 with
      // items: []) says nothing about its contents, so it reads as empty
      // here and the check reduces to the build's own claims against stock.
      // Failing open on the cart-aware half beats blocking a legitimate add
      // over an unreachable /cart.js.
      //
      // In EDIT mode the lines this save REPLACES are about to be removed,
      // so they are not part of what the cart will hold and drop out here —
      // the stock they occupy is exactly the stock the replacement claims.
      // Filtering them out is also what makes the fold below match against
      // what _saveEdit's own fold sees, which is the post-removal cart.
      const items = ((cart && cart.items) || []).filter((it) => !this._isEditTarget(it));
      const inCart = new Map();
      items.forEach((it) => { inCart.set(it.id, (inCart.get(it.id) || 0) + it.quantity); });

      // What the cart would hold PER VARIANT if this add went through.
      // Summed by variant id rather than checked line by line: a mainline
      // and its backup can be the same webbing at the same length, and the
      // two draw on one stock.
      const projected = new Map(inCart);
      const bump = (id, n) => projected.set(id, (projected.get(id) || 0) + n);
      const fold = lines.length === 1 ? this._foldPlan(items, lines[0]) : null;
      if (fold) {
        // The fold REPLACES the matched line's quantity rather than adding
        // beside it, so take that line's current quantity back out and put
        // the recomputed one in (see _foldPlan).
        bump(Number(lines[0].variantId), fold.quantity - fold.match.quantity);
      } else {
        lines.forEach((l) => bump(Number(l.variantId), l.quantity));
      }

      for (const line of capped) {
        const id = Number(line.variantId);
        if ((projected.get(id) || 0) <= line.maxQty) continue;
        const held = inCart.get(id) || 0;
        // The webbing is NAMED: a build has up to three lines, so a bare
        // number wouldn't say which piece is short. "75 m of X" on a custom
        // line, "3 × X" on a fixed-length one (whose units are pieces).
        const label = (this._entry(line.role) || {}).label || 'this webbing';
        const of = (n) => (line.custom ? `${n} ${line.unit} of ${label}` : `${n} × ${label}`);
        const has = (n) => (line.custom ? `${n} ${line.unit}` : String(n));
        if (held <= 0) return `Only ${of(line.maxQty)} in stock.`;
        // What MORE could be asked for. A fold target counts as
        // already-in-cart even though folding rewrites it, because the fold
        // GROWS that line — whatever sits on it is spoken for.
        const avail = line.maxQty - held;
        if (avail > 0) return `Only ${of(avail)} left — your cart already has ${has(held)}.`;
        return `All ${of(line.maxQty)} in stock are already in your cart.`;
      }
      return '';
    }

    // WHICH line an add folds into and what its quantity becomes — the
    // fold's whole arithmetic, factored out so the stock gate can project
    // exactly the number this will write. Two copies of it could drift and
    // let through an add Shopify then clamps, which is the failure the gate
    // exists to prevent.
    //
    // `items` is a cart's item list. In edit mode the caller passes it with
    // the edit's own lines already filtered out — the fold there runs AFTER
    // the removal, so those lines are not there to match against.
    // Null when nothing matches (the caller does a plain add).
    _foldPlan(items, line) {
      // "Segment"/"_build_id" join the ignore list for the same reason the
      // pieces count and the Build permalink are on it: they are minted
      // per ADD (see _lines), so comparing them would make every re-add
      // look like a different build and this fold would never fire again
      // (Jerry, 2026-08-01). Filtering them out also lets a line written
      // before the identity existed still match one that carries it.
      // 'Sale' joins them for a related reason: it is minted from the price at
      // ADD time, so a line added before a sale started (no Sale property) and
      // the same configuration added after it (carrying one) would otherwise
      // stop folding and sit in the cart as two identical rows. Shopify charges
      // both at the current price anyway, so folding them under the newer
      // label is both tidier and the more accurate attribution.
      const ignore = [this.piecesProperty, 'Build', 'Segment', '_build_id', 'Sale'];
      const match = (items || []).find(
        (it) => it.id === Number(line.variantId)
          && window.bcCartAdd.samePropsIgnoring(it.properties || {}, line.properties, ignore)
      );
      if (!match) return null;
      if (!line.custom) return { match, pieces: null, quantity: match.quantity + line.quantity };
      // Existing pieces: the property when sane, else derived the same
      // way the cart display derives it (a pre-fix merged line may
      // carry a stale property).
      const prevPieces = parseInt((match.properties || {})[this.piecesProperty], 10)
        || Math.max(1, Math.round(match.quantity / line.length));
      const pieces = prevPieces + line.qty;
      return { match, pieces, quantity: Math.max(1, Math.ceil(line.length * pieces)) };
    }

    // Fold this add into an existing cart line of the SAME build (same
    // variant + same properties, ignoring the pieces count and the Build
    // permalink that mirrors it). Left alone, Shopify merges
    // identical-property lines by summing the RAW quantity — which for a
    // custom-length line is METERS, so a double add doubled the charge
    // while the "Qty" property still read 1 piece. Folding ourselves keeps
    // the spec truthful: pieces bump, quantity recomputes from
    // ceil(length × pieces), and the properties + Build q= rewrite to the
    // new totals. Fixed-length lines fold too — Shopify's native merge is
    // arithmetically fine there, but it leaves the Build permalink's q=
    // at the add-time value, and the EDIT flow restores qty from that
    // permalink (a later Save Segment would silently shrink the line).
    // Returns true when it handled the add; false → caller does a plain
    // /cart/add.js (including when the change call is rejected — native
    // merge is then no worse than the old behavior).
    // Callers gate this to SOLO MAINLINE builds (see _addToCart): the line
    // it folds into is matched by variant + properties, which a multi-line
    // build would have to satisfy three times over before any of the three
    // could be safely bumped.
    //
    // `cart` is an already-read cart to fold against — the plain add path
    // reads one for the stock gate and hands it over. _saveEdit must NOT
    // pass one: its fold runs after the removal, so a cart read before it
    // would still hold the lines that are now gone.
    async _mergeAdd(line, cart) {
      const properties = line.properties;
      const cartNow = cart || await window.bcCartAdd.getCart();
      if (!cartNow) return false;
      const plan = this._foldPlan(cartNow.items, line);
      if (!plan) return false;
      const { match, quantity } = plan;

      const newProps = { ...(match.properties || {}), ...properties };
      // The line being folded INTO keeps its own identity — it is the
      // segment the customer already has in the cart (and already sees
      // labelled), gaining pieces rather than being replaced by a new one.
      // A line that carries neither keeps the freshly minted values, which
      // is how a pre-identity line picks up its first code.
      const matchProps = match.properties || {};
      if (matchProps.Segment) newProps.Segment = matchProps.Segment;
      if (matchProps._build_id) newProps._build_id = matchProps._build_id;
      const params = this._buildParams();
      if (line.custom) {
        newProps[this.piecesProperty] = String(plan.pieces);
        if (plan.pieces > 1) params.set('q', String(plan.pieces));
        else params.delete('q');
      } else if (quantity > 1) {
        params.set('q', String(quantity));
      } else {
        params.delete('q');
      }
      newProps.Build = `${location.origin}${location.pathname}?${params.toString()}`;

      const { ok, response } = await window.bcCartAdd.change({
        id: match.key,
        quantity,
        properties: newProps,
      });
      if (!ok) return false; // response is the cart JSON on success
      window.bcCartAdd.dispatchCartUpdate(this, {
        source: 'bc-segment-builder',
        cart: response,
        response,
        itemCount: 1,
        productId: (this._entry('main') || {}).productId,
        variantId: Number(line.variantId),
      });
      return true;
    }

    async _addToCart() {
      if (this._adding) return;
      const entry = this._entry('main');
      if (!this._lineState('main') || !entry) return;
      // Settle anything the typing paths still owe BEFORE the build leaves
      // the page. The cart itself reads state, not the drawing or the
      // address bar (see _lines / _buildParams, which lift only `view` from
      // location.search), so neither of these can change what is added —
      // but the customer is about to be shown "Added!" beside a drawing,
      // and a permalink they may copy straight afterwards, and both must
      // describe the thing that went in the cart. Also covers the edit-save
      // path, which navigates away and would otherwise drop the pending
      // URL write on the floor.
      this._flushScene();
      this._flushUrl();

      const invalid = this._validateLines();
      if (invalid) {
        this._showError(invalid);
        return;
      }
      // Every present line, in ONE payload: a partial failure can't strand
      // half a build, and the build id is minted here, once per add.
      const lines = this._lines();
      if (!lines.length) {
        this._showError('This configuration can’t be added right now.');
        return;
      }
      const items = lines.map((line) => (
        { id: Number(line.variantId), quantity: line.quantity, properties: line.properties }
      ));

      this._adding = true;
      this._setBusy(true);
      try {
        // ONE /cart.js read serves the stock gate and, on the plain path,
        // the fold after it. Read at all only when something needs it: a
        // build with no inventory-capped line and no fold to attempt has
        // nothing to ask the cart. (_saveEdit reads its own — its fold runs
        // after the removal, so this snapshot is stale by then.)
        const needsCart = lines.some((l) => Number.isFinite(l.maxQty))
          || (!this._editKey && lines.length === 1);
        const cartSnapshot = needsCart ? await window.bcCartAdd.getCart() : null;
        // Refuse an unfillable build before ANY cart mutation — inside the
        // busy state so the button stays disabled across the read, and the
        // `finally` restores it on the way out.
        const short = this._stockError(lines, cartSnapshot);
        if (short) {
          this._showError(short);
          return;
        }
        if (this._editKey) {
          if (await this._saveEdit(lines, items)) {
            // Back to the cart, where the edit started — and the old keys
            // are dead anyway (replacing the lines re-keyed them). Stay busy
            // until the navigation lands: a re-click now would double-add.
            //
            // Deliberately NO added animation here: the navigation IS the
            // feedback, it starts in the same tick, and holding it back to
            // play an 800ms burst nobody asked for would only delay the
            // cart. (The PDP has no equivalent — it never navigates.)
            this._navigating = true;
            location.assign(this.cartUrl);
          }
          return;
        }
        // Folding a re-add into an existing line is a SOLO mainline affair
        // (see _mergeAdd) — a multi-line build always adds fresh lines.
        if (lines.length === 1 && await this._mergeAdd(lines[0], cartSnapshot)) {
          this._showError('');
          this._playAdded();
          return;
        }
        const { ok, response, cart } = await window.bcCartAdd.add(items);
        if (!ok) {
          this._showError(response.description || response.message || 'Could not add this segment to the cart.');
          this.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: { source: 'bc-segment-builder', data: { message: response.message, description: response.description, errors: response.errors } },
          }));
          return;
        }
        this._showError('');
        window.bcCartAdd.dispatchCartUpdate(this, {
          source: 'bc-segment-builder',
          cart,
          response,
          // The build's own count: how many cart LINES it just added.
          // productId/variantId stay the MAINLINE's — the build's subject,
          // and what the PDP-side listeners (bc-accessories' ride-along)
          // match against.
          itemCount: items.length,
          productId: entry.productId,
          variantId: Number(lines[0].variantId),
        });
        this._playAdded();
      } catch (err) {
        console.error('[bc-segment] cart/add failed', err);
        this._showError('Something went wrong adding to the cart. Please try again.');
      } finally {
        if (!this._navigating) {
          this._adding = false;
          this._setBusy(false);
        }
      }
    }

    // ---- helpers ----------------------------------------------------------

    _attr(v) { return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
    _esc(v) {
      return String(v == null ? '' : v)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    _showError(msg) {
      const el = this.querySelector('[data-bc-error]');
      if (!el) return;
      el.textContent = msg || '';
      el.hidden = !msg;
      // The <p> is hidden until it has text, and a hidden node's later
      // reveal is not reliably announced — so the message ALSO goes down
      // the live region, which is what the product form does with its own
      // add errors (assets/product-form.js). Clearing announces nothing.
      if (msg) this._announce(msg, ERROR_ANNOUNCE_MS);
    }
    _setBusy(on) {
      const btn = this.querySelector('[data-bc-add]');
      if (btn) { btn.disabled = on; btn.setAttribute('aria-busy', on ? 'true' : 'false'); }
    }

    // PDP parity for a successful add (Jerry, 2026-08-02: "the add to cart
    // doesn't do the same animation that product pages do on the segment
    // builder. Can we mimic that behavior?"). Horizon's choreography, from
    // AddToCartComponent in assets/product-form.js, is three things:
    //   1. data-added="true" on the button — upstream CSS then turns the
    //      label transparent and plays the checkmark burst that the section
    //      markup carries, dropping the attribute 800ms after the
    //      animations settle;
    //   2. a <fly-to-cart> tile flying from the button to the header cart
    //      icon (theme setting gated);
    //   3. the cart bubble's count + pulse, and the header's cart-count
    //      announcement — all of which this builder ALREADY gets, because
    //      its cart:update reaches <cart-icon>/<header-actions> through
    //      assets/bc-events-compat.js. Nothing to do here for those.
    // Item 3 is also why there is no drawer to open: this store runs
    // cart_type: page, so the PDP doesn't open one either.
    //
    // The one deliberate difference: upstream fires all of this on CLICK,
    // before the POST. We fire it on SUCCESS, because this builder POSTs
    // the whole build itself and Shopify can still reject it — a burst on
    // click would claim an add that failed. Same call, for the same
    // reason, as bc-custom-length's _customAddToCart.
    _playAdded() {
      const btn = this.querySelector('[data-bc-add]');
      if (!btn) return;

      this._flyToCart(btn);
      this._announce(
        (typeof Theme !== 'undefined' && Theme.translations && Theme.translations.added) || 'Added',
        ADDED_ANNOUNCE_MS
      );

      // Re-adding twice inside the hold would otherwise let the FIRST
      // timer strip data-added mid-second-burst.
      if (this._addedTimer) clearTimeout(this._addedTimer);
      btn.dataset.added = 'true';

      // Upstream waits for the button's animations to finish before it
      // starts the 800ms hold, so a slow frame can't cut the burst short
      // (utilities.js onAnimationEnd — DocumentTimeline animations only,
      // i.e. skip anything scroll-driven).
      // Both capability checks matter: anything thrown here would skip the
      // timer below and strand data-added, i.e. leave the label invisible
      // for the rest of the session. No animations found just means the
      // hold starts immediately, which is what upstream does too.
      const anims = typeof btn.getAnimations === 'function' && typeof DocumentTimeline !== 'undefined'
        ? btn.getAnimations({ subtree: true }).filter((a) => a.timeline instanceof DocumentTimeline)
        : [];
      Promise.allSettled(anims.map((a) => a.finished)).then(() => {
        this._addedTimer = setTimeout(() => {
          this._addedTimer = null;
          btn.removeAttribute('data-added');
        }, ADDED_HOLD_MS);
      });
    }

    // The flying tile. Upstream builds the same element off the variant's
    // featured image; ours uses the MAINLINE webbing's catalog image (the
    // same one the picker tiles show), which is the build's subject.
    _flyToCart(btn) {
      // Merchant switch, read exactly where the PDP reads it — Theme
      // settings → "Add to cart animation".
      if (this.dataset.addToCartAnimation !== 'true') return;
      const cartIcon = document.querySelector('.header-actions__cart-icon');
      const entry = this._entry('main');
      const image = entry && entry.image;
      // fly-to-cart.js is a low-priority module load; if it hasn't defined
      // the element yet we skip the flight rather than append a stub —
      // FlyToCart declares `source`/`destination` as CLASS FIELDS, so
      // upgrade would re-initialise them to undefined and throw on the
      // observer. (By the time anyone has configured a segment it is long
      // since defined; this is belt and braces.)
      if (!cartIcon || !image || !customElements.get('fly-to-cart')) return;

      const fly = document.createElement('fly-to-cart');
      // --main is the PDP's own curve set (--quick is the quick-add modal's,
      // --sticky the sticky bar's) — this button is the page's primary add.
      fly.classList.add('fly-to-cart--main');
      fly.style.setProperty('background-image', `url(${image})`);
      fly.style.setProperty('--start-opacity', '0');
      fly.source = btn;
      fly.destination = cartIcon;
      document.body.appendChild(fly);
    }

    // Screen-reader channel for the add. The burst is aria-hidden art, so
    // this is the only thing AT gets from the button itself; the header
    // separately announces the new cart count. Cleared after a hold so an
    // identical later message is announced again (a live region only
    // speaks when its text CHANGES) — same trick, same durations, as
    // assets/product-form.js.
    _announce(msg, holdMs) {
      const el = this.querySelector('[data-bc-status]');
      if (!el) return;
      if (this._announceTimer) clearTimeout(this._announceTimer);
      el.textContent = msg;
      this._announceTimer = setTimeout(() => {
        this._announceTimer = null;
        el.textContent = '';
      }, holdMs);
    }
    _flash(sel, msg) {
      const el = this.querySelector(sel);
      if (!el) return;
      // Cache the ORIGINAL label + clear pending restores (two flashes within
      // 1.8s would otherwise trap the flash text as "original").
      if (!this._flashState) this._flashState = new Map();
      const state = this._flashState.get(el) || { original: el.textContent };
      if (state.timer) clearTimeout(state.timer);
      el.textContent = msg;
      state.timer = setTimeout(() => {
        el.textContent = state.original;
        this._flashState.delete(el);
      }, 1800);
      this._flashState.set(el, state);
    }
  }

  customElements.define('bc-segment-builder', BcSegmentBuilder);
})();
